/**
 * Reading a date somebody typed, or an agency wrote in a file.
 *
 * One module, two entry points — `parseDateWithFormat` for a file whose column map declares a format,
 * `parseTypedDateTime` for a row somebody typed — sharing the meridiem rules, the range checks and the
 * wall-clock-to-instant conversion. They were two implementations that disagreed, and the grid's
 * refused every value with a time of day on it.
 */
export * from './dateText.js';
export * from './vocabulary.js';
import { startOfDayIn } from './dateText.js';
import { z } from 'zod';
/**
 * Contracts shared by the API and the SPA.
 *
 * The point of this package is that the front end never hand-writes a type for a response
 * the back end already describes. As domain modules land, their response DTO schemas move
 * here and both sides import the same definition.
 */
/** The one error envelope for the whole API (§3.1). */
export const apiErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        requestId: z.string().optional(),
        details: z.unknown().optional(),
    }),
});
export const paginationQuery = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});
/**
 * Wraps an item schema in the paged envelope, so a list response is described once rather
 * than spelled out per module. `nextCursor` absent means this was the last page.
 */
export function pageOf(item) {
    return z.object({ items: z.array(item), nextCursor: z.string().optional() });
}
/**
 * Auth contracts, imported by both sides.
 *
 * The SPA parses every response through these, so a change to the API's shape fails loudly
 * at the boundary instead of rendering undefined three components deep (§3.2's "untyped
 * service boundaries" is the LIS mistake this exists to avoid).
 */
export const meResponse = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    org: z.object({ id: z.string(), name: z.string() }),
    roles: z.array(z.object({ id: z.string(), name: z.string() })),
    permissions: z.array(z.string()),
    /** True on a first sign-in, after an admin reset, or once the password has expired. */
    mustChangePassword: z.boolean(),
});
export const loginResponse = z.object({
    accessToken: z.string(),
    expiresInSeconds: z.number(),
    user: meResponse,
});
/**
 * Single session: the two reasons a session can be in the way, or gone
 * (docs/SINGLE-SESSION-PLAN.md §4a, §5).
 *
 * In shared rather than beside the code that throws them because both ends read them: the API puts
 * one of these in an error's `details.reason`, and the SPA branches on it to show a prompt rather
 * than a red toast. A string literal repeated on both sides of that boundary is the kind of thing
 * that gets corrected in one file and left in the other, and the failure is silent — the prompt
 * simply never appears and the user sees "conflict".
 */
/** 409 on login: the account has a live session, and the caller has not said to replace it. */
export const SESSION_ACTIVE_ELSEWHERE = 'SESSION_ACTIVE_ELSEWHERE';
/** 401 on refresh: this session was replaced by a login somewhere else. */
export const SESSION_ENDED_ELSEWHERE = 'SESSION_ENDED_ELSEWHERE';
/**
 * The sentence a superseded session is told, in one place — which the plan asks for explicitly: "the
 * message must be the same sentence in every path, defined once" (§4).
 *
 * There are three paths and they are not obviously the same code: a refresh that fails while the app
 * is open, a refresh that fails during the bootstrap on load, and the API's own error message for a
 * client that has nothing better to show. Three copies of a sentence is three sentences, and the one
 * that gets rewritten is the one somebody happens to be looking at.
 *
 * Plain, and about the cause rather than the mechanism: a user who reads "your refresh token was
 * superseded" learns nothing they can act on. This tells them what happened and, by implication,
 * that signing in again is all it takes.
 */
export const SESSION_ENDED_ELSEWHERE_MESSAGE = 'You have been signed out because you signed in somewhere else.';
/** 401 on refresh, and the poll's reason: an administrator deactivated or archived this account. */
export const SESSION_ACCOUNT_DEACTIVATED = 'SESSION_ACCOUNT_DEACTIVATED';
/** Same rule as the sentence above: one place, every path. */
export const SESSION_ACCOUNT_DEACTIVATED_MESSAGE = 'You have been signed out because your account was deactivated. Ask an administrator if you need access.';
/**
 * The 409's `details`, as a contract — which is also how **D-3** is enforced rather than remembered.
 *
 * The decision was that the prompt says WHEN and nothing else: "signed in 40 minutes ago", never an
 * IP address and never a device. `.strict()` makes that a property of the schema instead of a note
 * in a plan: adding `ip` or `userAgent` to the payload fails the contract test, so the disclosure
 * cannot arrive as a well-meant improvement to a message.
 *
 * `startedAt` is an ISO timestamp and the relative phrasing is the front end's job — the server does
 * not know what "ago" means on a clock it cannot see.
 */
/**
 * The poll's answer (docs/SINGLE-SESSION-PLAN.md §4c, D-1).
 *
 * `current: false` for exactly two things the poll may conclude: the session was superseded by a login
 * elsewhere, or an administrator deactivated the account — `reason` says which. Every other state
 * (expired, unknown, rotated, logged out) is `true`, not because those sessions are healthy but because
 * the poll is not the thing that decides about them. See the note on `sessionState` in the auth service.
 *
 * `refreshAccess: true` means the account's roles or a role's permissions changed since this session's
 * access token was minted: the client refreshes now rather than carrying the old permissions for up to
 * fifteen minutes.
 */
export const sessionStateResponse = z
    .object({
    current: z.boolean(),
    reason: z.enum([SESSION_ENDED_ELSEWHERE, SESSION_ACCOUNT_DEACTIVATED]).optional(),
    refreshAccess: z.literal(true).optional(),
})
    .strict();
export const sessionActiveElsewhereDetails = z
    .object({
    reason: z.literal(SESSION_ACTIVE_ELSEWHERE),
    startedAt: z.string().datetime(),
})
    .strict();
/**
 * Org-wide password policy. Null means never expire — a policy that silently defaulted to
 * expiring would lock people out by accident.
 */
export const orgSettings = z.object({
    passwordExpiryDays: z.number().nullable(),
});
export const orgResponse = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    status: z.enum(['ACTIVE', 'SUSPENDED']),
});
export const userSummary = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    /** ARCHIVED is the terminal state that stands in for deletion; there is no hard delete. */
    status: z.enum(['ACTIVE', 'DISABLED', 'ARCHIVED']),
    roles: z.array(z.object({ id: z.string(), name: z.string() })),
    /** Dormant placeholder. Recorded; no code path reads it yet. */
    ssoEnabled: z.boolean(),
    /**
     * Recorded, and deliberately **not enforced** — Phase 1 of docs/FACILITY-SCOPING-PLAN.md.
     * Nothing filters data by this yet. Assignments exist so they can be got right before
     * they start restricting anything.
     */
    facilityIds: z.array(z.string()),
    mustChangePassword: z.boolean(),
    lastLoginAt: z.string().optional(),
    passwordChangedAt: z.string(),
    createdAt: z.string(),
});
export const userListResponse = z.object({ items: z.array(userSummary) });
/** `GET /users/names` — who the ids on a record's history are, for anyone signed in to the org. */
export const userNamesResponse = z.object({
    items: z.array(z.object({ id: z.string(), name: z.string() })),
});
export const roleSummary = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    permissions: z.array(z.string()),
    isSystem: z.boolean(),
    /**
     * Inactive roles grant nothing and cannot be newly assigned (no delete exists). Optional so a client
     * reading an older API, or a test fixture written before this field, reads as active.
     */
    status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
});
export const roleListResponse = z.object({ items: z.array(roleSummary) });
/**
 * Facility — one physical location, and the source a report comes from (D2).
 *
 * Ingestion configuration (transport, SFTP credentials, API keys) is deliberately absent:
 * what a partner's systems can actually do is a question for a technical call with the
 * customer, not one to guess at in a schema. Adding it later is additive on both sides —
 * new optional fields on this DTO, which the SPA ignores until it renders them.
 */
export const facilityAddress = z.object({
    line1: z.string().optional(),
    line2: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
});
export const facilityContact = z.object({
    name: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
});
/**
 * Which shapes of data an agency sends (docs/AGENCY-INGESTION-PLAN.md §2.1).
 *
 * Confirmed from the CCSO file and §4 of COLLIER-CATEGORIES.md: agencies send either bare event data
 * with no person identifier (CCSO, EMS, fire — an overdose happened at an address) or person-level
 * data with demographics (treatment providers). Hospitals send both.
 *
 * This drives which column maps an agency may hold and which targets those maps may write: an
 * INCIDENT map cannot map a column to a participant demographic, because there is no person in the
 * file to attach it to. It is configuration a user can correct, not an assertion about reality —
 * the seeded values are inferred (plan §9 O-7) and the county has not confirmed the list.
 */
export const AGENCY_DATA_SHAPES = ['INCIDENT', 'PERSON', 'BOTH'];
export const agencyDataShape = z.enum(AGENCY_DATA_SHAPES);
/** How often an agency is expected to send. Coded so "late" is answerable later. */
export const INGEST_CADENCES = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'AD_HOC'];
/**
 * Per-agency ingestion configuration — the sub-document `facility/v1/types.ts` named as the seam.
 *
 * Everything here is optional, and `transport` has exactly one value today. SFTP and API transports
 * are deferred (plan §8) because what a county's systems can actually do is a question for a
 * technical call, and guessing at it in a schema produces fields nobody fills in correctly.
 *
 * The contact is who to chase about a malformed or late file. Deliberately not a login: the person
 * who owns an agency's export is usually not a user of this system.
 */
export const facilityIngestion = z
    .object({
    transport: z.literal('MANUAL_UPLOAD').optional(),
    /** Applied when a file arrives without a map named. One active map per (agency, shape). */
    defaultColumnMapId: z.string().optional(),
    dataContactName: z.string().optional(),
    dataContactEmail: z.string().optional(),
    /** Coded against the `expectedCadence` lookup. */
    expectedCadence: z.string().optional(),
    note: z.string().optional(),
})
    .strict();
/**
 * What a participant code looks like, in one place (docs/PARTICIPANT-ID-PLAN.md §2, §7).
 *
 * ── Why this is shared and not five regexes ──────────────────────────────────────────
 * It was five: the by-code lookup, two report-link schemas, a measure entry's subject and a grid
 * row's, each hardcoding `^PTC-\d{6}$`. The moment a code could start with its agency's name, every
 * one of them refused it — so an agency-prefixed participant was registrable and then unreferenceable,
 * by code, anywhere. The plan claimed both formats would resolve; it had not checked, and a test
 * caught it.
 *
 * Both formats, because §7 keeps the old ones: re-prefixing an identifier people have printed is the
 * one thing §2 promises never happens, so a mixed estate is the honest outcome and both halves have to
 * be accepted for as long as the old ones exist — which is forever.
 *
 * The prefix bound matches the facility's own rule (2–12 characters, uppercase, digits, hyphens), so a
 * code this accepts is one some agency could actually have minted.
 */
export const PARTICIPANT_CODE_SHAPE = /^[A-Z0-9][A-Z0-9-]{0,11}-\d{6}$/;
/** The one sentence every refusal of a malformed code uses. */
export const PARTICIPANT_CODE_MESSAGE = 'must be a participant code like PTC-000123 or CCSO-000001';
/**
 * Which rows that match nobody may create a participant (docs/PARTICIPANT-ID-PLAN.md §3).
 *
 * - `OFF` — none. Every unmatched row records its events and waits in review, or for a person decision.
 * - `UNIDENTIFIED` — rows that name nobody at all: no agency ID, no date of birth, no participant code.
 * - `UNMATCHED` — those, AND rows with a strong identifier (an agency ID or an exact date of birth) that
 *   the ladder found no candidate for. Registered with that identifier, so the next file finds them.
 *
 * Every person created this way is UNVERIFIED and listed in Unverified People. A row the ladder found
 * candidates for is never auto-created under any setting; it goes to review.
 */
export const AUTO_CREATE_MODES = ['OFF', 'UNIDENTIFIED', 'UNMATCHED'];
export const facilitySummary = z.object({
    id: z.string(),
    facilityCode: z.string(),
    name: z.string(),
    facilityType: z.string().optional(),
    /**
     * The agency's category, coded against the `agencyType` lookup — OCC-1, the customer's own first
     * story ("add a category for AGENCIES … PUBLIC SAFETY/LAW ENFORCEMENT").
     *
     * Distinct from `facilityType`, which is free text and predates it. A coded category is what makes
     * "roll up by agency type" answerable; free text counts spellings.
     */
    agencyType: z.string().optional(),
    dataShape: agencyDataShape.optional(),
    /**
     * What a participant ID this agency registers is prefixed with
     * (docs/PARTICIPANT-ID-PLAN.md §2, D-1).
     *
     * Absent means `PTC-`, which is what every participant registered before this existed carries, and
     * what a registration with no agency gets (D-2, D-9). Several agencies may share one on purpose:
     * the prefix names the reporting organisation and never the treatment site, because an ID reading
     * `METHCLINIC-000412` states a treatment relationship and cannot be withdrawn once printed.
     */
    participantIdPrefix: z.string().optional(),
    /**
     * Which unmatched rows from this agency create a participant — see `AUTO_CREATE_MODES` (§3, D-6, D-7).
     *
     * OFF unless somebody turned it on. `register` performs no deduplication, so this trades an
     * accurate "number of people" for demographics that reach the breakdowns — the trade the customer
     * named, with the auto-created records marked UNVERIFIED so the first number stays legible.
     */
    autoCreate: z.enum(AUTO_CREATE_MODES).optional(),
    externalCode: z.string().optional(),
    address: facilityAddress.optional(),
    contact: facilityContact.optional(),
    /** Present on the detail response only — it is configuration, not list data. */
    ingestion: facilityIngestion.optional(),
    status: z.enum(['ACTIVE', 'INACTIVE']),
    /**
     * Open cases citing this facility. Present on the detail response only — computing it per
     * row would be one count query per list item, and the number is only ever acted on when
     * somebody is about to retire the facility.
     */
    openCaseCount: z.number().optional(),
    createdAt: z.string(),
});
export const facilityListResponse = pageOf(facilitySummary);
/**
 * Participant — one de-identified person, as one org holds them (D1, D5).
 *
 * Two DTOs on purpose. The summary carries no date of birth and no demographics: a list of
 * fifty people is the request most likely to be made casually, logged, or left open on a
 * shared screen, and it almost never needs PHI. Callers who need it fetch the one record
 * they are working on, which is a separate request and a separate audit trail.
 */
/**
 * How far a stored date of birth can be trusted as a disambiguator.
 *
 * Only EXACT ever produces a match key. PARTIAL is a real but imprecise date (year-only,
 * year-month); PLACEHOLDER is a value that looks like a system default rather than a
 * birthday — 1900-01-01, the Unix epoch, any 1 January. Both route to human review rather
 * than auto-matching, because a date of birth must never cause a false match.
 */
export const dobQuality = z.enum(['EXACT', 'PARTIAL', 'PLACEHOLDER', 'ABSENT']);
// ─── Phone numbers (OCC-45, decided 10/15) ─────────────────────────────────────────────────
/**
 * A participant's phones: a primary and an optional secondary, each with a type. US numbers only, stored as
 * ten digits, encrypted like every other personal field, and shown only to a reader with record-level access
 * (measure:read and participant:read) — in a file too.
 */
export const PHONE_TYPES = ['MOBILE', 'HOME', 'WORK'];
export const PHONE_TYPE_LABELS = {
    MOBILE: 'Mobile',
    HOME: 'Home',
    WORK: 'Work',
};
/**
 * The ten digits of a US number as written, or undefined when it is not one.
 *
 * Punctuation and a leading +1 or 1 are dropped. A North American number's area code and exchange each start
 * 2–9, and neither is N11; anything else is a typo, refused rather
 * than stored as something nobody can dial.
 */
export function usPhoneDigits(value) {
    let digits = value.replace(/[\s().\-+]/g, '');
    if (!/^\d+$/.test(digits))
        return undefined;
    if (digits.length === 11 && digits.startsWith('1'))
        digits = digits.slice(1);
    if (digits.length !== 10)
        return undefined;
    const area = digits.slice(0, 3);
    const exchange = digits.slice(3, 6);
    if (!/^[2-9]\d\d$/.test(area) || !/^[2-9]\d\d$/.test(exchange))
        return undefined;
    if (/^[2-9]11$/.test(area) || /^[2-9]11$/.test(exchange))
        return undefined;
    return digits;
}
/** "(239) 555-0134" from ten digits; anything else as given. */
export function formatUsPhone(digits) {
    return /^\d{10}$/.test(digits)
        ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
        : digits;
}
/** What a phone input mask shows while typing: "(239) 55", "(239) 555-01". Digits beyond ten are dropped. */
export function maskUsPhone(typed) {
    const d = typed
        .replace(/\D/g, '')
        .replace(/^1(?=\d{10})/, '')
        .slice(0, 10);
    if (d.length === 0)
        return '';
    if (d.length < 4)
        return `(${d}`;
    if (d.length < 7)
        return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
/**
 * A date of birth as it is typed: digits only, a hyphen after the year and after the month, so "19850307"
 * reads "1985-03-07". A year, or a year and month, stays as typed: both are accepted precisions.
 */
export function maskDateOfBirth(typed) {
    const d = typed.replace(/\D/g, '').slice(0, 8);
    if (d.length <= 4)
        return d;
    if (d.length <= 6)
        return `${d.slice(0, 4)}-${d.slice(4)}`;
    return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
}
/**
 * Whether a typed date of birth is after `today` (YYYY-MM-DD): refused, not flagged (decided 10/15). A
 * partial date is in the future when its year, or year and month, is.
 */
export function dateOfBirthInFuture(value, today) {
    const v = value.trim();
    if (/^\d{4}$/.test(v))
        return v > today.slice(0, 4);
    if (/^\d{4}-\d{2}$/.test(v))
        return v > today.slice(0, 7);
    if (/^\d{4}-\d{2}-\d{2}$/.test(v))
        return v > today;
    return false;
}
export const phoneInput = z.object({
    number: z
        .string()
        .trim()
        .max(32)
        .refine((v) => usPhoneDigits(v) !== undefined, 'must be a US phone number, like (239) 555-0134')
        .transform((v) => usPhoneDigits(v)),
    type: z.enum(PHONE_TYPES),
});
/** A phone as a reader with record-level access sees it. */
export const participantPhone = z.object({ number: z.string(), type: z.enum(PHONE_TYPES) });
export const participantSummary = z.object({
    id: z.string(),
    participantCode: z.string(),
    status: z.enum(['ACTIVE', 'INACTIVE', 'MERGED']),
    ageBand: z.string().optional(),
    /**
     * The Safe Harbor three-digit postal prefix. Present on the summary for the same reason
     * `ageBand` is: it is the deliberately-safe derived form, and it is the one geographic value
     * that can be aggregated without decrypting a record.
     */
    zip3: z.string().optional(),
    /** The full five-digit ZIP, decrypted for a reader of people (tester feedback 10/7). */
    zip: z.string().optional(),
    /**
     * Phones, formatted, for a reader with record-level access only (OCC-45); absent for everybody else. Flat
     * rather than nested, so an export column can name each.
     */
    primaryPhone: z.string().optional(),
    primaryPhoneType: z.string().optional(),
    secondaryPhone: z.string().optional(),
    secondaryPhoneType: z.string().optional(),
    sex: z.string().optional(),
    /**
     * The facility this participant is associated with.
     *
     * A data association only — nothing in any access decision reads it. Facility-based access
     * enforcement is a separate, unbuilt piece of work (docs/FACILITY-SCOPING-PLAN.md).
     */
    facilityId: z.string().optional(),
    facilityName: z.string().optional(),
    /** Quality metadata, not PHI — so a reviewer can triage a list without decrypting it. */
    dobQuality,
    dobFlags: z.array(z.string()),
    /**
     * How sure we are this is a person somebody identified (docs/PARTICIPANT-ID-PLAN.md §4).
     *
     * Optional at the boundary so a fixture or an older response without it still parses; absent reads as
     * VERIFIED, exactly as it does in the database.
     */
    identityConfidence: z.enum(['VERIFIED', 'UNVERIFIED']).optional(),
    registeredAt: z.string(),
    createdAt: z.string(),
});
/**
 * One row of the cleanup queue (docs/PARTICIPANT-ID-PLAN.md D-8): a person the import invented from a
 * row that named nobody, with the batch and row that did it.
 */
export const unverifiedParticipant = participantSummary.extend({
    sourceBatchId: z.string().optional(),
    sourceRowNumber: z.number().optional(),
});
export const unverifiedParticipantListResponse = pageOf(unverifiedParticipant);
export const participantDemographics = z.object({
    race: z.string().optional(),
    ethnicity: z.string().optional(),
    disabilityStatus: z.string().optional(),
    veteranStatus: z.string().optional(),
    /**
     * The stable dimensions from CRS CDE-01 and CDE-02.
     *
     * Free text for now — coded dropdowns are a later-phase customer decision
     * (docs/DATA-CAPTURE-PLAN.md §9 D-1), which is why these carry no `*_OPTIONS` vocabulary
     * beside them the way race and ethnicity do. When they are coded, the vocabularies land in
     * this file and the inputs become selects; the stored type does not change.
     */
    incomeLevel: z.string().optional(),
    employmentStatus: z.string().optional(),
    housingStatus: z.string().optional(),
    insuranceCoverage: z.string().optional(),
    mhDiagnosis: z.string().optional(),
    sudDiagnosis: z.string().optional(),
    dvSurvivor: z.string().optional(),
    /** Recidivist. Coded: Yes / No / Unknown. */
    recidivist: z.string().optional(),
    /** The day somebody was marked one, as `YYYY-MM-DD`. A day rather than an instant. */
    recidivistDate: z.string().optional(),
    /** Co-occurring MH + SUD diagnosis. Coded: Yes / No / Unknown. */
    coOccurringDx: z.string().optional(),
    /** Household composition, coded against the same `familyType` list the family record uses. */
    familyType: z.string().optional(),
    /** The full code. The retainable prefix is `zip3` on the participant itself. */
    postalCode: z.string().optional(),
});
export const CIRCUMSTANCE_DIMENSIONS = [
    { key: 'incomeLevel', label: 'Income level', category: 'incomeLevel' },
    { key: 'employmentStatus', label: 'Employment status', category: 'employmentStatus' },
    { key: 'housingStatus', label: 'Housing status', category: 'housingStatus' },
    { key: 'insuranceCoverage', label: 'Insurance coverage', category: 'insuranceCoverage' },
    { key: 'mhDiagnosis', label: 'Mental health diagnosis', category: 'mhDiagnosis' },
    { key: 'sudDiagnosis', label: 'Substance use diagnosis', category: 'sudDiagnosis' },
    { key: 'dvSurvivor', label: 'Domestic violence survivor', category: 'dvSurvivor' },
    {
        key: 'familyType',
        label: 'Family by type',
        category: 'familyType',
        hint: 'The household this person is part of, as the agency described it. Recorded on the person, so a file can say it without naming anybody else in the household.',
    },
    { key: 'recidivist', label: 'Recidivist', category: 'recidivist' },
    {
        key: 'recidivistDate',
        label: 'Date marked recidivist',
        /**
         * A date control rather than a text box, and the only date in this block.
         *
         * `kind` exists so the form can tell the three apart — a coded dimension has a `category`, a date says so,
         * and what is left is free text. Typing a date into a text box is how `03/04` becomes April the third.
         */
        kind: 'date',
        hint: 'Stored encrypted, like the flag it belongs to. Only meaningful alongside a Yes.',
    },
    {
        key: 'postalCode',
        label: 'ZIP / postal code',
        placeholder: 'e.g. 34112',
        hint: 'Stored encrypted. Only the first three digits are retained for reporting.',
    },
];
/** The API's limit on the one remaining free-text dimension, mirrored so the form matches. */
export const FREE_TEXT_MAX_LENGTH = 200;
/** One participant's date of birth, from the privileged endpoint. Never part of the detail DTO. */
export const participantDateOfBirth = z.object({
    /** Absent when the participant was registered without one — a supported state, and audited alike. */
    dateOfBirth: z.string().optional(),
    dobQuality: z.string(),
    dobPrecision: z.string().optional(),
});
export const participantDetail = participantSummary.extend({
    /**
     * No `dateOfBirth`, deliberately.
     *
     * It was here, and was returned to every holder of `participant:read` with no audit. A date of
     * birth is directly identifying, so it now has its own endpoint behind `participant:dob:read`
     * and one audit entry per read (docs/PARTICIPANT-DOB-ACCESS-PLAN.md §1). `dobQuality` and
     * `ageBand` stay on the summary — they are what reporting and matching actually use.
     */
    /**
     * The band somebody recorded directly, distinct from `ageBand`.
     *
     * `ageBand` is what is actually shown, and it is derived from the date of birth whenever there
     * is one — a derived band always wins, so the two cannot disagree on screen. This is what the
     * source said, and it is only consulted when there is no date to derive from.
     */
    ageBandRecorded: z.string().optional(),
    demographics: participantDemographics,
    /**
     * The agencies' own IDs for this person, each with the agency that issued it (tester feedback 10/7): what a
     * "Client / Chart ID" column fills, so a mapped ID is somewhere on the person's screen.
     */
    externalIds: z
        .array(z.object({ facilityId: z.string(), facilityName: z.string().optional(), value: z.string() }))
        .optional(),
    /** True when an import introduced this person rather than somebody registering them. */
    registeredByImport: z.boolean().optional(),
    /**
     * Present only on a record that was merged away (PARTICIPANT-MERGE-PLAN §3.7).
     *
     * A tombstone keeps its own code and its own audit trail, so it stays readable by id — and it has
     * to say where the person's history went, or somebody who looked up an old code lands on a record
     * marked MERGED with nothing on it.
     */
    mergedIntoParticipantId: z.string().optional(),
    mergeId: z.string().optional(),
    /**
     * Set when a lookup BY CODE landed here through a merged-away code (M-8).
     *
     * Codes get written on paperwork, so they resolve forever — to the survivor, not to the tombstone.
     * This names the code that was used, so the screen can say so rather than silently showing a
     * different person's record than the one that was asked for.
     */
    resolvedFromMergedCode: z.string().optional(),
});
/**
 * Duplicate detection and merge (docs/PARTICIPANT-MERGE-PLAN.md).
 *
 * `signals` and `status` are `z.string()` rather than enums on purpose, the same choice the match
 * review contract makes: a new signal added server-side should render as an unfamiliar row, not
 * blank the whole list at the parse boundary. `evidence` is what the screen shows — the score is
 * present because the API returns it, and deliberately not displayed: a number invites somebody to
 * treat it as a decision, where a sentence invites them to go and look at the two records.
 */
export const mergeCandidate = z.object({
    id: z.string(),
    participantIdA: z.string(),
    participantIdB: z.string(),
    participantCodeA: z.string(),
    participantCodeB: z.string(),
    signals: z.array(z.string()),
    score: z.number(),
    status: z.string(),
    /** The same thing the score says, in words. This is what a reviewer reads. */
    evidence: z.string(),
    dismissedReason: z.string().optional(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
});
export const mergeCandidateListResponse = pageOf(mergeCandidate);
/** What one scan found. Pairs a human already decided about are not offered again. */
export const mergeScanResult = z.object({
    scanned: z.number(),
    found: z.number(),
    alreadyOpen: z.number(),
    suppressed: z.number(),
});
/** One collection a merge touched, and what it did there. */
export const mergeRepointed = z.object({
    collectionName: z.string(),
    moved: z.array(z.string()).optional(),
    withdrawn: z.array(z.string()).optional(),
});
/**
 * What the merge did to the identity spine, or deliberately did not do (M-1).
 *
 * `merged: false` is not a failure. It means another org holds a record against the identity, so
 * the org's merge stopped at its own boundary rather than asserting that two of somebody else's
 * people are one person.
 */
export const mergeSpineOutcome = z.object({
    merged: z.boolean(),
    identityId: z.string(),
    survivorIdentityId: z.string(),
    otherOrgCount: z.number().optional(),
});
/** One field the two records disagreed about, and what the reviewer chose. */
export const mergeConflictResolution = z.object({
    field: z.string(),
    survivorValue: z.string().optional(),
    mergedValue: z.string().optional(),
    chosen: z.string().optional(),
});
/**
 * What a merge reports back, including the two things a person has to look at afterwards.
 *
 * Nothing is deduplicated automatically (M-10) and both external IDs are kept (M-12), so the result
 * names what it left for a human rather than quietly resolving it.
 */
export const mergeResult = z.object({
    mergeId: z.string(),
    survivorId: z.string(),
    survivorCode: z.string(),
    mergedParticipantId: z.string(),
    mergedParticipantCode: z.string(),
    repointed: z.array(mergeRepointed),
    spine: mergeSpineOutcome,
    possibleDuplicateEntries: z.array(z.object({ kind: z.string(), day: z.string(), count: z.number() })),
    facilitiesWithTwoExternalIds: z.array(z.string()),
});
/** The stored record of a merge — what a deliberate, manual walk-back would read (M-6). */
export const participantMergeRecord = z.object({
    id: z.string(),
    survivorId: z.string(),
    survivorCode: z.string(),
    mergedParticipantId: z.string(),
    mergedParticipantCode: z.string(),
    reason: z.string(),
    differentDobAcknowledged: z.boolean().optional(),
    conflictResolutions: z.array(mergeConflictResolution).optional(),
    repointed: z.array(mergeRepointed),
    spine: mergeSpineOutcome,
    mergedBy: z.string(),
    mergedAt: z.string(),
});
export const participantListResponse = pageOf(participantSummary);
export const RACE_OPTIONS = [
    { code: 'AMERICAN_INDIAN_ALASKA_NATIVE', label: 'American Indian or Alaska Native' },
    { code: 'ASIAN', label: 'Asian' },
    { code: 'BLACK_AFRICAN_AMERICAN', label: 'Black or African American' },
    { code: 'NATIVE_HAWAIIAN_PACIFIC_ISLANDER', label: 'Native Hawaiian or Other Pacific Islander' },
    { code: 'WHITE', label: 'White' },
    { code: 'MORE_THAN_ONE_RACE', label: 'More than one race' },
    { code: 'UNKNOWN', label: 'Unknown / declined' },
];
export const ETHNICITY_OPTIONS = [
    { code: 'HISPANIC_LATINO', label: 'Hispanic or Latino' },
    { code: 'NOT_HISPANIC_LATINO', label: 'Not Hispanic or Latino' },
    { code: 'UNKNOWN', label: 'Unknown / declined' },
];
/** Shared by disability and veteran status — same three answers, same meanings. */
export const YES_NO_UNKNOWN_OPTIONS = [
    { code: 'YES', label: 'Yes' },
    { code: 'NO', label: 'No' },
    { code: 'UNKNOWN', label: 'Unknown / declined' },
];
/**
 * Sex is a configured vocabulary now, not a constant here (RECORD-MANAGEMENT-PLAN D-7).
 *
 * This held 'F', 'M', 'X' and 'UNKNOWN', and every screen that rendered sex as a coded value asked
 * `lookup` for a category that did not exist — so the dropdowns were empty while the forms wrote
 * these. The customer's own values (Male, Female, Other, Unknown) are seeded into `lookup`, editable
 * in Record Management, and `migrations/20260921100100-sex-coded.cjs` codes what was already stored.
 *
 * Deleted rather than deprecated: two definitions of one vocabulary is how 'F' and 'FEMALE' end up
 * on different records for the same person.
 */
/** Resolves a stored code to its label, falling back to the code so nothing renders blank. */
export function demographicLabel(options, code) {
    if (code === undefined)
        return undefined;
    return options.find((o) => o.code === code)?.label ?? code;
}
/**
 * The customer's age bands (COLLIER-CATEGORIES.md), replacing the nine ten-year bands the API
 * previously derived.
 *
 * Two of them carry more weight than they look:
 *
 * '56+' is open-ended, and it is also where HIPAA Safe Harbor capping lands. A record whose birth
 * year is suppressed for implying an age over 89 reports this band — more aggregated than the
 * '90+' bucket it replaces, so the change is safe in the only direction that matters.
 *
 * 'Unknown' is a real band rather than an absence. A participant with no date of birth and no
 * recorded band belongs in a denominator rather than outside it, so the API returns this instead of
 * nothing.
 *
 * `RECORDABLE_AGE_BANDS` and `AGE_BAND_OPTIONS` hold the same values and stay separate because they
 * are used for different things: one populates an input that writes to a record, the other a
 * filter. Should the filter ever gain an "any" pseudo-band, the recordable list must not.
 */
export const RECORDABLE_AGE_BANDS = [
    '0-12',
    '13-17',
    '18-24',
    '25-55',
    '56+',
    'Unknown',
];
export const AGE_BAND_OPTIONS = [
    '0-12',
    '13-17',
    '18-24',
    '25-55',
    '56+',
    'Unknown',
];
/**
 * The cross-org view. Codes, orgs and statuses only — seeing that a person exists in
 * another org is a different privilege from reading that org's record of them.
 */
export const participantIdentityLinks = z.object({
    identityCode: z.string(),
    firstSeenOrgId: z.string(),
    records: z.array(z.object({
        orgId: z.string(),
        participantCode: z.string(),
        status: z.enum(['ACTIVE', 'INACTIVE', 'MERGED']),
    })),
});
/** Case — a linking/context record, never the source of truth (D3). */
// ─── Time standards (slice 12, OCC-46) ───────────────────────────────────────────────────────
/**
 * How quickly things should happen on a case (COUNTY-DEFINITIONS-NEEDED §25), each set in Admin → Time
 * standards: referral to intake, intake to the first service, a follow-up after a non-fatal overdose, and a
 * case review at a fixed interval. Each is shown on the case, on the case list and in a report as on track,
 * due soon or overdue while it is open, and as met, met late or missed once it is not.
 */
export const TIME_STANDARD_KEYS = [
    'referralToIntake',
    'intakeToService',
    'overdoseFollowUp',
    'caseReview',
];
export const TIME_STANDARD_TITLES = {
    referralToIntake: 'Referral to intake',
    intakeToService: 'Intake to first service',
    overdoseFollowUp: 'Follow-up after a non-fatal overdose',
    caseReview: 'Case review',
};
export const timeStandardSettings = z
    .object({
    referralToIntakeDays: z.number().int().min(1).max(365),
    intakeToServiceDays: z.number().int().min(1).max(365),
    followUpDays: z.number().int().min(1).max(365),
    reviewEveryDays: z.number().int().min(1).max(730),
    /** How many days before a deadline "due soon" begins. */
    dueSoonDays: z.number().int().min(0).max(60),
})
    .strict();
export const TIME_STANDARD_DEFAULTS = {
    referralToIntakeDays: 7,
    intakeToServiceDays: 14,
    followUpDays: 30,
    reviewEveryDays: 90,
    dueSoonDays: 3,
};
export const TIME_STANDARD_STATUSES = [
    'ON_TRACK',
    'DUE_SOON',
    'OVERDUE',
    'MET',
    'LATE',
    'MISSED',
];
export const TIME_STANDARD_STATUS_WORDS = {
    ON_TRACK: 'On track',
    DUE_SOON: 'Due soon',
    OVERDUE: 'Overdue',
    MET: 'Met',
    LATE: 'Met late',
    MISSED: 'Missed',
};
/** Worst first: what a case's list row shows when several standards apply. */
export const TIME_STANDARD_STATUS_ORDER = [
    'OVERDUE',
    'DUE_SOON',
    'ON_TRACK',
    'MISSED',
    'LATE',
    'MET',
];
export const timeStandardResult = z.object({
    key: z.enum(TIME_STANDARD_KEYS),
    status: z.enum(TIME_STANDARD_STATUSES),
    /** The deadline (YYYY-MM-DD, county). */
    dueOn: z.string(),
    /** When it was done, if it was. */
    doneOn: z.string().optional(),
    /** What it is about, where a case has several: "Overdose on 2026-09-14". */
    about: z.string().optional(),
});
function daysBetween(from, to) {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}
/**
 * One deadline: done on time (met), done after (met late), not done and the case closed first (missed), or
 * still open — on track, due soon within `dueSoonDays` of it, overdue past it.
 */
function deadline(key, start, days, doneOn, facts, settings, today, about) {
    const dueOn = addCalendarDays(start, days);
    const base = { key, dueOn, ...(doneOn ? { doneOn } : {}), ...(about ? { about } : {}) };
    if (doneOn)
        return { ...base, status: doneOn <= dueOn ? 'MET' : 'LATE' };
    if (facts.status === 'CLOSED')
        return { ...base, status: 'MISSED' };
    if (today > dueOn)
        return { ...base, status: 'OVERDUE' };
    return {
        ...base,
        status: daysBetween(today, dueOn) <= settings.dueSoonDays ? 'DUE_SOON' : 'ON_TRACK',
    };
}
/** Every standard that applies to a case, today. */
export function evaluateCaseStandards(facts, settings, today) {
    const out = [];
    // Intake is the case's opening: a case with a referral date was taken in on the day it opened.
    if (facts.referredOn) {
        out.push(deadline('referralToIntake', facts.referredOn, settings.referralToIntakeDays, facts.openedOn, facts, settings, today));
    }
    out.push(deadline('intakeToService', facts.openedOn, settings.intakeToServiceDays, facts.firstServiceOn, facts, settings, today));
    for (const o of facts.overdoses) {
        out.push(deadline('overdoseFollowUp', o.on, settings.followUpDays, o.followedUpOn, facts, settings, today, `Overdose on ${o.on}`));
    }
    // Reviews recur: only the next one is due, counted from the last (or from the opening), while open.
    if (facts.status === 'OPEN') {
        const last = facts.lastReviewOn && facts.lastReviewOn > facts.openedOn
            ? facts.lastReviewOn
            : facts.openedOn;
        out.push(deadline('caseReview', last, settings.reviewEveryDays, undefined, facts, settings, today));
    }
    return out;
}
/** The status a list row shows: the worst of a case's standards. */
export function worstStatus(results) {
    return TIME_STANDARD_STATUS_ORDER.find((s) => results.some((r) => r.status === s));
}
export const timeStandardsResponse = z.object({
    settings: timeStandardSettings,
    custom: z.boolean(),
});
/** One case's standards, or the worst of several cases' for a list. */
export const caseStandardsResponse = z.object({ results: z.array(timeStandardResult) });
export const caseStandardsWorstResponse = z.object({
    worst: z.record(z.string(), z.enum(TIME_STANDARD_STATUSES)),
});
export const timeStandardsReportRow = z.object({
    caseId: z.string(),
    caseCode: z.string(),
    facilityName: z.string().optional(),
    result: timeStandardResult,
});
export const timeStandardsReport = z.object({
    asOf: z.string(),
    settings: timeStandardSettings,
    /** Per standard, how many of each status. */
    counts: z.record(z.string(), z.record(z.string(), z.number())),
    /** What needs doing: everything overdue or due soon, most overdue first. */
    attention: z.array(timeStandardsReportRow),
    casesConsidered: z.number(),
});
export const caseSummary = z.object({
    id: z.string(),
    caseCode: z.string(),
    participantId: z.string(),
    participantCode: z.string().optional(),
    facilityId: z.string(),
    facilityName: z.string().optional(),
    assignedUserId: z.string().optional(),
    status: z.enum(['OPEN', 'CLOSED']),
    openedAt: z.string(),
    closedAt: z.string().optional(),
    contextNote: z.string().optional(),
    /**
     * Episode fields (CRS CASE-06). Free text for now (docs/DATA-CAPTURE-PLAN.md §9 D-1).
     *
     * These give a case episode semantics — what kind of episode, where the referral came from,
     * why it ended — WITHOUT making it the source of truth. D3 still holds: what happened to a
     * person lives in `measure_entry`, hanging off the participant. A measure entry carries no
     * caseId, deliberately, so no clinical fact depends on an episode existing.
     */
    episodeType: z.string().optional(),
    /** A code from the org's `episodeType` list (CASE-DASHBOARD CD-5). */
    episodeTypeCode: z.string().optional(),
    referralSource: z.string().optional(),
    episodeNote: z.string().optional(),
    closeReason: z.string().optional(),
    /** A code from the org's `caseCloseReason` list (CD-5). */
    closeReasonCode: z.string().optional(),
    /** The day the referral was made, in the county (YYYY-MM-DD) — for referral to intake. */
    referredOn: z.string().optional(),
    /** Where the case stands now, as a code from the org's `casePhase` list. */
    phaseCode: z.string().optional(),
    /**
     * The person's housing status when the case opened and when it closed (improvement rate) — encrypted at
     * rest like the person's own, and returned only to a reader who may read people.
     */
    housingAtOpening: z.string().optional(),
    housingAtClosing: z.string().optional(),
    /** Active additional participants and treatment records — the detail response fills these. */
    participantCount: z.number().optional(),
    treatmentCount: z.number().optional(),
    createdAt: z.string(),
});
export const caseListResponse = pageOf(caseSummary);
/**
 * Additional people on an episode — a guardian, a family member in a joint programme.
 *
 * Modelled on `reportParticipantLink`, and uniqueness includes `role` for the same reason D4
 * gives: the same person can be two things on one episode, and each is separately provenanced.
 *
 * The episode's PRIMARY participant is **not** duplicated here. It lives on `case.participantId`
 * and nowhere else — one participant, one home. Two places to ask "whose episode is this" would
 * disagree within a month.
 */
export const caseParticipant = z.object({
    id: z.string(),
    caseId: z.string(),
    participantId: z.string(),
    participantCode: z.string().optional(),
    /** Free text: 'Guardian', 'Sibling in joint programme'. Not a fixed vocabulary yet (§9 D-1). */
    role: z.string(),
    status: z.enum(['ACTIVE', 'REMOVED']),
    assignedAt: z.string(),
    assignedBy: z.string(),
    removedAt: z.string().optional(),
    removedBy: z.string().optional(),
    removalNote: z.string().optional(),
});
/**
 * A treatment recorded against an episode.
 *
 * Its own collection rather than an array on the case: an episode accumulates treatments over
 * months, and an unbounded growing array sits on the document every case list reads — the same
 * argument PHASE-2-DATA-MODEL.md §3.6 made about report links.
 *
 * This records what treatment was planned or delivered, which is an episode attribute. It is NOT
 * where an overdose, an arrest or an ED visit goes — those are facts about a person and live in
 * `measure_entry`. Keeping that line is what stops the case becoming the spine again.
 */
export const caseTreatment = z.object({
    id: z.string(),
    caseId: z.string(),
    participantId: z.string(),
    participantCode: z.string().optional(),
    /** Free text for now (§9 D-1). */
    treatmentType: z.string().optional(),
    modality: z.string().optional(),
    startedAt: z.string(),
    endedAt: z.string().optional(),
    status: z.enum(['ACTIVE', 'COMPLETED', 'DISCONTINUED', 'RETRACTED']),
    outcome: z.string().optional(),
    /** Clinical narrative. Encrypted at rest; present only on the detail response. */
    notes: z.string().optional(),
    recordedAt: z.string(),
    recordedBy: z.string(),
});
/** One change to a case's status (CASE-DASHBOARD-PLAN CD-2): append-only, oldest first. */
export const caseStatusEvent = z.object({
    type: z.enum(['OPENED', 'CLOSED', 'REOPENED']),
    at: z.string(),
    reason: z.string().optional(),
    reasonCode: z.string().optional(),
    source: z.enum(['MANUAL', 'IMPORT', 'REVERT', 'BACKFILL']),
    recordedAt: z.string(),
    recordedBy: z.string(),
});
export const caseDetail = caseSummary.extend({
    participants: z.array(caseParticipant),
    treatments: z.array(caseTreatment),
    /** Absent from responses made before the history existed; the list is the case's history. */
    history: z.array(caseStatusEvent).default([]),
    /** The latest review recorded on the case (slice 12): the case review standard counts from it. */
    lastReviewOn: z.string().optional(),
});
export const caseParticipantListResponse = z.object({ items: z.array(caseParticipant) });
export const caseTreatmentListResponse = z.object({ items: z.array(caseTreatment) });
/** The statuses a treatment moves through. RETRACTED stands in for deletion; there is none. */
export const TREATMENT_STATUSES = ['ACTIVE', 'COMPLETED', 'DISCONTINUED', 'RETRACTED'];
/** Report — one document from one facility, referencing one or more participants (D4). */
/**
 * Who holds an agency identifier starting with what somebody typed
 * (docs/QUERY-WORKSPACE-PLAN.md §3, §4.1).
 *
 * A row is an identifier, not a person: the same number at two agencies is two different people, which
 * is why the issuing facility is named beside every match. The participant appears as its CODE — the
 * same de-identified handle the list returns — because this is a way to find somebody's record, not a
 * way to read it.
 */
export const externalIdMatch = z.object({
    participantId: z.string(),
    participantCode: z.string(),
    facilityId: z.string(),
    facilityName: z.string().optional(),
    /** As the agency wrote it, not as it was normalized for matching. */
    value: z.string(),
});
export const externalIdSearchResponse = z.object({ items: z.array(externalIdMatch) });
/**
 * How many participants hold each value of a demographic (docs/QUERY-WORKSPACE-PLAN.md §2a).
 *
 * ── Why counts and not a filter ───────────────────────────────────────────────────────
 * Twelve demographics are encrypted with randomized field encryption, so no `$match` can touch them and
 * no list can be filtered by one. §2 weighed three answers and this is the one that ships: four of those
 * dimensions have exact counters maintained inside the participant's own write transaction, so "how many
 * are recorded as MULTI_RACIAL" is answerable now, cheaply, without decrypting anything.
 *
 * It answers the reporting question. It deliberately does not produce a list of people — that would be a
 * decryption pass over the org's most sensitive column, which is D-1 and a slice of its own.
 *
 * ── Why there is no total and no percentage ───────────────────────────────────────────
 * Because the denominator would be wrong. These counters are incremented from the write path and have
 * never been backfilled, so a participant recorded before a dimension gained its counter is missing from
 * that dimension's totals — and a percentage of an under-counted denominator is confidently wrong in a way
 * a bare count is not. The caveat says so in words, on every dimension, rather than being a footnote
 * somebody can strip.
 */
/**
 * A value and how many of something carry it.
 *
 * Shared by the participant cohort counts and the custom-field breakdown, which are two different
 * questions over the same shape: how many PEOPLE are recorded as White, and how many RECORDS hold a
 * given value of a field somebody defined. Keeping one object is right; adding a field that only
 * means something to one of them is not — see `cohortCount` below, which broke the breakdown's
 * fixtures the moment it tried.
 */
export const codedCount = z.object({
    code: z.string(),
    /** Resolved through the vocabulary, retired values included, so a stored code still reads as itself. */
    label: z.string(),
    count: z.number(),
});
/**
 * A participant cohort count, which carries a second number the breakdown has no use for.
 *
 * `count` is people somebody identified, and it is what it has always been. D-5
 * (docs/PARTICIPANT-ID-PLAN.md §4): auto-created participants are counted BESIDE it and never inside
 * it, because `register` performs no deduplication — a monthly extract of demographics with no
 * identifiers makes one person per row, every month, and a single figure quietly including a year of
 * those reads as growth.
 *
 * `unverifiedCount` is always present, even as zero. A missing field and a zero say the same thing,
 * and only one of them is a promise a caller can rely on.
 *
 * ── Why this is its own object ───────────────────────────────────────────────────────
 * It was added to the shared one, which the custom-field breakdown also uses — so a breakdown of
 * "discharge disposition" was suddenly required to report how many of its values were unverified
 * PEOPLE, a question it cannot answer about a field that may live on an event. Its test fixtures
 * caught it.
 */
export const cohortCount = codedCount.extend({
    unverifiedCount: z.number(),
});
export const cohortDimension = z.object({
    dimension: z.string(),
    label: z.string(),
    /** Always present, and always the same sentence: these numbers are right about now, not about history. */
    caveat: z.string(),
    counts: z.array(cohortCount),
});
export const cohortCountsResponse = z.object({ items: z.array(cohortDimension) });
export const reportSourceFile = z.object({
    filename: z.string(),
    mimeType: z.string().optional(),
    sizeBytes: z.number(),
    checksumSha256: z.string(),
    receivedAt: z.string(),
    /** Short-lived presigned URL; the API never proxies object bytes. */
    downloadUrl: z.string().optional(),
});
export const reportSummary = z.object({
    id: z.string(),
    reportCode: z.string(),
    facilityId: z.string(),
    facilityName: z.string().optional(),
    reportType: z.string().optional(),
    reportDate: z.string().optional(),
    receivedAt: z.string(),
    externalReportId: z.string().optional(),
    status: z.enum(['RECEIVED', 'PARSED', 'LINKED', 'NEEDS_REVIEW', 'SUPERSEDED']),
    participantCount: z.number(),
    sourceFile: reportSourceFile.optional(),
    createdAt: z.string(),
});
export const reportListResponse = pageOf(reportSummary);
/**
 * The headline counts behind the Reports page's status tiles.
 *
 * `reports` is a loose record rather than an enum of the five known statuses, deliberately:
 * a status added server-side should render as an unfamiliar tile, not blank the page at the
 * parse boundary. The API zero-fills every status it knows about, so a missing key means a
 * status this SPA has never heard of rather than "none of those exist".
 */
export const reportStatusCounts = z.object({
    reports: z.record(z.string(), z.number()),
    total: z.number(),
    /** Open rows in the review queue — the number behind the Needs Review tile's sub-line. */
    openReviewItems: z.number(),
});
/**
 * How a participant came to be linked to a report — persisted forever, on the link.
 *
 * This is what answers "why did it pick her?" a year later. Phase 2 writes only SYSTEM_ID
 * and MANUAL; the ingestion ladder and review queue write the rest against the same shape.
 */
export const matchMethod = z.enum([
    'SYSTEM_ID',
    'EXTERNAL_ID',
    'DOB_UNIQUE',
    'DOB_PLUS_ATTRS',
    'MANUAL',
    'MANUAL_NEW',
]);
export const reportParticipantLink = z.object({
    id: z.string(),
    reportId: z.string(),
    participantId: z.string(),
    participantCode: z.string().optional(),
    /** The same person may appear twice on one report in different roles (D4). */
    role: z.enum(['SUBJECT', 'CONTACT', 'GUARDIAN', 'WITNESS']),
    caseId: z.string().optional(),
    matchMethod,
    matchTier: z.number().optional(),
    matchConfidence: z.number(),
    matchedBy: z.string(),
    matchedAt: z.string(),
    candidates: z.array(z.object({
        participantId: z.string(),
        participantCode: z.string(),
        score: z.number(),
        reason: z.string(),
    })),
    reviewedBy: z.string().optional(),
    reviewedAt: z.string().optional(),
    reviewNote: z.string().optional(),
    /** Retracted, never deleted — the history is the point. */
    status: z.enum(['ACTIVE', 'RETRACTED']),
});
export const reportParticipantListResponse = z.object({
    items: z.array(reportParticipantLink),
});
/**
 * Match review — a subject the ladder would not decide (docs/MATCH-LADDER.md §1).
 *
 * The candidate set is what the machine found, not a menu the reviewer is confined to:
 * `resolve` takes a free participant id, because someone who knows the person may well pick
 * a participant the date of birth never surfaced.
 *
 * `reason` and `role` are `z.string()` rather than enums, deliberately, and against the rule
 * every other contract here follows. The API's DTO already declares both as strings, and an
 * enum would mean that adding a sixth reason code server-side blanks the whole queue at the
 * parse boundary — the one screen whose job is to be reachable. An unrecognised code renders
 * as itself instead.
 */
export const matchReviewCandidate = z.object({
    participantId: z.string(),
    participantCode: z.string(),
    score: z.number(),
    reason: z.string(),
});
export const matchReviewItem = z.object({
    id: z.string(),
    /**
     * Which kind of thing is waiting on a person.
     *
     * One queue serves both reports and measure entries (docs/DATA-CAPTURE-PLAN.md §9 D-2), and
     * the two carry different identifiers — so exactly one of `reportId` / `measureEntryId` is
     * present, and this says which. A `z.string()` rather than an enum for the same reason
     * `reason` is one: a third source added server-side should render as an unfamiliar row, not
     * blank the queue at the parse boundary.
     */
    sourceKind: z.string(),
    /** Present when `sourceKind` is REPORT. */
    reportId: z.string().optional(),
    /** Present when `sourceKind` is MEASURE_ENTRY. */
    measureEntryId: z.string().optional(),
    measureEntryCode: z.string().optional(),
    role: z.string(),
    /**
     * Position in the submitted subject list — the only handle a report subject with no
     * participant has. Absent for a measure entry, which is one subject and is identified by its
     * own id.
     */
    subjectIndex: z.number().optional(),
    reason: z.string(),
    candidates: z.array(matchReviewCandidate),
    status: z.enum(['OPEN', 'RESOLVED', 'DISMISSED']),
    /**
     * What the source supplied, minus the date of birth. The API withholds it here on purpose:
     * it would put PHI into the most casually-requested response in the queue, for no decision
     * it actually informs. It remains stored, encrypted, if an investigation later needs it.
     */
    input: z.object({
        /** Absent when whoever submitted the row stated no agency — a hand-entered event, or a typed grid row. */
        facilityId: z.string().optional(),
        externalId: z.string().optional(),
        participantCode: z.string().optional(),
        sex: z.string().optional(),
    }),
    resolvedBy: z.string().optional(),
    resolvedAt: z.string().optional(),
    resolutionNote: z.string().optional(),
    resolvedParticipantId: z.string().optional(),
    createdAt: z.string(),
});
export const matchReviewListResponse = pageOf(matchReviewItem);
/** The five reasons the ladder routes a subject to a person rather than deciding. */
export const MATCH_REVIEW_REASONS = [
    'AMBIGUOUS',
    'TIER_CONFLICT',
    'DOB_NOT_USABLE',
    'DOB_SINGLE_CANDIDATE',
    'NO_CANDIDATES',
];
/**
 * Measure entry — one recorded event, linked to a participant.
 *
 * The "entry linked to participant" pattern: one collection with a `kind`, rather than nine
 * near-identical tables. Every measure the Collaborative tracks is the same shape — something
 * happened, to someone, somewhere, on a date — and the differences between an overdose and a
 * drug-court enrolment are a handful of detail fields, not a different record.
 */
export const MEASURE_KINDS = [
    'FATAL_OVERDOSE',
    'NONFATAL_OVERDOSE',
    'NARCAN_ADMINISTERED',
    'NARCAN_DISTRIBUTED',
    'MARCHMAN_ACT',
    'BAKER_ACT',
    'ED_VISIT',
    'OPIOID_ARREST',
    'DRUG_COURT_PARTICIPATION',
    /**
     * Medication-assisted treatment: methadone, buprenorphine, naltrexone.
     *
     * A kind rather than a custom field, which is what it was. A custom field records a value and
     * nothing else; a kind is counted — it reaches the dashboard, the measure filters, the exports and
     * the roll-ups, and it carries `matMedication` so "methadone" and "buprenorphine" are separable
     * rather than both being "the MAT column had something in it".
     *
     * Named for both because the agency column is: a methadone clinic's feed and a prescriber's are the
     * same event with a different medication, and splitting them into two kinds would make every
     * question about treatment a union of two.
     */
    'METHADONE_MAT',
];
/** Display labels, so nine screaming-snake constants are not what a case worker reads. */
export const MEASURE_KIND_LABELS = {
    FATAL_OVERDOSE: 'Fatal overdose',
    NONFATAL_OVERDOSE: 'Non-fatal overdose',
    // An event of this kind is one ENCOUNTER; how many times Narcan was given in it is `narcanAdministrations`
    // (confirmed 10/7). Said in the label, so a count of these is never read as a count of administrations.
    NARCAN_ADMINISTERED: 'Narcan administered (encounters)',
    // One event per distribution ENCOUNTER (10/7); what was handed out is `narcanDoses`, as reported.
    NARCAN_DISTRIBUTED: 'Narcan distributed (encounters)',
    MARCHMAN_ACT: 'Marchman Act',
    BAKER_ACT: 'Baker Act',
    ED_VISIT: 'ED visit',
    OPIOID_ARREST: 'Opioid arrest',
    DRUG_COURT_PARTICIPATION: 'Drug court participation',
    METHADONE_MAT: 'Methadone / MAT',
};
/**
 * One colour per measure, everywhere it is drawn — tile, chart, legend and PDF (dashboard feedback B8). A kind
 * that is green in one chart and grey in the next makes a reader compare the wrong lines. Gray is kept for
 * "not recorded", "not yet attributed" and "unknown", so no measure is gray.
 */
export const MEASURE_COLORS = {
    FATAL_OVERDOSE: '#b02a37',
    NONFATAL_OVERDOSE: '#e8590c',
    NARCAN_ADMINISTERED: '#2b8a3e',
    NARCAN_DISTRIBUTED: '#82c91e',
    MARCHMAN_ACT: '#5f3dc4',
    BAKER_ACT: '#1971c2',
    ED_VISIT: '#0c8599',
    OPIOID_ARREST: '#8f5b2e',
    DRUG_COURT_PARTICIPATION: '#c2255c',
    METHADONE_MAT: '#9c36b5',
};
/** The colour of a value that is not a value: not recorded, not yet attributed, unknown. */
export const MUTED_COLOR = '#adb5bd';
/** The two overdose kinds, the dashboard's first section (feedback A2). */
export const OVERDOSE_KINDS = [
    'FATAL_OVERDOSE',
    'NONFATAL_OVERDOSE',
];
/**
 * Which detail fields each kind actually asks for.
 *
 * A form that asked every kind for every field would be asking a drug-court enrolment for its
 * Narcan dose. The field names come from CRS ADE-02; this table is what lets one form serve
 * nine kinds without nine hand-written forms, and it is read by the create page directly.
 */
export const MEASURE_DETAIL_FIELDS = {
    // `opioidType` is the route of administration, and only the overdose and Narcan kinds have one —
    // a Baker Act has no route. Distinct from `substance`, which is the drug.
    //
    // Every kind carries `sourceRowRef`: any of them can arrive on a file, and it is the agency's own
    // row identifier. It is hidden from the manual form by its `provenance` flag rather than by being
    // left out here, so the column mapper can still offer it for every kind.
    FATAL_OVERDOSE: [
        'confirmedStatus',
        'substanceType',
        'substance',
        'opioidType',
        'patientStatusInitial',
        'intentional',
        'narcanAdministeredBy',
        'transportedTo',
        'referralSource',
        'location',
        'sourceRowRef',
    ],
    NONFATAL_OVERDOSE: [
        'confirmedStatus',
        'substanceType',
        'substance',
        'opioidType',
        'patientStatusInitial',
        'intentional',
        'narcanUnits',
        'narcanAdministeredBy',
        'overdoseOutcome',
        'outcome',
        'transportedTo',
        'transportRefused',
        'referralSource',
        'referredTo',
        'location',
        'sourceRowRef',
    ],
    NARCAN_ADMINISTERED: [
        'opioidType',
        // The substance the encounter involved, from the same proposed list as an overdose's (10/8): Narcan by
        // substance, fentanyl included, beside overdoses by substance.
        'substanceType',
        'substance',
        'narcanUnits',
        'narcanAdministrations',
        'narcanLotNumber',
        'narcanAdministeredBy',
        'overdoseOutcome',
        'outcome',
        'transportedTo',
        'transportRefused',
        'location',
        'sourceRowRef',
    ],
    // An agency-level count (CRS ADE-01) — no person, so no patient status and no referral.
    NARCAN_DISTRIBUTED: ['narcanUnits', 'narcanDoses', 'narcanLotNumber', 'location', 'sourceRowRef'],
    MARCHMAN_ACT: [
        'admissionType',
        'disposition',
        'outcome',
        'referralSource',
        'referredTo',
        'sourceRowRef',
    ],
    BAKER_ACT: [
        'admissionType',
        'disposition',
        'outcome',
        'referralSource',
        'referredTo',
        'sourceRowRef',
    ],
    ED_VISIT: [
        'edDisposition',
        'lengthOfStayDays',
        'transportedTo',
        'disposition',
        'outcome',
        'referralSource',
        'referredTo',
        'location',
        'sourceRowRef',
    ],
    OPIOID_ARREST: [
        'arrestCharge',
        'arrestDisposition',
        'disposition',
        'substanceType',
        'substance',
        'location',
        'sourceRowRef',
    ],
    DRUG_COURT_PARTICIPATION: [
        'courtProgramStatus',
        'disposition',
        'outcome',
        'referralSource',
        'sourceRowRef',
    ],
    /**
     * `matMedication` first, because it is the fact that makes the event mean something.
     *
     * No `substance`: that is the drug somebody used, and conflating it with the medication they were
     * prescribed would make a question about fentanyl return methadone.
     */
    METHADONE_MAT: [
        'matMedication',
        'admissionType',
        'disposition',
        'outcome',
        'referralSource',
        'referredTo',
        'sourceRowRef',
    ],
};
export const MEASURE_DETAIL_FIELD_DEFS = [
    {
        key: 'confirmedStatus',
        label: 'Confirmed or suspected',
        placeholder: '',
        /**
         * Coded — Confirmed / Suspected / Unknown (CRS ADE-02, R71).
         *
         * ── Why coded, and why that is the whole point ────────────────────────────────────────
         * It was free text, so 'Confirmed', 'confirmed' and 'CONF' could all mean the same thing on three
         * records — and "how many confirmed overdoses did this agency report" would then count spellings
         * rather than overdoses. That is the exact debt DATA-CAPTURE-PLAN §9 D-1 named for the demographics,
         * and coding is what pays it off: `countByDetailCode` can group it, and the customer owns the values.
         *
         * ── Why it is NOT encrypted, unlike the participant flags ─────────────────────────────
         * Because it is a fact about an EVENT, not about a person. The model's line is already drawn and
         * stated: a demographic is encrypted, an overdose's disposition is not. Encrypting it would make it
         * exactly as uncountable as the free text it replaces, which would defeat the reason for coding it —
         * and 'Suspected' against an overdose at an agency discloses nothing about who the person was.
         */
        coded: true,
    },
    {
        key: 'substanceType',
        label: 'Substance (from the list)',
        placeholder: '',
        /**
         * The drug, coded from a proposed list (COUNTY-DEFINITIONS-NEEDED): what makes substance countable. The
         * words below are kept as written; a write whose words exactly match a value is coded from them, and
         * nothing else is guessed.
         */
        coded: true,
    },
    {
        key: 'substance',
        label: 'Substance (as written)',
        placeholder: 'e.g. Fentanyl',
        /**
         * Still free text, and waiting on the county (R71).
         *
         * This is the actual DRUG — fentanyl, heroin — as distinct from `opioidType`, which is the route.
         * Coding it needs the county's drug list, and inventing one here would put a vocabulary in front of
         * agencies that nobody agreed to. Tracked in COLLIER-CATEGORIES.md §6.
         */
    },
    {
        key: 'opioidType',
        label: 'Type of opioid use',
        placeholder: '',
        // Route of administration, from the lookup. Not the same question as `substance` above.
        coded: true,
    },
    {
        key: 'narcanUnits',
        label: 'Narcan units',
        placeholder: 'e.g. 2 doses',
        /**
         * Still free text, and the open question is not which values but which TYPE (R71).
         *
         * "2 doses" is prose, so units cannot be summed — and R70/R71 both want a quantity. Whether this
         * becomes a number depends on what the county means by a unit: a kit, a dose, a millilitre. Tracked
         * in COLLIER-CATEGORIES.md §6. Cheaper to settle before agencies fill it with sentences.
         */
    },
    {
        key: 'overdoseOutcome',
        label: 'Overdose outcome (from the list)',
        placeholder: '',
        // Coded from a proposed list, beside the words, on the same terms as `substanceType`.
        coded: true,
    },
    {
        key: 'outcome',
        label: 'Outcome (as written)',
        placeholder: 'e.g. Transported, survived',
        /**
         * Still free text, and waiting on the county (R71).
         *
         * Coding it needs their outcome categories. Note that several coded fields already cover parts of what
         * people write here — `patientStatusInitial`, `transportRefused`, `transportedTo`, `edDisposition` —
         * so the question for the county is as much "what does this field add" as "what are its values".
         * Tracked in COLLIER-CATEGORIES.md §6.
         */
    },
    { key: 'disposition', label: 'Disposition', placeholder: 'e.g. Admitted for evaluation' },
    {
        key: 'location',
        label: 'Location',
        placeholder: 'e.g. Residence, 3rd Ave N',
        // An overdose address is a household address. Encrypted, so it cannot be aggregated.
        sensitive: true,
    },
    /**
     * ── Added for agency ingestion (docs/AGENCY-INGESTION-PLAN.md §3.2) ─────────────────
     * Enumerated in one pass rather than a field at a time, and that is the whole point. `Mixed` is
     * banned, so there is no bag to grow into and every field costs a schema change — which means the
     * way to avoid a migration per agency is to write down the realistic set up front. These come
     * from the eight real CCSO columns, the ten banner metrics, and what each agency type reports.
     *
     * All coded fields take their vocabulary from a `lookup` category named after the field, the
     * convention the demographic fields already use. Two are numbers, and say so.
     */
    {
        key: 'patientStatusInitial',
        label: 'Initial patient status',
        placeholder: '',
        // CCSO writes this as prose ("Lying on the kitchen floor… unconscious with shallow breathing").
        // The narrative goes to the encrypted notes; this is the countable part of it.
        coded: true,
    },
    {
        key: 'intentional',
        label: 'Intentional',
        placeholder: '',
        /**
         * Yes / No / Unknown. CCSO writes "INTENTIONAL- took excessive pills" in a narrative column on
         * two of nine rows, and this field is deliberately NOT inferred from that text (plan §9 O-3,
         * accepted): a keyword rule that reads intent from prose is a guess that ends up in a published
         * statistic. It is filled by agencies that send it as a real column.
         */
        coded: true,
    },
    {
        key: 'narcanDoses',
        // Neutral (10/7): stored and shown exactly as the agency reports it — doses or kits, no conversion.
        label: 'Narcan units distributed (as reported)',
        placeholder: 'e.g. 2',
        /**
         * A count, where `narcanUnits` above is a category — CCSO fills that column with Single / Multi
         * / No. Both exist because two agencies will send each, and collapsing them would mean either
         * losing CCSO's answer or inventing a number it never gave.
         */
        numeric: true,
    },
    {
        key: 'narcanAdministrations',
        label: 'Narcan administrations (times given in this encounter)',
        placeholder: 'e.g. 2',
        /**
         * Confirmed with the county's tester (10/7): the number is how many times Narcan was given in this one
         * encounter. One event per encounter; this is its count of administrations. Summed, it is "administrations";
         * the events themselves are "encounters".
         */
        numeric: true,
    },
    {
        key: 'narcanLotNumber',
        label: 'Narcan lot number',
        placeholder: 'e.g. 231204A',
        /**
         * The manufacturer's batch code for the dose that was used or handed out.
         *
         * ── Why free text ─────────────────────────────────────────────────────────────────────
         * A lot number is whatever the manufacturer printed on the box. It is not a vocabulary anybody owns,
         * new values appear with every shipment, and coding it would mean an administrator adding a lookup
         * value before a nurse could record the kit in their hand.
         *
         * ── Why NOT encrypted ─────────────────────────────────────────────────────────────────
         * It is a fact about a manufactured product, not about a person. The model's line is already drawn
         * and stated: a demographic is encrypted, an overdose's disposition is not. Encrypting it would also
         * defeat the reason to have it — a recall names a lot, and answering "which of our kits came from
         * that batch" requires the field to be searchable.
         */
    },
    {
        key: 'narcanAdministeredBy',
        label: 'Narcan administered by',
        placeholder: '',
        // CCSO's rows say "reporter gave Narcan" and "Roommate gave Narcan" — bystander administration
        // is a distinct number the Collaborative will want, and it is invisible without this field.
        coded: true,
    },
    { key: 'transportedTo', label: 'Transported to', placeholder: 'e.g. NCH North' },
    {
        key: 'transportRefused',
        label: 'Transport refused',
        placeholder: '',
        // "signed out AMA" appears in CCSO's outcomes. Yes / No / Unknown.
        coded: true,
    },
    { key: 'edDisposition', label: 'ED disposition', placeholder: '', coded: true },
    { key: 'lengthOfStayDays', label: 'Length of stay (days)', placeholder: 'e.g. 3', numeric: true },
    { key: 'arrestCharge', label: 'Charge', placeholder: '', coded: true },
    { key: 'arrestDisposition', label: 'Arrest disposition', placeholder: '', coded: true },
    { key: 'courtProgramStatus', label: 'Court program status', placeholder: '', coded: true },
    {
        key: 'admissionType',
        label: 'Admission type',
        placeholder: '',
        // Voluntary / Marchman / Baker / court-ordered. Distinguishes an involuntary admission from a
        // voluntary one arriving on the same feed.
        coded: true,
    },
    { key: 'referralSource', label: 'Referral source', placeholder: '', coded: true },
    {
        key: 'matMedication',
        label: 'Medication',
        placeholder: '',
        /**
         * Methadone, buprenorphine, naltrexone — coded, because the whole reason METHADONE_MAT is a kind
         * rather than a custom field is that these are counted separately.
         *
         * Not `sensitive`: it is a fact about a treatment event at an agency, on the same side of the line
         * the model already draws — an overdose's disposition is in the clear, a demographic is not. A
         * medication name is only identifying if you already know whose record you are reading, which is
         * true of every field on an entry.
         */
        coded: true,
    },
    {
        key: 'referredTo',
        label: 'Referred to',
        placeholder: '',
        // Where they went next — the follow-up half of RFP R3's metric catalog.
        coded: true,
    },
    {
        key: 'sourceRowRef',
        label: "Agency's own row reference",
        placeholder: '',
        /**
         * Provenance, never a match key.
         *
         * CCSO's AR Number lands here. It increments ~950 in six days across the sample, so it is a CAD
         * call sequence rather than a person identifier (plan §1.2) — recording it makes "which row did
         * this come from" answerable without reopening the file, and nothing matches on it.
         */
        provenance: true,
    },
];
/**
 * Whether a participant has been established for this entry.
 *
 * `PENDING_MATCH` is the state that makes the design work: an ingested overdose whose person
 * the ladder would not identify is still a real overdose, so the entry is written and the
 * participant arrives later from the review queue. An entry that only existed once a person was
 * known would under-report the exact number the system exists to report.
 */
export const measureMatchStatus = z.enum(['MATCHED', 'PENDING_MATCH', 'UNMATCHED']);
/**
 * Where a recorded event came from.
 *
 * `GRID` is its own value rather than `MANUAL` (docs/GRID-ENTRY-PLAN.md D-5). A person typed it, so it is
 * manual in the ordinary sense — but a grid commits many rows in one act, and a later "undo that batch"
 * slice has nothing to withdraw unless the rows say they came from a grid. Cheap now, impossible to
 * backfill later: a row written as MANUAL can never be told apart from one somebody typed into a form.
 */
export const measureSource = z.enum(['MANUAL', 'INGESTED', 'GRID']);
/** Non-sensitive event attributes. Free text for now (§9 D-1). */
export const measureDetail = z.object({
    /** Coded against the `opioidType` lookup — the route, not the substance. */
    opioidType: z.string().optional(),
    confirmedStatus: z.string().optional(),
    /** Coded against `substanceType` — a proposed list. `substance` keeps the words. */
    substanceType: z.string().optional(),
    /** Coded against `overdoseOutcome` — a proposed list. `outcome` keeps the words. */
    overdoseOutcome: z.string().optional(),
    substance: z.string().optional(),
    outcome: z.string().optional(),
    narcanUnits: z.string().optional(),
    /** The manufacturer's batch code. Free text, not encrypted — it describes a product, not a person. */
    narcanLotNumber: z.string().optional(),
    disposition: z.string().optional(),
    // Added for agency ingestion (§3.2). Coded fields carry a lookup code; two are numbers.
    patientStatusInitial: z.string().optional(),
    intentional: z.string().optional(),
    narcanDoses: z.number().optional(),
    /** Times Narcan was given in this encounter (confirmed 10/7). */
    narcanAdministrations: z.number().optional(),
    narcanAdministeredBy: z.string().optional(),
    transportedTo: z.string().optional(),
    transportRefused: z.string().optional(),
    edDisposition: z.string().optional(),
    lengthOfStayDays: z.number().optional(),
    arrestCharge: z.string().optional(),
    arrestDisposition: z.string().optional(),
    courtProgramStatus: z.string().optional(),
    admissionType: z.string().optional(),
    referralSource: z.string().optional(),
    referredTo: z.string().optional(),
    /** METHADONE_MAT: which medication. Coded against `matMedication`. */
    matMedication: z.string().optional(),
    /** The agency's own row identifier. Provenance, never a match key. */
    sourceRowRef: z.string().optional(),
});
/** The coded detail fields and the lookup category each validates against. */
export const CODED_MEASURE_DETAIL_FIELDS = MEASURE_DETAIL_FIELD_DEFS.filter((f) => f.coded).map((f) => f.key);
/**
 * The detail fields sent as numbers. Derived from the definitions, never listed by hand: a hand-kept list
 * missed `narcanAdministrations` when it arrived (#186), and the grid sent it as text the API refused.
 */
export const NUMERIC_MEASURE_DETAIL_FIELDS = MEASURE_DETAIL_FIELD_DEFS.filter((f) => f.numeric).map((f) => f.key);
/** A kind's name in a grid header, where its full name and a field's would not fit (tester feedback 10/8). */
export const MEASURE_KIND_SHORT_LABELS = {
    FATAL_OVERDOSE: 'Fatal OD',
    NONFATAL_OVERDOSE: 'Non-fatal OD',
    NARCAN_ADMINISTERED: 'Narcan',
    NARCAN_DISTRIBUTED: 'Narcan dist.',
    MARCHMAN_ACT: 'Marchman',
    BAKER_ACT: 'Baker Act',
    ED_VISIT: 'ED visit',
    OPIOID_ARREST: 'Arrest',
    DRUG_COURT_PARTICIPATION: 'Drug court',
    METHADONE_MAT: 'MAT',
};
/** A detail field's name in a grid header, after its kind's: "Narcan: times given". The full label is its tooltip. */
export const MEASURE_DETAIL_SHORT_LABELS = {
    confirmedStatus: 'confirmed?',
    substanceType: 'substance',
    substance: 'substance text',
    opioidType: 'opioid use',
    narcanUnits: 'units',
    overdoseOutcome: 'outcome',
    outcome: 'outcome text',
    disposition: 'disposition',
    location: 'location',
    patientStatusInitial: 'first status',
    intentional: 'intentional',
    narcanDoses: 'units',
    narcanAdministrations: 'times given',
    narcanLotNumber: 'lot number',
    narcanAdministeredBy: 'given by',
    transportedTo: 'taken to',
    transportRefused: 'refused ride',
    edDisposition: 'disposition',
    lengthOfStayDays: 'stay (days)',
    arrestCharge: 'charge',
    arrestDisposition: 'disposition',
    courtProgramStatus: 'program status',
    admissionType: 'admission type',
    referralSource: 'referred by',
    matMedication: 'medication',
    referredTo: 'referred to',
    sourceRowRef: 'row reference',
};
export const measureEntrySummary = z.object({
    id: z.string(),
    entryCode: z.string(),
    /** `z.string()` not an enum: a tenth kind should render as itself, not blank the list. */
    kind: z.string(),
    /** Absent while an ingested entry is still waiting on a person, and for agency-level counts. */
    participantId: z.string().optional(),
    participantCode: z.string().optional(),
    matchStatus: measureMatchStatus,
    source: measureSource,
    occurredAt: z.string(),
    facilityId: z.string().optional(),
    facilityName: z.string().optional(),
    detail: measureDetail,
    status: z.enum(['ACTIVE', 'RETRACTED']),
    recordedAt: z.string(),
    createdAt: z.string(),
});
/**
 * The detail view adds the two encrypted fields.
 *
 * Kept off the summary for the same reason a participant's demographics are: a list is the
 * request most likely to be made casually, and a narrative note has no business in one.
 */
export const measureEntryDetail = measureEntrySummary.extend({
    location: z.string().optional(),
    notes: z.string().optional(),
    matchMethod: z.string().optional(),
    matchTier: z.number().optional(),
    matchConfidence: z.number().optional(),
    matchedBy: z.string().optional(),
    matchedAt: z.string().optional(),
    reportId: z.string().optional(),
    recordedBy: z.string(),
    retractedBy: z.string().optional(),
    retractedAt: z.string().optional(),
    retractionNote: z.string().optional(),
});
export const measureEntryListResponse = pageOf(measureEntrySummary);
/** Counts per kind, for the entries page's tiles. Loose record, same reasoning as reports. */
export const measureEntryCounts = z.object({
    kinds: z.record(z.string(), z.number()),
    total: z.number(),
    pendingMatch: z.number(),
});
/**
 * Family — a household, identified by a minted code and nothing else (CRS CASE-05).
 *
 * ── There is no name field, and that is the feature ──────────────────────────────────
 * A household is `FAM-000123`. Not "the Smiths". The schema has nowhere to put a surname, so
 * `strict: 'throw'` turns an attempt to send one into a 400 rather than a silently dropped
 * field — de-identification enforced by the shape of the record instead of by everyone
 * remembering.
 *
 * ── familyType is stored in the clear ────────────────────────────────────────────────
 * Unlike the participant demographics, and deliberately (docs/DATA-CAPTURE-PLAN.md §9 D-3): it
 * is a household composition category the Homeless Coalition already reports in aggregate from
 * HMIS, not a clinical or financial fact about a person. Encrypting it would make the one thing
 * this collection exists to report unaggregatable, for no privacy gained — the surname's absence
 * is what de-identifies the household.
 */
export const familySummary = z.object({
    id: z.string(),
    familyCode: z.string(),
    /** Free text for now (§9 D-1). The HMIS vocabulary is in FAMILY_TYPE_SUGGESTIONS below. */
    familyType: z.string().optional(),
    status: z.enum(['ACTIVE', 'INACTIVE']),
    note: z.string().optional(),
    /** Active memberships. Present on both DTOs — it is the number that makes a list readable. */
    memberCount: z.number(),
    createdBy: z.string(),
    createdAt: z.string(),
});
export const familyMember = z.object({
    id: z.string(),
    familyId: z.string(),
    participantId: z.string(),
    /** Denormalized for display, so a member list does not need a query per row. */
    participantCode: z.string().optional(),
    /** Free text: 'Mother', 'Grandparent, primary carer'. Not a fixed vocabulary yet (§9 D-1). */
    role: z.string().optional(),
    /** REMOVED, never deleted — who was in a household when is the history worth keeping. */
    status: z.enum(['ACTIVE', 'REMOVED']),
    assignedAt: z.string(),
    assignedBy: z.string(),
    removedAt: z.string().optional(),
    removedBy: z.string().optional(),
    removalNote: z.string().optional(),
});
export const familyDetail = familySummary.extend({
    members: z.array(familyMember),
});
export const familyListResponse = pageOf(familySummary);
/**
 * The ten household compositions HMIS already uses, from the Collaborative's data-tracking
 * sheet.
 *
 * Offered as suggestions rather than enforced as an enum, because the field is free text for now
 * (§9 D-1) and an org may need a category nobody anticipated. When the coding phase lands, this
 * list is what it starts from — which is the whole reason it is written down here rather than
 * left in a spreadsheet.
 */
export const FAMILY_TYPE_SUGGESTIONS = [
    'Two Parent Family',
    'Couple With No Children',
    'Grandparent(s) With Child',
    'Non-Custodial Caregiver',
    'Female Single Parent',
    'Female Single',
    'Male Single Parent',
    'Male Single',
    'Foster Parent',
    'Other',
];
/**
 * Lookup — one permitted value in a configurable vocabulary.
 *
 * `code` is what a record stores and never changes; `label` is what a form shows and may be
 * rewritten freely. That split is what makes relabelling safe — a wording change never rewrites
 * stored data or invalidates a report.
 */
export const lookupValue = z.object({
    id: z.string(),
    category: z.string(),
    code: z.string(),
    label: z.string(),
    sortOrder: z.number(),
    isActive: z.boolean(),
    /** The customer has not settled this value yet — selectable, and marked in the UI. */
    provisional: z.boolean().optional(),
    note: z.string().optional(),
    /**
     * Directionality, used only by the `relationshipType` category.
     *
     * Two fields that mean nothing for the other fourteen categories, which is a real impurity in a
     * generic vocabulary row and was accepted with its eyes open (PARTICIPANT-RELATIONSHIPS-PLAN.md
     * §6 D-2). The alternative was a second vocabulary collection with its own seeding path and its
     * own admin surface for the same idea; one slightly impure collection beat two mechanisms.
     *
     * They are here rather than in code because adding STEP_PARENT_OF / STEP_CHILD_OF must not be a
     * release — the same reason every other vocabulary is data.
     */
    symmetric: z.boolean().optional(),
    /** The code naming the other direction. Absent for a symmetric type, required otherwise. */
    inverseCode: z.string().optional(),
});
export const lookupListResponse = z.object({ items: z.array(lookupValue) });
/**
 * What a vocabulary's values are used by (docs/RECORD-MANAGEMENT-PLAN.md §5).
 *
 * `source` is the part that matters, because the answers are not equally trustworthy:
 * QUERY is counted from the records now, ROLLUP comes from counters that have never been backfilled,
 * and ON_DEMAND means the values are encrypted and counting them is a separate, audited action — so
 * an empty list there means "not counted", never "nothing uses this".
 *
 * The two that are about a field an organisation defined for itself
 * (docs/CUSTOM-FIELDS-PLAN.md §3, §8):
 *
 *   - NOT_STORED — nothing can record against this list at all, because no field uses it. Distinguished
 *     from QUERY-with-no-rows for the same reason ON_DEMAND is: "we looked and found none" and "there is
 *     nowhere to look" are different sentences, and only one means the list is unused.
 *   - ENCRYPTED — the field is marked sensitive, so its values are ciphertext and no aggregation can
 *     touch them. This is the trade the definition recorded, and it is a dead end by design rather than
 *     a count waiting to be asked for: unlike ON_DEMAND, there is no action that produces the number.
 */
export const categoryUsage = z.object({
    category: z.string(),
    source: z.enum(['QUERY', 'ROLLUP', 'ON_DEMAND', 'COUNTED', 'NOT_STORED', 'ENCRYPTED']),
    caveat: z.string().optional(),
    counts: z.array(z.object({ code: z.string(), count: z.number() })),
});
/**
 * Whether the numbers in a `CategoryUsage` are counts at all.
 *
 * One function, because three places on the values screen have to agree: the per-value count cell, the
 * offer of an on-demand count, and the retirement confirmation that says how many records hold a value.
 * A screen that treated an uncounted answer as zero would print "0 records" beside a value hundreds of
 * people hold, which is the one wrong answer worse than no answer.
 */
export function usageIsCounted(source) {
    return source === 'QUERY' || source === 'ROLLUP' || source === 'COUNTED';
}
/**
 * Resolves a stored code to its label, falling back to the code itself.
 *
 * The fallback is not defensive dressing: a participant may hold a code that has since been
 * retired, or one from before a vocabulary changed. Rendering the raw code makes that visible,
 * where rendering blank would silently lose the fact that something was recorded.
 */
export function lookupLabel(values, code) {
    if (code === undefined)
        return undefined;
    return values.find((v) => v.code === code)?.label ?? code;
}
/** True when a stored code is not in the current vocabulary — worth marking in the UI. */
export function isUnrecognisedCode(values, code) {
    if (code === undefined || values.length === 0)
        return false;
    return !values.some((v) => v.code === code);
}
/**
 * A labeled relationship between two participants — the family relationship graph (CRS CASE-05,
 * brought consciously into scope; docs/PARTICIPANT-RELATIONSHIPS-PLAN.md).
 *
 * ── Distinct from `family` ───────────────────────────────────────────────────────────
 * A household says who lives together; this says how two people are related, and the two disagree
 * constantly. A father who does not live with his children is one row here and no shared household
 * at all, which is exactly why the grouping could not be stretched to cover it.
 *
 * ── One row, and the DTO is written from the viewer's side ───────────────────────────
 * The stored row means "`from` IS `relationshipType` OF `to`", and only one row exists per
 * relationship — two would let the halves disagree, and removing one and not the other would leave
 * the graph asserting something nobody asserted.
 *
 * That makes reading two-sided, so this DTO is deliberately NOT the row. It is the row as seen
 * from one participant: `relatedParticipantId` is always the *other* person, and `role` is always
 * what that person is to the participant whose panel is open. The inversion that produces `role`
 * happens in exactly one tested function on the server (roleOfOther) and nowhere else, because a
 * relationship rendered from the wrong side is a bug that reads as fact.
 */
export const participantRelationship = z.object({
    id: z.string(),
    /** The participant whose panel this was read from. */
    participantId: z.string(),
    /** The other person. Never the same as `participantId` — a self-link is refused. */
    relatedParticipantId: z.string(),
    /** Denormalized for display, so a list does not need a query per row. */
    relatedParticipantCode: z.string().optional(),
    /**
     * What the related participant is TO this participant, as a code.
     *
     * Already inverted where inversion applies: a stored `A PARENT_OF B` row reads CHILD_OF on A's
     * panel and PARENT_OF on B's. Resolve it for display through the `relationshipType` vocabulary.
     */
    role: z.string(),
    /** The code as stored, before inversion — what an audit trail or a support question needs. */
    storedType: z.string(),
    /** True when this participant is the `from` side of the stored row. */
    isSubject: z.boolean(),
    /** REMOVED, never deleted — who stated a relationship and who withdrew it is the history. */
    status: z.enum(['ACTIVE', 'REMOVED']),
    note: z.string().optional(),
    linkedAt: z.string(),
    linkedBy: z.string(),
    removedAt: z.string().optional(),
    removedBy: z.string().optional(),
    removalNote: z.string().optional(),
});
export const participantRelationshipListResponse = z.object({
    items: z.array(participantRelationship),
});
/**
 * Which side of the relationship the participant being edited is on.
 *
 * Needed because a type alone is ambiguous when submitted from one participant's page: PARENT_OF
 * does not say whether this participant is the parent or the child. The UI phrases it as a sentence
 * and sends the answer rather than hoping a convention is remembered. Symmetric types ignore it.
 */
export const relationshipDirection = z.enum(['FROM_THIS', 'TO_THIS']);
/**
 * What counts as an acceptable password.
 *
 * In shared rather than in one module's schema file because two modules need it — `user` validates
 * an admin setting somebody's password, `auth` validates a user changing their own — and the two
 * must not disagree about the minimum. It used to live in `user/v1/schema.ts`, which meant `auth`
 * either duplicated the rule or imported across the module boundary; the second is what created the
 * import cycle that broke every script (see modules/moduleGraph.test.ts).
 *
 * Minimum length only, and a generous cap. Length is the property that actually correlates with
 * strength; composition rules push people toward predictable substitutions and a sticky note. The
 * cap exists so a huge input cannot turn argon2 into a denial-of-service.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 200;
export const passwordRule = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `must be at least ${PASSWORD_MIN_LENGTH} characters`)
    .max(PASSWORD_MAX_LENGTH);
/* ────────────────────────────────────────────────────────────────────────────────────
 * AGENCY INGESTION — the field catalog and the per-agency column map
 * docs/AGENCY-INGESTION-PLAN.md §4
 * ──────────────────────────────────────────────────────────────────────────────────── */
/**
 * One thing an incoming column can be mapped to.
 *
 * The catalog is what makes "map this column to an existing field" possible: the mapping UI cannot
 * offer a target it has no list of, and the apply step cannot write a target it does not recognise.
 *
 * ── Derived, not hand-listed ─────────────────────────────────────────────────────────
 * The detail entries come from MEASURE_DETAIL_FIELD_DEFS and the demographic entries from
 * CIRCUMSTANCE_DIMENSIONS, so a field added to either appears in the mapping UI without a second
 * edit. Hand-listing them would have been shorter to write and would have drifted the first time
 * somebody added a field — which is the failure this codebase has now had twice.
 *
 * ── Where "create a brand-new field" plugs in ────────────────────────────────────────
 * Here, and only here. A user-defined field becomes a catalog entry with `domain: 'CUSTOM'` and a
 * different backing store; verify, map and apply all read the catalog and would not change. `Mixed`
 * stays banned — a custom field would be a row in its own collection, not a bag on the event.
 */
/**
 * `REVIEW` is a domain with no storage behind it.
 *
 * A person file's name column has to be mappable — a reviewer deciding between two candidates for
 * the same date of birth needs to see who the file was talking about — and it must never reach a
 * participant. This system holds no names: not on `participant`, not on `participant_identity`, and
 * that absence IS the de-identification (PARTICIPANT-IMPORT-PLAN §2.1, P-1a).
 *
 * So the domain says where a value may go rather than what it is. A REVIEW target is read from the
 * staged row for display during the import and written nowhere, which is enforced in one place: the
 * person evaluator never puts it in the patch it returns.
 */
/**
 * 'CASE' joins them for the enrollment episode a roster can open
 * (docs/PERSON-IMPORT-ENROLLMENT-PLAN.md §2) — a period somebody was in a programme, which is
 * neither an event nor an attribute of the person.
 */
/**
 * 'CUSTOM' is a domain with a different backing store rather than a different meaning
 * (docs/CUSTOM-FIELDS-PLAN.md §5, §8).
 *
 * A field an organisation defined for itself is mapped, verified and validated exactly like a
 * built-in one — the whole point of resolving the catalog per organisation was that the pure
 * functions would not need to know the difference. Where it differs is at the write: the value goes
 * to `custom_field_value` keyed on `customFieldId`, not onto the participant or the entry, so the
 * domain has to be distinguishable at exactly that one step.
 *
 * `customScope` is what says WHICH record it lands on, and it is on the entry rather than derived
 * from the domain because both scopes may be bound on either shape. That is §5's point: an incident
 * row already resolves to a participant, so the prohibition was always about the map rather than
 * about the data.
 */
export const FIELD_DOMAINS = ['MEASURE', 'PARTICIPANT', 'CASE', 'REVIEW', 'CUSTOM'];
export const FIELD_VALUE_TYPES = ['TEXT', 'DATE', 'NUMBER', 'CODED'];
/**
 * Measure targets belong to an incident file only.
 *
 * They were BOTH while a person file had nowhere else to put anything. Under P-6 a person row does
 * not produce a measure entry in this build, so binding a roster's admission date to
 * `measure.occurredAt` would verify as MAPPED and produce nothing — the silent drop this catalog
 * has now been bitten by twice. A PERSON map is refused such a target outright, with the reason,
 * which is the same principle as marking a column Ignore explicitly.
 *
 * When a file that is genuinely both is worth supporting, it is two maps against one upload; the
 * version model already allows that.
 */
const INCIDENT_ONLY = ['INCIDENT'];
const PERSON_ONLY = ['PERSON'];
/**
 * A target either shape may bind (docs/CUSTOM-FIELDS-PLAN.md §5, D-6 — slice 8).
 *
 * ── What this widening actually says ──────────────────────────────────────────────────
 * That a target belongs to the PERSON a row is about, and that either kind of file may be about a person.
 * An incident row already resolves to one — that is what the match ladder does, and `matchStatus` records
 * the outcome — so the prohibition that kept these off incident maps was about the map, not about the data.
 *
 * ── What it does NOT say ──────────────────────────────────────────────────────────────
 * That an incident row may CREATE a person. It may not, and nothing here changes that: `resolveMeasureEntry`
 * either matches an existing participant or opens a match review, and there is no path from an incident row
 * to a registration (P-6). So a date of birth on an incident file is a match key and never a new record,
 * which is the difference between enriching somebody and inventing them.
 *
 * The honesty lives at the write, not here: a value destined for a participant is recorded when the row
 * resolved to one, filling only what is empty, and the row says so when it did not. And the map validator
 * refuses an incident map that binds one of these with no column that could identify anybody — because a
 * demographic on a file that can match nobody is an afternoon's mapping that records nothing.
 */
const EITHER_SHAPE = ['INCIDENT', 'PERSON'];
/**
 * The participant's own fields a person file may carry, beside the match keys.
 *
 * One list, so the catalog, the import's write path and any later reporting cannot drift from each
 * other. Every coded one is validated against its vocabulary at the write by the same
 * `assertCodesValid` the manual register form goes through — an unrecognised value from an agency
 * is a mapping bug worth surfacing, not data to store.
 *
 * Sourced from COLLIER-CATEGORIES.md §1, which is the customer's own list.
 */
export const PARTICIPANT_DEMOGRAPHIC_FIELDS = [
    { key: 'race', label: 'Race', coded: true },
    { key: 'ethnicity', label: 'Ethnicity', coded: true },
    { key: 'employmentStatus', label: 'Employment status', coded: true },
    { key: 'housingStatus', label: 'Housing status', coded: true },
    { key: 'incomeLevel', label: 'Income level', coded: true },
    { key: 'veteranStatus', label: 'Veteran status', coded: true },
    { key: 'insuranceCoverage', label: 'Insurance coverage', coded: true },
    { key: 'disabilityStatus', label: 'Disability status', coded: true },
    { key: 'dvSurvivor', label: 'Domestic violence survivor', coded: true },
    /**
     * Recidivist, and when they were marked one (CRS R68's input, captured ahead of the metric).
     *
     * One flag and one date per participant, not a list — the same shape as every other dimension here: a single
     * current value with no history. A participant who is marked, unmarked and marked again keeps the latest
     * answer and the latest date, and the audit trail is what says it changed.
     *
     * ── Why the flag is coded and not a boolean ───────────────────────────────────────────
     * Because "not known" is a real answer and a boolean cannot hold it. Yes / No / Unknown through the `lookup`
     * vocabulary, like `dvSurvivor` and `coOccurringDx` — so the customer owns the values and can reword them
     * without a release.
     *
     * ── Why recidivism itself is still not computed ───────────────────────────────────────
     * This is somebody's assertion that a person is a recidivist, recorded because agencies have it and it was
     * asked for. The RATE remains uncomputed and undefined: AGENCY-INGESTION-PLAN §9 O-4 flags it as a
     * percentage with no agreed numerator or denominator, and a flag on a participant is not that answer.
     */
    { key: 'recidivist', label: 'Recidivist', coded: true },
    {
        key: 'recidivistDate',
        label: 'Date marked recidivist',
        coded: false,
        /**
         * Encrypted like the flag it belongs to.
         *
         * A date beside a sensitive flag is as identifying as the flag: "marked on 14 March" narrows a cohort of
         * one in a small county, and storing it in the clear beside ciphertext would make the encryption of the
         * flag decorative.
         */
        encrypted: true,
        valueType: 'DATE',
    },
    { key: 'mhDiagnosis', label: 'Mental health diagnosis', coded: true },
    { key: 'sudDiagnosis', label: 'Substance use diagnosis', coded: true },
    /**
     * Retired (tester feedback 10/7): hidden from screens, forms, the mapper and dashboards. Existing values are
     * kept — the app cannot delete, and the audit trail has every one — but nothing offers or shows it now.
     * Mental-health and substance-use diagnosis remain, and together say what this one did.
     */
    { key: 'coOccurringDx', label: 'Co-occurring diagnosis', coded: true, retired: true },
    /**
     * The household this person belongs to, by composition.
     *
     * ── Why it reuses the FAMILY record's vocabulary ──────────────────────────────────────
     * Because `familyType` already exists and already holds exactly these values — two-parent family,
     * couple with no children, grandparent with child, single parent, foster parent. The catalog derives
     * a coded demographic's category from its key, so naming this field `familyType` points it at that
     * one list rather than at a parallel one somebody would have to keep in step.
     *
     * ── Why it is on the participant as well as on the family ─────────────────────────────
     * A `family` record is a household this system has grouped, with members. An agency's export says
     * "this person is in a two-parent family" without naming anybody else in it, and that is a fact
     * about the person on the row — the only thing the file actually asserts. Requiring a household
     * record first would mean inventing the other members, which is how a de-identified system grows
     * people nobody reported.
     */
    { key: 'familyType', label: 'Family by type', coded: true },
    /**
     * The age band, for a file that gives a band and no date of birth.
     *
     * Never used when a date is present: the band is derived from the date so the two cannot
     * disagree on screen. Recorded when there is no date, which is the honest record of what the
     * agency actually knows.
     */
    {
        key: 'ageBandRecorded',
        label: 'Age band (when no date of birth)',
        coded: true,
        category: 'ageBand',
    },
    /**
     * The full postal code, encrypted at the service boundary. `zip3` is derived from it there and is
     * deliberately not a target of its own.
     */
    { key: 'postalCode', label: 'ZIP / postal code', coded: false, encrypted: true },
];
/** The demographic keys, for anything that needs to iterate them. */
export const PARTICIPANT_DEMOGRAPHIC_KEYS = PARTICIPANT_DEMOGRAPHIC_FIELDS.map((f) => f.key);
export const FIELD_CATALOG = [
    // ── The event ─────────────────────────────────────────────────────────────────────
    {
        key: 'measure.occurredAt',
        label: 'Date / time of event',
        domain: 'MEASURE',
        valueType: 'DATE',
        // The one genuinely required target: an event with no date cannot be counted in any period,
        // which is what every banner metric is.
        required: true,
        shapes: INCIDENT_ONLY,
    },
    {
        key: 'measure.location',
        label: 'Location / address',
        domain: 'MEASURE',
        valueType: 'TEXT',
        /**
         * Encrypted, and never a match key.
         *
         * The CCSO sample has one address appearing as both a survivor and a fatality, so address
         * matching would merge two people (plan §1.3). Nothing in MatchInput takes it.
         */
        encrypted: true,
        shapes: INCIDENT_ONLY,
    },
    {
        key: 'measure.notes',
        label: 'Narrative / notes',
        domain: 'MEASURE',
        valueType: 'TEXT',
        // Where CCSO's "Initial patient status" prose belongs: free narrative about a person's medical
        // state at an address. Encrypted, and deliberately not mined for coded values (plan §9 O-3).
        encrypted: true,
        shapes: INCIDENT_ONLY,
    },
    // ── The event's detail, derived from the field definitions ─────────────────────────
    ...MEASURE_DETAIL_FIELD_DEFS.filter((f) => f.key !== 'location').map((f) => ({
        key: `measure.detail.${f.key}`,
        label: f.label,
        domain: 'MEASURE',
        valueType: f.numeric ? 'NUMBER' : f.coded ? 'CODED' : 'TEXT',
        ...(f.coded ? { lookupCategory: f.key } : {}),
        shapes: INCIDENT_ONLY,
    })),
    // ── The person ────────────────────────────────────────────────────────────────────
    {
        key: 'participant.participantCode',
        label: 'Our participant ID',
        domain: 'PARTICIPANT',
        valueType: 'TEXT',
        // Tier 0 of the ladder. Present when an agency has been given our codes and echoes them back.
        matchKey: true,
        shapes: EITHER_SHAPE,
    },
    {
        key: 'participant.externalId',
        /**
         * Named for what the column is actually called on a provider's export.
         *
         * "Agency's own person ID" was accurate and unrecognisable: a treatment provider is looking at a
         * column headed Client ID or Chart Number, and the person mapping it should not have to work out
         * that those are the same thing. The parenthetical keeps the meaning for whoever needs it.
         */
        label: "Client / Chart ID (agency's own person ID)",
        domain: 'PARTICIPANT',
        valueType: 'TEXT',
        /**
         * Tier 1, scoped to the sending agency.
         *
         * Distinct from `measure.detail.sourceRowRef`, and the distinction matters: this identifies a
         * PERSON across that agency's files, where sourceRowRef identifies a ROW. CCSO's AR Number is
         * the second (plan §1.2), and mapping it here would link unrelated overdoses into one person.
         */
        matchKey: true,
        shapes: EITHER_SHAPE,
    },
    {
        key: 'participant.dateOfBirth',
        label: 'Date of birth',
        domain: 'PARTICIPANT',
        valueType: 'DATE',
        encrypted: true,
        // Tiers 2 and 3, and exact-only: a partial or placeholder date never becomes a match key.
        matchKey: true,
        shapes: EITHER_SHAPE,
    },
    {
        key: 'participant.sex',
        label: 'Sex',
        domain: 'PARTICIPANT',
        valueType: 'CODED',
        lookupCategory: 'sex',
        // Tier 3 only, as a disambiguator between candidates a date of birth already narrowed — so it
        // feeds the ladder but cannot start it. See `disambiguatorOnly`.
        matchKey: true,
        disambiguatorOnly: true,
        shapes: EITHER_SHAPE,
    },
    {
        key: 'review.displayName',
        label: 'Name (shown while reviewing, never stored)',
        domain: 'REVIEW',
        valueType: 'TEXT',
        /**
         * Encrypted in the staging row, and nowhere else — there is no participant field for it to be
         * encrypted INTO. Marked so the sample rule refuses to list it: a column of names is the one
         * thing on a client list nobody needs to browse to map it (P-5).
         */
        encrypted: true,
        shapes: PERSON_ONLY,
    },
    /**
     * ── The enrollment episode ────────────────────────────────────────────────────────
     *
     * Treatment providers and the courts both send rosters whose rows carry an enrollment date, and
     * time-in-program and enrollment-period reporting cannot be answered without it
     * (docs/PERSON-IMPORT-ENROLLMENT-PLAN.md). Mapping the enrollment date is what makes an import
     * open an episode at all: a file without it registers and matches people exactly as it does today.
     *
     * None of these is a match key. When somebody enrolled says nothing about who they are.
     */
    {
        key: 'case.openedAt',
        label: 'Enrollment / episode start date',
        domain: 'CASE',
        valueType: 'DATE',
        shapes: PERSON_ONLY,
    },
    {
        key: 'case.closedAt',
        label: 'Discharge / episode end date',
        domain: 'CASE',
        valueType: 'DATE',
        shapes: PERSON_ONLY,
    },
    {
        key: 'case.episodeType',
        label: 'Programme / episode type',
        domain: 'CASE',
        valueType: 'TEXT',
        shapes: PERSON_ONLY,
    },
    {
        /**
         * Who referred them INTO the programme.
         *
         * Deliberately not `measure.detail.referralSource`, which is an event's referral. Same word,
         * different record — and mapping a roster's referral column to the event one would attach it to
         * nothing, because a person file produces no events (P-6).
         */
        key: 'case.referralSource',
        label: 'Referred by (into the programme)',
        domain: 'CASE',
        valueType: 'TEXT',
        shapes: PERSON_ONLY,
    },
    // ── The person's demographics ─────────────────────────────────────────────────────
    //
    // These were removed once, because mapping one silently produced nothing and the obvious fix —
    // just write it — is last-writer-wins across twenty agencies with no provenance. They are back
    // because that policy now exists and is enforced at the write, not here (P-3):
    //
    //   register  → write them; a participant this import creates has no prior value to overwrite
    //   link      → fill only what is EMPTY; never overwrite what another agency already said
    //   either    → the reviewer sees both outcomes per field, and the audit records which file
    //
    // The catalog's job is only to say a column MAY mean this. Where the value is allowed to land is
    // the evaluator's and the apply step's, and both are written to that policy.
    //
    // Bindable on either shape since D-6 (slice 8), which is the tester's "demographics on an incident file"
    // ask. An overdose report that carries a date of birth and a race is a common shape, and refusing the
    // second while accepting the first was a rule about the map rather than about the data. Same write policy,
    // and now on both paths: written on a registration, filled-into-empties on a match, reported on the row
    // when the row resolved to nobody.
    ...PARTICIPANT_DEMOGRAPHIC_FIELDS.filter((f) => !f.retired).map((f) => ({
        key: `participant.${f.key}`,
        label: f.label,
        domain: 'PARTICIPANT',
        valueType: f.valueType ?? (f.coded ? 'CODED' : 'TEXT'),
        ...(f.coded ? { lookupCategory: f.category ?? f.key } : {}),
        ...(f.encrypted ? { encrypted: true } : {}),
        shapes: EITHER_SHAPE,
    })),
    /**
     * ── What is still deliberately NOT offered ───────────────────────────────────────────
     * `zip3` — derived from the postal code server-side. A derived field a caller can supply is one
     * that can disagree with the value it was supposed to come from.
     *
     * Anything that would make a person row produce a measure entry. A treatment provider's roster
     * may carry an admission date, and mapping it to `measure.occurredAt` would make one upload two
     * imports (P-6, deferred). One shape at a time; the version model already allows two maps against
     * one file when that changes.
     */
    /**
     * ── The history of the entries above, kept because the reasoning still matters ───────
     * The demographics were here once, and mapping one silently produced nothing: the apply step
     * extracted only the four match keys, so a column somebody mapped to Housing status verified as
     * MAPPED and went nowhere. They were removed rather than made to work, because the obvious fix
     * was the wrong one — at the time a person feed could not register anybody, so an ingested
     * demographic could only overwrite a participant the ladder had already matched, and
     * COLLIER-CATEGORIES.md §5.2 records these as a single CURRENT value with no per-field
     * provenance. Writing them would have been last-writer-wins across twenty agencies, with the
     * audit row reading "updated by system".
     *
     * What changed is not the risk — it is that somebody decided. A person import now registers and
     * links under a human decision, and the write policy is fill-empties-only on a link. The entries
     * came back with that policy, not before it, which was the whole point of removing them.
     */
];
/** Lookup by key, for the apply step and for validating a saved map. */
export function fieldCatalogEntry(key) {
    return FIELD_CATALOG.find((e) => e.key === key);
}
/** The vocabularies a catalog carries, if it carries any. */
export function catalogVocabularies(catalog) {
    return catalog.vocabularies;
}
/**
 * Whether binding this column gives a map a way to FIND somebody, rather than merely to narrow a
 * shortlist.
 *
 * The three that can: our participant code (tier 0), the agency's own id (tier 1), and a date of birth
 * (tiers 2 and 3, as candidates). Sex cannot, and a custom field never can — whom a row is about is
 * decided by known keys, and letting an administrator nominate another would change how people are
 * found rather than what is recorded about them.
 *
 * Asked by the map validator before a template goes live, and by the import before it applies. Both
 * ask this one function so the two cannot drift into disagreeing about what an identifier is.
 */
export function canStartAMatch(entry) {
    return entry.matchKey === true && entry.disambiguatorOnly !== true;
}
/** One entry, from a resolved catalog. The same lookup as `fieldCatalogEntry`, against a list. */
export function catalogEntry(catalog, key) {
    return catalog.find((e) => e.key === key);
}
/** The targets a map of this shape may bind, for the mapping UI's select. */
export function catalogForShape(shape) {
    // A BOTH agency may map anything; its individual maps are still INCIDENT or PERSON.
    if (shape === 'BOTH')
        return [...FIELD_CATALOG];
    return FIELD_CATALOG.filter((e) => e.shapes.includes(shape));
}
/** The same narrowing, over a catalog that may carry an organisation's own fields. */
export function narrowToShape(catalog, shape) {
    if (shape === 'BOTH')
        return [...catalog];
    return catalog.filter((e) => e.shapes.includes(shape));
}
/** The sentinel meaning "this column is deliberately not used" (plan §9 O-5). */
export const IGNORE_TARGET = 'IGNORE';
/**
 * The sentinel meaning "there is no field for this column yet"
 * (docs/CUSTOM-FIELDS-PLAN.md §1).
 *
 * ── Why this is not IGNORE ────────────────────────────────────────────────────────────
 * They differ in what they say about the data, which is the only thing that matters later. `IGNORE`
 * is *we do not keep this* — a decision that is finished. `UNMATCHED` is *this matters and has
 * nowhere to go yet*, which is a decision that is waiting on somebody to define a field for it.
 *
 * Both let the import proceed. Only this one is counted, shown by name on the import, and queued for
 * an administrator — so that "which columns are agencies sending that we cannot store" is a question
 * with an answer rather than a thing people remember.
 *
 * Its values are deliberately NOT stored (D-8). A value kept against a field that does not exist is
 * PHI with no type, no sensitivity decision and no retention rule; the file itself is still in object
 * storage, and re-applying against a better map is the supported path.
 */
export const UNMATCHED_TARGET = 'UNMATCHED';
/* ── Fields an organisation defined for itself ───────────────────────────────────── */
/**
 * What a custom field describes (docs/CUSTOM-FIELDS-PLAN.md §5).
 *
 * The tester's ask, restated: a file that is incident-shaped should still be able to carry facts
 * about a person. So a field says which of the two it is, and the write path follows from that rather
 * than from the shape of the file it arrived in — a PERSON field is written to the participant only
 * when the row resolves to one, under the fill-empties-only rule imports already follow for
 * demographics; an EVENT field is written to the measure entry.
 */
export const CUSTOM_FIELD_SCOPES = ['PERSON', 'EVENT'];
export const customFieldScope = z.enum(CUSTOM_FIELD_SCOPES);
/**
 * The shape of a key somebody types in.
 *
 * Lower camel case, letters and digits, no dots — because the mappable target this becomes is
 * `custom.<key>` (slice 5), and reserving the dot here is what guarantees a field an administrator
 * invents can never collide with a built-in target like `participant.race`. Nothing in the built-in
 * catalog begins `custom.`, and nothing here can produce a second dot.
 */
export const CUSTOM_FIELD_KEY_SHAPE = /^[a-z][a-zA-Z0-9]*$/;
/**
 * The lookup category a coded custom field's values live in.
 *
 * Namespaced rather than bare, for the reason `LOOKUP_CATEGORIES` is a closed list: a category name
 * this build does not know about is normally a typo, and the one legitimate exception has to be
 * recognisable by shape. `custom:dischargeDisposition` is managed in Record Management exactly like
 * `race` is — the same screen, the same four operations, the same `assertValidCode` on write.
 */
/**
 * The mappable target a custom field becomes: `custom.<key>` (docs/CUSTOM-FIELDS-PLAN.md §6.1).
 *
 * A dot, where the vocabulary namespace uses a colon, because this sits in the same space as
 * `participant.race` and `measure.detail.opioidType` — a target key, read by the same functions, and it
 * should look like one. Nothing in the built-in catalog begins `custom.`, and `CUSTOM_FIELD_KEY_SHAPE`
 * admits no second dot, so a field an administrator invents can never collide with a built-in target.
 */
export const CUSTOM_TARGET_PREFIX = 'custom.';
export function customFieldTargetKey(key) {
    return `${CUSTOM_TARGET_PREFIX}${key}`;
}
/** True for a column-map target that names a field an organisation defined. Shape-checked. */
export function isCustomTarget(target) {
    return (target.startsWith(CUSTOM_TARGET_PREFIX) &&
        CUSTOM_FIELD_KEY_SHAPE.test(target.slice(CUSTOM_TARGET_PREFIX.length)));
}
/** The field key back out of a target, for a message that should name the field rather than the target. */
export function customFieldKeyFromTarget(target) {
    return isCustomTarget(target) ? target.slice(CUSTOM_TARGET_PREFIX.length) : undefined;
}
export const CUSTOM_CATEGORY_PREFIX = 'custom:';
export function customCategoryFor(key) {
    return `${CUSTOM_CATEGORY_PREFIX}${key}`;
}
/** True for a vocabulary belonging to a field an organisation defined. Shape-checked, not trusted. */
export function isCustomCategory(value) {
    return (value.startsWith(CUSTOM_CATEGORY_PREFIX) &&
        CUSTOM_FIELD_KEY_SHAPE.test(value.slice(CUSTOM_CATEGORY_PREFIX.length)));
}
/** The field key back out of its category, for a screen that has one and needs the other. */
export function customFieldKeyOf(category) {
    return isCustomCategory(category) ? category.slice(CUSTOM_CATEGORY_PREFIX.length) : undefined;
}
/**
 * One field an organisation defined for itself.
 *
 * `valueType`, `scope` and `sensitive` are absent from the edit surface on purpose — see the service,
 * which refuses to change them and says why in a sentence rather than a code.
 */
export const customFieldDefinition = z.object({
    id: z.string(),
    /** Stable and permanent. Every stored value and every column map points at it. */
    key: z.string(),
    label: z.string(),
    description: z.string().optional(),
    valueType: z.enum(FIELD_VALUE_TYPES),
    scope: customFieldScope,
    /**
     * Encrypted at rest, and therefore never filterable, groupable or countable (§3).
     *
     * The trade, not a safety setting: a sensitive field is stored and displayed like any other and
     * disappears from every report and query panel, because randomized encryption means the database
     * cannot match, group or sort what it holds. The form that sets this has to say so in those words.
     */
    sensitive: z.boolean(),
    /** CODED only — `custom:<key>`, managed in Record Management like any other vocabulary. */
    lookupCategory: z.string().optional(),
    /** NUMBER only. */
    min: z.number().optional(),
    max: z.number().optional(),
    integerOnly: z.boolean().optional(),
    sortOrder: z.number(),
    /** Retired rather than deleted: it stops being offered and its stored values stay readable. */
    isActive: z.boolean(),
    createdAt: z.string(),
});
export const customFieldListResponse = z.object({ items: z.array(customFieldDefinition) });
/**
 * Whether this field can ever appear in a report or a query panel.
 *
 * One function, so the form that warns about it, the field list that labels it and the reporting
 * that excludes it (slice 7) cannot disagree. Two independent reasons a field is not reportable, and
 * the caller usually wants to say which:
 *
 *   - sensitive, so the stored value is ciphertext that no `$match` or `$group` can touch;
 *   - free text, which is not reportable in any configuration — a thousand distinct answers
 *     grouped by value is a thousand groups of one.
 */
export function isCustomFieldReportable(field) {
    return !field.sensitive && field.valueType !== 'TEXT';
}
/**
 * Why not, in the words the screen should use. `undefined` when it is reportable.
 *
 * Sensitivity is named first when both apply: it is the one the administrator just chose, and the one
 * they can still change their mind about before the field exists.
 */
/* ── A custom field's recorded values ────────────────────────────────────────────────── */
/**
 * The records a custom field's value may hang on (docs/CUSTOM-FIELDS-PLAN.md §5, §8).
 *
 * Two, and they are the two scopes: a PERSON field's value belongs to a participant, an EVENT field's
 * to a measure entry. The pairing is enforced on write rather than left to the caller — a value on the
 * wrong kind of record is not a mislabelled row, it is a fact attached to the wrong thing.
 *
 * Deliberately not the six that carry attachments. A file is a document *about* a record and any
 * record can have documents; a custom field says what a record IS allowed to state about itself, and
 * only these two have a definition scope that can name them.
 */
export const CUSTOM_VALUE_ENTITY_TYPES = ['participant', 'measureEntry'];
export const customValueEntityType = z.enum(CUSTOM_VALUE_ENTITY_TYPES);
/** Which record a field of this scope belongs on. The one place the pairing is stated. */
export function entityTypeForScope(scope) {
    return scope === 'PERSON' ? 'participant' : 'measureEntry';
}
/**
 * One recorded value, as a record's panel reads it.
 *
 * ── Why the value is one string and not four typed fields ─────────────────────────────
 * On disk it is typed — a number sorts as a number, a date ranges as a date (§8a) — and that is what
 * makes reporting possible. On the wire it is the one rendering both the display and the edit control
 * need: a date as `YYYY-MM-DD`, a number as its decimal form, a coded value as its CODE. Four optional
 * fields where exactly one is populated would make every consumer branch on which, to arrive at the
 * same string.
 *
 * `valueLabel` is the coded case, resolved through the field's own vocabulary — so a retired code still
 * renders as what it meant, the way `labelsFor` does for every built-in coded field.
 */
export const customFieldValue = z.object({
    fieldId: z.string(),
    /** The definition's permanent name, so a caller can key its own state on something stable. */
    key: z.string(),
    label: z.string(),
    valueType: z.enum(FIELD_VALUE_TYPES),
    /**
     * Whether this value was stored encrypted.
     *
     * Surfaced so a screen can say so beside the value. It is the reason the field is absent from every
     * report, and somebody looking at a filled-in field is exactly who wonders where it went.
     */
    sensitive: z.boolean(),
    value: z.string(),
    valueLabel: z.string().optional(),
    recordedAt: z.string(),
});
export const customFieldValueListResponse = z.object({ items: z.array(customFieldValue) });
/**
 * How many records hold a value for one field — what a retirement confirmation shows first.
 *
 * It informs and never gates, for the reason Record Management gives about a vocabulary value: a field
 * in use is exactly the one an organisation most often wants to stop collecting, and refusing would mean
 * that still needs a developer (§10).
 */
export const customFieldValueCount = z.object({ fieldId: z.string(), count: z.number() });
/**
 * How many records hold each value of one custom field (docs/CUSTOM-FIELDS-PLAN.md §6.4).
 *
 * Coded fields only, and only those not marked sensitive — which is the trade the definition recorded, said
 * here as a shape rather than a footnote: there is no response for a field that cannot be counted, so no
 * caller can render an empty breakdown and call it a result.
 *
 * Served by `ix_custom_field_value_reporting`, which is the reason a coded field is the answer to "we want
 * to report on this" and free text never is.
 */
export const customFieldBreakdown = z.object({
    fieldId: z.string(),
    label: z.string(),
    /** `codedCount`, not `cohortCount`: a field's values have no verified/unverified distinction. */
    counts: z.array(codedCount),
});
/**
 * One record that holds a matching value (§6.4).
 *
 * A row is a RECORD, not a value: the point of searching a custom field is to reach the participants or the
 * events that hold it. The code is what comes back, because that is the de-identified handle every other
 * search in this system returns.
 */
export const customValueMatch = z.object({
    entityType: customValueEntityType,
    entityId: z.string(),
    /** The record's own code. Absent only if the record has since gone, which should not happen. */
    code: z.string().optional(),
    /** The value, in the canonical form — a code, a decimal, or `YYYY-MM-DD`. */
    value: z.string(),
    /** For a coded field, what its vocabulary calls that code. */
    valueLabel: z.string().optional(),
});
export const customValueSearchResponse = pageOf(customValueMatch);
export function customFieldNotReportableReason(field) {
    if (field.sensitive) {
        return ('Marked sensitive, so it is stored encrypted and cannot be counted, filtered or grouped in ' +
            'any report. It will be recorded and displayed on the record, and nowhere else.');
    }
    if (field.valueType === 'TEXT') {
        return ('Free text is never reportable, whatever else is chosen — a thousand different answers ' +
            'grouped by value is a thousand groups of one. A coded field is the answer to "we want to ' +
            'report on this".');
    }
    return undefined;
}
/* ── The column map ──────────────────────────────────────────────────────────────── */
export const COLUMN_MAP_STATUSES = ['DRAFT', 'ACTIVE', 'SUPERSEDED'];
export const columnMapStatus = z.enum(COLUMN_MAP_STATUSES);
export const COLUMN_MAP_FILE_TYPES = ['CSV', 'XLSX'];
/** What to do with a value no `valueMap` entry covers. */
export const UNMAPPED_VALUE_POLICIES = ['PASS_THROUGH', 'USE_UNKNOWN', 'ROW_ERROR'];
export const valueMapping = z.object({ from: z.string(), to: z.string() });
/**
 * One incoming column, and what it means.
 *
 * `sourceColumnNormalized` exists because agencies re-export with cosmetic changes — a trailing
 * space, a change of case — and a map that broke on those would need re-confirming every month.
 * Matching is on the normalized form; the verbatim string is kept for display, so the verification
 * table can show the column as the file spells it.
 */
export const columnBinding = z.object({
    sourceColumn: z.string(),
    sourceColumnNormalized: z.string(),
    /** A catalog key, or IGNORE. */
    target: z.string(),
    valueMap: z.array(valueMapping).optional(),
    unmappedValue: z.enum(UNMAPPED_VALUE_POLICIES).optional(),
    /** A row missing this column's value is an error rather than a blank field. */
    required: z.boolean().optional(),
    /**
     * How THIS column writes its dates, when it differs from the map's (a date column only).
     *
     * One format per map served most files, and the time-optional rule (#143) widened that further. It does
     * not cover a file whose columns disagree on ORDER or YEAR WIDTH — events as `MM/DD/YYYY h:mma`, a date
     * of birth as `DD/MM/YY` — and guessing between them is how 03/04 becomes April. Absent means the map's
     * `source.dateFormat`.
     */
    dateFormat: z.string().optional(),
    /** Who decided what this column means, and when. The audit trail of a mapping decision. */
    mappedBy: z.string().optional(),
    mappedAt: z.string().optional(),
});
/**
 * How one row becomes N facts.
 *
 * ── The finding that shaped this ─────────────────────────────────────────────────────
 * A CCSO row is two facts: a non-fatal overdose AND a Narcan administration, both banner metrics
 * counted off the same columns (plan §1.1). An engine built on "one row → one record" — the obvious
 * design, and the common one — cannot express that file, and would have undercounted Narcan by a
 * third. So a map emits a list.
 *
 * `kind` is either fixed or read from a column: CCSO's "Overdose Type" holds `Non` and `FATAL`,
 * which become NONFATAL_OVERDOSE and FATAL_OVERDOSE, while the Narcan emitter's kind is constant.
 * `when` is what stops row 4 — Narcan Administered = "No" — producing a Narcan entry.
 */
export const emitterKind = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('FIXED'), value: z.string() }),
    z.object({
        mode: z.literal('FROM_COLUMN'),
        sourceColumnNormalized: z.string(),
        valueMap: z.array(valueMapping),
    }),
]);
/**
 * The values an agency writes in a yes/no column to mean "this did not happen"
 * (docs/EMITTER-DEFAULT-PLAN.md §4).
 *
 * ── Exact match, never a prefix ───────────────────────────────────────────────────────
 * This is the whole design, and it comes from one cell. CCSO's "Overdose Type" column holds `Non` —
 * short for non-fatal, meaning an overdose that HAPPENED — in two of its three rows. `Non` and `No`
 * differ by one letter, so any rule that matched a prefix, a substring, or a leading `N` would drop
 * every non-fatal overdose in the file and leave the fatal ones. The same agency writes `Non-Hispanic`
 * and `Non Opioid-Related` elsewhere, so this is its wording rather than one unlucky column.
 *
 * `0` is here as a string because the Narcan columns are counts: `0` distributed means none, while `1`
 * and `2` administered mean it happened. Only exact `0` is refused — `0.0` and `00` would fire, which
 * is the safe direction, since an event that should not be there is visible and a missing one is not.
 *
 * `UNKNOWN` is deliberately ABSENT. It appears in six of this agency's columns, and an agency writing
 * it against a Baker Act is not asserting one — but reading it as "did not happen" loses real events
 * from agencies that mean "it happened and we lack the detail". Not this list's call to make; §9 of the
 * plan leaves it open, and a per-emitter condition overrides either way.
 */
export const EMITTER_NEGATIVE_VALUES = ['', 'NO', 'N', '0', 'NONE', 'FALSE'];
/**
 * Whether a cell means "this happened", for the `MEANINGFUL` operator.
 *
 * Trimmed and upper-cased, then matched whole against the list. Shared so the SPA can say on screen
 * what the server will do, rather than describing it in prose that drifts.
 */
export function meansItHappened(raw) {
    const text = raw === null || raw === undefined ? '' : String(raw).trim().toUpperCase();
    return !EMITTER_NEGATIVE_VALUES.includes(text);
}
export const EMITTER_OPERATORS = [
    'EQUALS',
    'NOT_EQUALS',
    'IN',
    'NOT_IN',
    'PRESENT',
    /**
     * "The column says this happened" — the default when a column is mapped to a record type.
     *
     * Its own operator rather than an implicit rule for an emitter with no condition, because "no
     * condition" already means something correct for a different file: the CCSO incident feed's emitters
     * have none because every row of it IS an incident, and there is no flag column to test. Making the
     * absence mean "test the column" would break the one import that works today, silently.
     */
    'MEANINGFUL',
];
export const emitterCondition = z.object({
    sourceColumnNormalized: z.string(),
    operator: z.enum(EMITTER_OPERATORS),
    values: z.array(z.string()).optional(),
});
/**
 * Each condition in words, wherever a person reads or chooses one — the editors, the rule read-back, the help
 * (tester feedback). One table, so the screens cannot drift. `MEANINGFUL` and `PRESENT` read no values.
 */
export const EMITTER_OPERATOR_WORDS = {
    MEANINGFUL: {
        label: 'Shows it happened',
        help: 'Counts anything except blank, No, N, 0, None or false, in any capitalization',
        needsValues: false,
    },
    PRESENT: { label: 'Has anything in it (even No or 0)', needsValues: false },
    EQUALS: { label: 'is', needsValues: true },
    NOT_EQUALS: { label: 'is not', needsValues: true },
    IN: { label: 'is one of', needsValues: true },
    NOT_IN: { label: 'is none of', needsValues: true },
};
/** A rule's condition read back in words: "Baker Act — Shows it happened", "Status — is one of "A" or "B"". */
/** Said wherever a rule's values are typed or picked: they match the way vocabulary values do (#152). */
export const RULE_VALUES_HELP = 'Capitalization and extra spaces are ignored, so NON matches Non';
export function describeCondition(when) {
    const words = EMITTER_OPERATOR_WORDS[when.operator];
    const values = words.needsValues
        ? ` ${(when.values ?? []).map((v) => `"${v}"`).join(' or ')}`
        : '';
    return `${when.sourceColumnNormalized} — ${words.label}${values}`;
}
/** "shows it happened", "is "Yes" or "Y"": a rule's condition, as the end of "when it …". */
function conditionPhrase(when) {
    const values = (when.values ?? []).map((v) => `"${v}"`).join(' or ');
    switch (when.operator) {
        case 'MEANINGFUL':
            return 'shows it happened';
        case 'PRESENT':
            return 'has anything in it';
        case 'EQUALS':
        case 'IN':
            return `is ${values}`;
        case 'NOT_EQUALS':
        case 'NOT_IN':
            return `is not ${values}`;
    }
}
/**
 * Everything a template does with one column: its field mapping, and every event rule that reads it. Empty
 * means nothing reads it — the only case a column is "Ignored".
 *
 * A column can have several uses: Narcan Administered is an event detail AND the condition that creates the
 * Narcan event. A rule that takes its kind from a column and is conditioned on the same column (Overdose Type,
 * "Shows it happened") says only that it decides the kind — that is what it does with it.
 */
export function columnUses(map, column, fieldLabel) {
    const uses = [];
    const target = map.columns.find((c) => c.sourceColumnNormalized === column)?.target;
    if (target && target !== IGNORE_TARGET && target !== UNMATCHED_TARGET) {
        const label = fieldLabel(target) ?? target;
        uses.push(target.startsWith('measure.detail.')
            ? { use: 'EVENT_DETAIL', text: `Event detail: ${label}` }
            : { use: 'FIELD', text: label });
    }
    const kindWords = (kinds) => [...new Set(kinds)].map((k) => MEASURE_KIND_LABELS[k] ?? k).join(' or ');
    for (const e of map.emitters) {
        const kinds = e.kind.mode === 'FIXED' ? [e.kind.value ?? ''] : (e.kind.valueMap ?? []).map((v) => v.to);
        const decides = e.kind.mode === 'FROM_COLUMN' && e.kind.sourceColumnNormalized === column;
        if (decides)
            uses.push({ use: 'DECIDES_KIND', text: `Decides the event kind: ${kindWords(kinds)}` });
        if (e.when?.sourceColumnNormalized === column && !decides) {
            uses.push({
                use: 'CREATES_EVENT',
                text: `Creates event: ${kindWords(kinds)} (when it ${conditionPhrase(e.when)})`,
            });
        }
    }
    return uses.filter((u, i) => uses.findIndex((x) => x.text === u.text) === i);
}
export const columnMapEmitter = z.object({
    /** Stable, and used in per-row error messages so "which fact failed" is answerable. */
    key: z.string(),
    kind: emitterKind,
    when: emitterCondition.optional(),
    /** The normalized column names this emitter reads. A column may feed several. */
    columns: z.array(z.string()),
});
export const columnMapSource = z.object({
    fileType: z.enum(COLUMN_MAP_FILE_TYPES),
    /** 1-based. CCSO's header is row 1; a file with a title block above it is not. */
    headerRow: z.number(),
    sheetName: z.string().optional(),
    delimiter: z.string().optional(),
    /**
     * Explicit, never guessed — and absent until somebody has looked.
     *
     * CCSO writes `06/20/26 9:57p` — two-digit year, US order, lowercase meridiem. Nothing about that
     * is safely inferable, and inferring it wrong turns 20 June 2026 into 26 June 2020 silently.
     *
     * Optional because a draft written from a file nobody has read yet has no answer to it. A map
     * cannot be ACTIVATED without one: the check moved from this shape to the coherence rules, which
     * is where "no event could be dated" belongs.
     */
    dateFormat: z.string().optional(),
    timezone: z.string(),
});
/**
 * What a map produces, derived from what it binds (docs/UNIFIED-IMPORT-PLAN.md §2.1, slice 2).
 *
 * ── Why this is derived and `dataShape` is kept ───────────────────────────────────────
 * `dataShape` is what somebody answered before they had seen a column, and D-5 keeps it: a migration
 * that rewrote it would be rewriting the record of what past imports were told to be. This is the same
 * question answered by the mapping instead, which is the only place that actually knows — and for now it
 * is only reported. Nothing branches on it until slice 3.
 */
export const columnMapProduces = z.object({
    /** The map writes something to a participant: a demographic, a sex, an agency ID, a date of birth. */
    participants: z.boolean(),
    /** The map declares at least one emitter, so its rows become measure entries. */
    events: z.boolean(),
});
/**
 * The one participant target that writes nothing.
 *
 * A participant code is minted by this system and is the way a row FINDS somebody — tier 0 of the match
 * ladder. An import can never write one, so a map that binds it and nothing else is reading people rather
 * than producing them, and calling that "produces participants" would tell somebody their incident file
 * was about to create records it cannot create.
 *
 * Every other `participant.*` target does land somewhere: a demographic and a sex on the record, an
 * external ID as its own row against the agency that issued it, a date of birth at registration.
 */
const LOOKUP_ONLY_PARTICIPANT_TARGETS = ['participant.participantCode'];
/**
 * What a map produces, from the map alone.
 *
 * ── Pure, and what that costs ─────────────────────────────────────────────────────────
 * It reads the map's own document and nothing else, so both the API and the browser can call it and
 * neither needs the organisation's catalog. The cost is `custom.*` targets: whether one is a PERSON or an
 * EVENT field is a property of the definition, which only the catalog knows. A map whose ONLY person-ish
 * binding is a person-scope custom field therefore reports `participants: false` here.
 *
 * That is acceptable while this is only reported, and it is why slice 3 — where the answer decides what
 * gets written — resolves the catalog rather than calling this.
 */
export function producesFor(map) {
    const writesToAPerson = map.columns.some((binding) => binding.target.startsWith('participant.') &&
        !LOOKUP_ONLY_PARTICIPANT_TARGETS.includes(binding.target));
    return {
        /**
         * A PERSON map produces participants whatever it binds, because that is what its rows ARE: one row is
         * one person, and a roster that mapped only a participant code would still be a list of people.
         */
        /**
         * The shape clause survives slice 7 deliberately, for legacy templates only.
         *
         * `dataShape` is absent on every template created since the toggle was removed, so for those this is
         * purely the bindings — which is what slice 2 claimed and what the engine now relies on. It is kept
         * for the templates that HAVE a shape because their answer must not change: a roster whose bindings
         * happen not to trip `writesToAPerson` was still a roster, and flipping it to "produces nothing"
         * would retire a working import on a derivation subtlety.
         */
        participants: map.dataShape === 'PERSON' || writesToAPerson,
        events: map.emitters.length > 0,
    };
}
export const columnMapSummary = z.object({
    id: z.string(),
    facilityId: z.string(),
    facilityName: z.string().optional(),
    name: z.string(),
    version: z.number(),
    status: columnMapStatus,
    /**
     * What somebody was asked before the upfront toggle was removed — history, not a rule (D-5, slice 7).
     *
     * Absent on every template created since. Read `produces` for what a template actually does; this is
     * kept so a question about an old import still has an answer.
     */
    dataShape: agencyDataShape.optional(),
    /**
     * Whether a row read through this map may introduce somebody nobody has (UNIFIED-IMPORT-PLAN D-3).
     *
     * Beside `produces`, and a different kind of answer: `produces.participants` is DERIVED from what the
     * bindings write, and this is CHOSEN. A map can write a person's race without being allowed to invent
     * the person — that is the normal case for an incident feed, and the distinction is the whole reason
     * this is not inferred from the bindings.
     */
    mayCreatePeople: z.boolean(),
    /** Absent means the agency's own setting (PARTICIPANT-ID-PLAN §3, D-6). */
    autoCreate: z.enum(AUTO_CREATE_MODES).optional(),
    source: columnMapSource,
    columnCount: z.number(),
    emitterCount: z.number(),
    /** Derived from the bindings, not chosen — see `producesFor`. Reported only, until slice 3. */
    produces: columnMapProduces,
    createdBy: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const columnMapDetail = columnMapSummary.extend({
    /**
     * The auto-create setting this template's rows are read under, and where it comes from: the template's
     * own override, or its agency's. Shown wherever the template is, because an override silently beating
     * the agency is exactly what a person cannot see from the agency's screen.
     */
    autoCreateInEffect: z.enum(AUTO_CREATE_MODES).optional(),
    autoCreateSource: z.enum(['TEMPLATE', 'AGENCY']).optional(),
    agencyAutoCreate: z.enum(AUTO_CREATE_MODES).optional(),
    columns: z.array(columnBinding),
    emitters: z.array(columnMapEmitter),
});
export const columnMapListResponse = pageOf(columnMapSummary);
/* ── Import batches ──────────────────────────────────────────────────────────────── */
export const INGEST_BATCH_STATUSES = [
    'UPLOADED',
    'INSPECTED',
    'NEEDS_MAPPING',
    'READY',
    'APPLIED',
    'FAILED',
    'REVERTED',
    /**
     * Set aside without being applied (decided 10/7): kept, with the reason, but no longer waiting for anybody
     * — out of "needs mapping" and "ready to apply". Nothing is deleted; the app cannot.
     */
    'SUPERSEDED',
];
export const ingestBatchStatus = z.enum(INGEST_BATCH_STATUSES);
export const COLUMN_STATUSES = [
    'MAPPED',
    'IGNORED',
    /** Somebody looked and said there is no field for it. Does not block (CUSTOM-FIELDS §1). */
    'UNMATCHED',
    'UNMAPPED',
    'MISSING',
];
/**
 * One column of an uploaded file, as verification found it.
 *
 * `UNMAPPED` is the one that blocks: a column nobody has explained cannot be applied, because a
 * column silently dropped is how a file that "imported fine" turns out to be missing a third of its
 * data. `IGNORED` is the same data not being used, decided by a person and recorded.
 */
export const inspectedColumn = z.object({
    /** As the file spells it. A MISSING column carries the map's spelling instead. */
    sourceColumn: z.string(),
    sourceColumnNormalized: z.string(),
    status: z.enum(COLUMN_STATUSES),
    target: z.string().optional(),
    targetLabel: z.string().optional(),
    /**
     * Where a mapped value ends up, in words — "on the person", "custom field on each event" — so "Mapped" says
     * where to look for it (tester feedback 10/7: a ZIP mapped to an event field read as missing on the person).
     */
    targetPlace: z.string().optional(),
    /** An UNMAPPED column whose map still binds it to a field since retired: the field, so it can be remapped. */
    retiredTarget: z.string().optional(),
    mappedBy: z.string().optional(),
    /** A value from the first data row, so a mapper can see what they are deciding about. */
    sample: z.string().optional(),
    required: z.boolean().optional(),
});
/**
 * How much of an upload repeats what is already recorded (the duplicate guard): rows matching an applied batch
 * by file, by content, or by event (same agency, same minute), and other waiting uploads sharing its file or
 * rows. Applying with overlapping rows needs a choice: only the new rows, or anyway with a reason (audited).
 */
export const batchOverlap = z.object({
    totalRows: z.number().int(),
    overlappingRows: z.number().int(),
    rowNumbers: z.array(z.number().int()),
    appliedBatches: z.array(z.object({ batchCode: z.string(), appliedAt: z.string().optional() })),
    sameFileAs: z.string().optional(),
    waitingBatches: z.array(z.string()),
});
/** What to do about rows already recorded, when applying an upload that overlaps (the duplicate guard). */
export const applyBatchBody = z
    .object({
    overlap: z
        .discriminatedUnion('mode', [
        z.object({ mode: z.literal('NEW_ROWS_ONLY') }).strict(),
        z
            .object({ mode: z.literal('APPLY_ANYWAY'), reason: z.string().trim().min(10).max(1000) })
            .strict(),
    ])
        .optional(),
})
    .strict();
/** The guard's sentence: "2 of 3 rows match events already recorded from ING-000046 on 30 Sep". */
export function overlapSentence(o) {
    const from = o.appliedBatches
        .map((b) => {
        if (!b.appliedAt)
            return b.batchCode;
        const d = new Date(b.appliedAt);
        const day = new Intl.DateTimeFormat('en-US', {
            timeZone: ANALYTICS_TIME_ZONE,
            day: 'numeric',
            month: 'short',
        })
            .formatToParts(d)
            .reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
        return `${b.batchCode} on ${day.day} ${day.month}`;
    })
        .join(', ');
    return `${o.overlappingRows} of ${o.totalRows} rows match events already recorded from ${from}`;
}
export const ingestBatchSummary = z.object({
    id: z.string(),
    batchCode: z.string(),
    facilityId: z.string(),
    facilityName: z.string().optional(),
    reportId: z.string(),
    columnMapId: z.string(),
    columnMapVersion: z.number(),
    /**
     * What this import was told it contained, before the toggle was removed — history (D-5, slice 7).
     *
     * Absent on any import started since. Read `produces` for what its template actually records.
     */
    dataShape: agencyDataShape.optional(),
    /**
     * What the template pinned to this import produces, derived from its bindings (slice 7).
     *
     * Carried here because every screen that used to ask "is this a person file?" is really asking this, and
     * with the shape gone a screen holding only the batch could not answer it without a second request.
     */
    produces: columnMapProduces,
    /** Whether rows read through that template may introduce people (D-3). */
    mayCreatePeople: z.boolean(),
    status: ingestBatchStatus,
    rowCount: z.number(),
    appliedRowCount: z.number(),
    errorRowCount: z.number(),
    /** Person batches: rows the ladder resolved on its own, and rows waiting for a person. */
    matchedRowCount: z.number().optional(),
    pendingDecisionCount: z.number().optional(),
    /**
     * People a reverted import introduced and could not unmake.
     *
     * A registration cannot be reverted: somebody introduced by a file may since have been linked to
     * a case and named on three measure entries. They stand, and are named here so whoever withdrew
     * the import knows exactly who is now theirs to deal with (P-4).
     */
    registeredParticipantCodes: z.array(z.string()).optional(),
    /**
     * What withdrawing this import did about the enrollment episodes it opened (ENROLLMENT §4, E-5).
     *
     * `revertedEpisodeCount` counts the ones it closed, with the withdrawal named in the reason.
     * `caseCodesLeftClosed` names the ones it deliberately left: episodes this import opened AND closed
     * itself, and episodes that were somebody else's open episode before this file closed them.
     * Re-opening those would assert somebody is enrolled again, which a withdrawal cannot know.
     */
    revertedEpisodeCount: z.number().optional(),
    caseCodesLeftClosed: z.array(z.string()).optional(),
    unmappedColumns: z.array(z.string()),
    /**
     * Columns somebody looked at and could not place (docs/CUSTOM-FIELDS-PLAN.md §1).
     *
     * Present when there are any, and never a blocker: the import applies, and these are what it could
     * not store. Their values are not kept anywhere — a value against a field that does not exist is PHI
     * with no type, no sensitivity decision and no retention rule (D-8).
     */
    unmatchedColumns: z.array(z.string()).optional(),
    createdBy: z.string(),
    createdAt: z.string(),
    inspectedAt: z.string().optional(),
    /** Why, when and by whom an unapplied import was set aside. */
    supersededNote: z.string().optional(),
    supersededAt: z.string().optional(),
    supersededBy: z.string().optional(),
});
export const ingestBatchDetail = ingestBatchSummary.extend({
    columns: z.array(inspectedColumn),
    /** Present while the import waits: how much of it is already recorded, or waiting in another upload. */
    overlap: batchOverlap.optional(),
    /** True when nothing blocks: no unmapped columns, no missing required ones. */
    readyToApply: z.boolean(),
});
export const ingestBatchListResponse = pageOf(ingestBatchSummary);
/** What starting an import hands back: the batch, and somewhere to PUT the file. */
export const startImportResponse = z.object({
    batch: ingestBatchDetail,
    uploadUrl: z.string(),
    storageKey: z.string(),
});
/** What the ladder made of one person, recorded at inspect and shown in the import review. */
export const rowMatch = z.object({
    status: z.enum(['AUTO_MATCHED', 'NEEDS_REVIEW', 'UNMATCHED']),
    participantId: z.string().optional(),
    participantCode: z.string().optional(),
    matchMethod: z.string().optional(),
    matchTier: z.number().optional(),
    confidence: z.number(),
    reviewReason: z.string().optional(),
    /** Kept even for a confident match: an override should show what else was considered. */
    candidates: z
        .array(z.object({
        participantId: z.string(),
        participantCode: z.string(),
        score: z.number(),
        reason: z.string(),
    }))
        .optional(),
    matchedAt: z.string(),
    /**
     * Set when the agency's auto-create setting will register this person at apply, with no decision asked
     * (docs/PARTICIPANT-ID-PLAN.md §3): `UNIDENTIFIED` for a row that names nobody, `IDENTIFIED` for one
     * with an agency ID or exact date of birth that matched nobody. Re-checked against the setting at apply.
     */
    autoCreate: z.enum(['UNIDENTIFIED', 'IDENTIFIED']).optional(),
});
export const ingestRow = z.object({
    id: z.string(),
    rowNumber: z.number(),
    /** The row as read. Unknown shape by nature — it is a county spreadsheet's line. */
    raw: z.record(z.unknown()),
    status: z.enum(['PENDING', 'APPLIED', 'ERROR', 'SKIPPED']),
    errors: z
        .array(z.object({
        emitterKey: z.string().optional(),
        field: z.string().optional(),
        message: z.string(),
    }))
        .optional(),
    producedEntryIds: z.array(z.string()).optional(),
    producedParticipantId: z.string().optional(),
    /** Demographics this row filled on a participant it linked to — provenance a single value cannot carry. */
    filledFields: z.array(z.string()).optional(),
    /**
     * What the file says about this person, after the column map has been applied.
     *
     * Present on person rows. This is what the review card renders: the values keyed by what each one
     * IS, rather than by the column it came from — which is the part a client cannot work out, because
     * the map lives on the server. A reviewer's `edits` sit on top of these, so a correction reads as a
     * correction rather than as the file's own word.
     */
    fileValues: z
        .object({
        dateOfBirth: z.string().optional(),
        externalId: z.string().optional(),
        participantCode: z.string().optional(),
        sex: z.string().optional(),
        demographics: z.record(z.string()).optional(),
        /** From the `review.displayName` binding. Shown while reviewing, never stored. */
        displayName: z.string().optional(),
    })
        .optional(),
    /**
     * What this row did about the person's enrollment episode
     * (docs/PERSON-IMPORT-ENROLLMENT-PLAN.md §3.2).
     *
     * Absent when the template binds no enrollment date, and when the row's own cell was blank. The
     * three outcomes are distinguishable on purpose: DIVERGED means the person was already enrolled on
     * a different date and this import deliberately changed nothing.
     */
    enrollment: z
        .object({
        outcome: z.enum(['OPENED', 'OPENED_AND_CLOSED', 'ALREADY_OPEN', 'CLOSED', 'DIVERGED']),
        caseId: z.string().optional(),
        fileOpenedAt: z.string().optional(),
        existingOpenedAt: z.string().optional(),
        fileClosedAt: z.string().optional(),
    })
        .optional(),
    match: rowMatch.optional(),
    /** A reviewer's corrections to what the file said. Never a name — see `review.displayName`. */
    edits: z.record(z.string()).optional(),
    /** What a person decided. A proposal until the batch is applied. */
    decision: z
        .object({
        disposition: z.enum(['LINK', 'REGISTER', 'SKIP']),
        participantId: z.string().optional(),
        participantCode: z.string().optional(),
        note: z.string().optional(),
        decidedBy: z.string(),
        decidedAt: z.string(),
    })
        .optional(),
});
export const ingestRowListResponse = pageOf(ingestRow);
/** A dry run: what applying would create, with nothing written. */
/**
 * What applying a batch will do about people, row by row — and under which auto-create setting.
 *
 * Asked before applying, from the same decision the apply makes (`decideRowPerson`), so the preview cannot
 * promise what the apply will not do. `setting` says which value is in effect and whose it is: a template
 * overriding its agency used to win silently.
 */
export const batchOutcomesResponse = z.object({
    setting: z.object({
        inEffect: z.enum(AUTO_CREATE_MODES),
        source: z.enum(['TEMPLATE', 'AGENCY']),
        /** The setting when the file was read — absent for a batch read before this was recorded. */
        atRead: z
            .object({ inEffect: z.enum(AUTO_CREATE_MODES), source: z.enum(['TEMPLATE', 'AGENCY']) })
            .optional(),
        /** True when the setting's value has changed since the file was read (not merely whose it is). */
        changedSinceRead: z.boolean().optional(),
        agency: z.enum(AUTO_CREATE_MODES),
        agencyName: z.string().optional(),
        templateName: z.string(),
    }),
    summary: z.object({
        matched: z.number(),
        created: z.number(),
        review: z.number(),
        decision: z.number(),
        skipped: z.number(),
        error: z.number(),
    }),
    rows: z.array(z.object({
        rowNumber: z.number(),
        outcome: z.enum(['MATCHED', 'CREATED', 'REVIEW', 'DECISION', 'SKIPPED', 'ERROR']),
        /** The person matched, when there is one. */
        participantCode: z.string().optional(),
        /** In words: why it goes to review, why it needs a decision, what will be created, what is wrong. */
        reason: z.string().optional(),
        events: z.number(),
    })),
    /** True when the batch has more rows than were predicted. */
    truncated: z.boolean(),
});
export const ingestPreviewResponse = z.object({
    rows: z.array(z.object({
        rowNumber: z.number(),
        facts: z.array(z.object({
            emitterKey: z.string(),
            kind: z.string(),
            /** Already resolved to an instant — the point of a preview is seeing which one. */
            occurredAt: z.string(),
            detail: z.record(z.unknown()),
            location: z.string().optional(),
        })),
        errors: z.array(z.object({
            emitterKey: z.string().optional(),
            field: z.string().optional(),
            message: z.string(),
            /** A value left off an event that is still recorded, rather than a reason it is not. */
            leftOff: z.literal(true).optional(),
        })),
    })),
});
/**
 * What an import would enroll, before it is applied
 * (docs/PERSON-IMPORT-ENROLLMENT-PLAN.md §3, slice 4).
 *
 * The outcomes are in the future tense even after apply, when `recorded` is true: the column asks
 * what each row does about enrollment, and a reviewer comparing before with after should not have to
 * hold two vocabularies in their head.
 *
 * `DIVERGES` is the one worth reading twice. The person is already enrolled here on a different date,
 * and this import will deliberately change nothing — the provider's roster is authoritative about
 * their own enrollment date, and the existing episode may have been opened by somebody with context
 * the file does not carry.
 */
export const enrollmentPreviewResponse = z.object({
    /** Whether the template binds an enrollment date at all. A roster without one enrolls nobody. */
    bound: z.boolean(),
    facilityName: z.string().optional(),
    /** Whether this caller holds `case:write`. Applying an import that opens episodes needs it (§5). */
    mayApply: z.boolean(),
    counts: z.object({
        willOpen: z.number(),
        willOpenAndClose: z.number(),
        alreadyEnrolled: z.number(),
        willClose: z.number(),
        diverging: z.number(),
        noDate: z.number(),
        undecided: z.number(),
    }),
    rows: z.array(z.object({
        rowNumber: z.number(),
        outcome: z.enum([
            'WILL_OPEN',
            'WILL_OPEN_AND_CLOSE',
            'ALREADY_OPEN',
            'WILL_CLOSE',
            'DIVERGES',
            'NO_DATE',
            'UNDECIDED',
            'SKIPPED',
        ]),
        /** True once the row is applied: this is what happened, not what would happen. */
        recorded: z.boolean(),
        participantCode: z.string().optional(),
        /** The episode already open at this agency, named so a reviewer can go and look at it. */
        caseCode: z.string().optional(),
        fileOpenedAt: z.string().optional(),
        existingOpenedAt: z.string().optional(),
        fileClosedAt: z.string().optional(),
    })),
});
/**
 * Files on records (docs/ATTACHMENTS-PLAN.md, CRS APP-01).
 *
 * ── What is deliberately not here ─────────────────────────────────────────────────────
 * No permission field, because an attachment has none of its own: read the record, see its files
 * (§1.1). No storage key, because a key is a server concern and one in a payload is one somebody will
 * be tempted to fetch directly. And no URL — a URL is minted per click by its own endpoint, audited,
 * with a few minutes to live, so a list that carried them would issue a capability for every row and
 * record a view nobody took (§9.1).
 */
export const ATTACHMENT_ENTITY_TYPES = [
    'participant',
    'case',
    'report',
    'measureEntry',
    'facility',
    'family',
];
export const attachment = z.object({
    id: z.string(),
    entityType: z.enum(ATTACHMENT_ENTITY_TYPES),
    entityId: z.string(),
    /**
     * What the uploader called it. **May name a person** — 'JaneDoe_consent.pdf' is what agencies send.
     *
     * Which is why it is in this payload and nowhere else: never in a storage key, a log line or an
     * audit row (§5.1, §8). Anything that later indexes or exports these rows treats it as PHI.
     */
    filename: z.string(),
    /** One short filing note, because `scan_0043.pdf` explains nothing. */
    caption: z.string().optional(),
    /** The server's determination from the bytes, not the uploader's claim. */
    mimeType: z.string(),
    sizeBytes: z.number(),
    checksumSha256: z.string(),
    /**
     * What opening it will do, decided server-side and stored.
     *
     * The screen's verb comes from this — "Preview" for what a browser renders, "Open" for what it
     * downloads — so the button says what is about to happen. HTML is always ATTACHMENT: a file the
     * browser would execute is never made a browsing context (§1.2, §6).
     */
    disposition: z.enum(['INLINE', 'ATTACHMENT']),
    /** Removing is a status, not a deletion. A removed file is a fact about the record. */
    status: z.enum(['ACTIVE', 'REMOVED']),
    uploadedBy: z.string(),
    uploadedAt: z.string(),
    removedBy: z.string().optional(),
    removedAt: z.string().optional(),
    removeReason: z.string().optional(),
});
export const attachmentListResponse = z.object({ items: z.array(attachment) });
/**
 * A URL for one file's bytes, minted per click.
 *
 * Short-lived and audited at issuance: it is a bearer capability for one object, so it is asked for when
 * somebody opens a file rather than attached to every row of a list (§5.3, §8).
 *
 * `disposition` is lower-case here because it is what the signed response header says, and the screen
 * uses it directly: `inline` may be shown in a frame or an image, `attachment` must be handed to the
 * browser to save. An HTML file is always `attachment` — nothing renders somebody else's page.
 */
export const attachmentUrlResponse = z.object({
    url: z.string(),
    disposition: z.enum(['inline', 'attachment']),
    mimeType: z.string(),
    filename: z.string(),
    expiresInSeconds: z.number(),
});
/** What step one of an upload hands back: where to PUT, and the limits that applied. */
export const attachmentUploadTicket = z.object({
    storageKey: z.string(),
    uploadUrl: z.string(),
    expiresInSeconds: z.number(),
    /** The type the server will treat it as, decided from the extension. */
    mimeType: z.string(),
    maxBytes: z.number(),
});
/**
 * Columns agencies are sending that this system has no field for
 * (docs/CUSTOM-FIELDS-PLAN.md §1, D-7).
 *
 * Column headings and agency names — never a value from anybody's row, because those were never
 * stored. Grouped by column so the answer is "three agencies send a discharge disposition", which is
 * something an administrator can act on, rather than a list of imports, which is not.
 */
export const unmatchedColumnsResponse = z.object({
    items: z.array(z.object({
        sourceColumnNormalized: z.string(),
        facilityNames: z.array(z.string()),
        batchCount: z.number(),
        lastSeenAt: z.string(),
    })),
});
/** What a coded column actually contains, for value mapping. Coded targets only. */
export const columnValuesResponse = z.object({
    values: z.array(z.object({ value: z.string(), count: z.number() })),
    /** The lookup this column feeds, so the UI can offer that vocabulary as the destination. */
    lookupCategory: z.string().optional(),
});
/** Every column of a file with what is actually in it — what guided mapping is built on. */
export const columnSamplesResponse = z.object({
    columns: z.array(z.object({
        sourceColumn: z.string(),
        sourceColumnNormalized: z.string(),
        status: z.enum(COLUMN_STATUSES),
        target: z.string().optional(),
        values: z.array(z.object({ value: z.string(), count: z.number() })),
        /** Mapped to something encrypted or free-text: not empty, deliberately not shown. */
        valuesWithheld: z.boolean().optional(),
    })),
});
/** What a candidate date format would make of this file's own values. */
export const datePreviewResponse = z.object({
    results: z.array(z.object({
        value: z.string(),
        count: z.number(),
        resolved: z.string().optional(),
        /** A column that records a day (a date of birth): the `YYYY-MM-DD` apply will store, no zone. */
        day: z.string().optional(),
        error: z.string().optional(),
    })),
});
export const fieldCatalogResponse = z.object({
    items: z.array(z.object({
        key: z.string(),
        label: z.string(),
        domain: z.enum(FIELD_DOMAINS),
        valueType: z.enum(FIELD_VALUE_TYPES),
        lookupCategory: z.string().optional(),
        encrypted: z.boolean().optional(),
        required: z.boolean().optional(),
        matchKey: z.boolean().optional(),
        disambiguatorOnly: z.boolean().optional(),
        /** NUMBER only — what the control may accept, so it can say so before the server refuses. */
        min: z.number().optional(),
        max: z.number().optional(),
        integerOnly: z.boolean().optional(),
        /** A grid header's short form, where the label is too long to head a column; the label is its tooltip. */
        shortLabel: z.string().optional(),
        shapes: z.array(agencyDataShape),
    })),
});
/** Trim, collapse internal whitespace, lower-case. The one place this is defined. */
export function normalizeColumnName(name) {
    return name.trim().replace(/\s+/g, ' ').toLowerCase();
}
export const healthResponse = z.object({
    status: z.literal('ok'),
    uptimeSeconds: z.number(),
});
/**
 * Permission strings, not role names (§6.3).
 *
 * Roles are stored, editable bundles of these; code never checks a role name, so adding a
 * role is data and never a deploy. Both the API's requirePermission() and the SPA's guards
 * draw from this one list, which is why it lives in shared.
 */
export const PERMISSIONS = [
    'org:read',
    'user:read',
    'user:write',
    'role:read',
    'role:write',
    'dashboard:read',
    /**
     * Editing the dashboards other people see: a role's, an agency's, and the organisation's standard
     * (docs/DASHBOARD-PLAN.md D-7). A layout grants nothing — every widget still asks the API as its reader —
     * but it decides what a whole team looks at first, which is a supervisor's call, not everyone's.
     */
    'dashboard:manage',
    /**
     * Changing a provisional definition — a recidivism window, a completion rule, a population figure
     * (COUNTY-DEFINITIONS-NEEDED). It changes published numbers for everyone, so it is its own grant: Admin
     * holds it, and every change is audited.
     */
    'definition:manage',
    'audit:read',
    // Phase 2 domain.
    'participant:read',
    'participant:write',
    'facility:read',
    'facility:write',
    'case:read',
    'case:write',
    'report:read',
    'report:write',
    /**
     * Measure entries — the recorded events (overdoses, Narcan, Marchman/Baker, ED visits,
     * arrests, drug court) that CRS CDE-03, ADE-01 and ADE-02 ask for.
     *
     * Their own pair rather than borrowing `report:*`: a report is a document a facility sent,
     * and a measure entry is an assertion that something happened to a person. An agency that
     * should be able to file overdose counts is not automatically one that should be able to
     * retract links on someone else's inbound report.
     */
    'measure:read',
    'measure:write',
    /**
     * Households (CRS CASE-05). Their own pair rather than borrowing `participant:*`: grouping
     * people into a household is a different act from recording facts about one person, and an
     * agency that should be able to read participants is not automatically one that should be able
     * to assert who lives with whom.
     */
    'family:read',
    'family:write',
    /**
     * Configurable vocabularies.
     *
     * Read is granted to every role including ViewOnly: a form cannot render its dropdowns without
     * it, and a vocabulary discloses nothing — 'MEDICAID' as a *permitted value* is configuration,
     * where 'MEDICAID' recorded against a participant is what that record encrypts.
     */
    'lookup:read',
    'lookup:write',
    /**
     * Defining a new field (docs/CUSTOM-FIELDS-PLAN.md §9).
     *
     * Its own permission rather than a reuse of `lookup:write`, because the two acts differ in what
     * they can do. Editing a vocabulary changes what an existing field accepts; defining a field
     * changes what this system stores — with a sensitivity decision behind it, and a reporting
     * consequence that follows from that decision and cannot be undone by editing a list. The same
     * person usually holds both, and they are still different grants.
     *
     * There is deliberately no `field:read`. A custom field's definition is configuration, read with
     * `lookup:read` like every other list a form has to render; a custom field's *value* is read
     * wherever its record is read, with that record's own permission — the attachments rule, for the
     * same reason (ATTACHMENTS-PLAN §1.1).
     */
    'field:write',
    /**
     * Cross-org visibility (D5). These are the two permissions that cross the tenant boundary
     * Phase 1 established, and they are controlled by the mechanism that already exists rather
     * than a new one: assertCanGrant() in the user and role services refuses to grant any
     * permission the granting actor does not itself hold.
     *
     * Admin now holds both, so an Admin CAN confer them — see the note on SYSTEM_ROLES.Admin. That was
     * a deliberate call for a single-operator deployment, and it is the one entry in this list where
     * it deserves a second look before a second tenant exists: cross-org read is the only permission
     * here whose blast radius is another organisation's records rather than this one's.
     */
    'participant:crossorg:read',
    'participant:crossorg:link',
    /**
     * The labeled relationship graph (docs/PARTICIPANT-RELATIONSHIPS-PLAN.md).
     *
     * Two permissions, and READ is as restricted as WRITE — which is unlike every other pair in
     * this list. Everywhere else a read is the ordinary case and a write is the privileged one.
     * Here the disclosure *is* the read: 'PTC-000123 parent-of PTC-000418' plus two age bands and
     * a shared ZIP prefix is a family a worker can often name, and no field on either record had
     * to be readable for that to happen. The graph's shape is the identifier.
     *
     * Which is why neither of these is in Staff or ViewOnly, and why RelationshipViewer exists as the
     * way to grant them to somebody who is not an administrator. Admin holds them too, as of the
     * "Admin holds everything" decision recorded on SYSTEM_ROLES.Admin — so the graph is no longer
     * dark, and turning it on for a non-admin is an Admin's act rather than an operator's.
     */
    'participant:relationships:read',
    'participant:relationships:write',
    /**
     * Deciding that two participant records are one person (docs/PARTICIPANT-MERGE-PLAN.md §4).
     *
     * One permission for both halves, deliberately. A list of "these two might be the same person" is
     * an assertion about real people rather than a report, so seeing the candidates is as privileged
     * as acting on them (M-11).
     *
     * It is in Admin and in DuplicateReviewer, and in nothing else. It used to be in no role at all —
     * see SYSTEM_ROLES.Admin for why that changed and what it costs. What has not changed is that
     * merging rewrites the participant reference on every case, entry, membership, relationship and
     * report link belonging to two people, and cannot be undone (M-6); an Admin conferring it on
     * somebody else should be as deliberate as the operator action it replaces.
     */
    'participant:merge',
    /**
     * Seeing a participant's actual date of birth, and finding a participant by one
     * (docs/PARTICIPANT-DOB-ACCESS-PLAN.md).
     *
     * A date of birth is directly identifying, which is why everything else about a participant is
     * built to avoid needing it: the age band is what reports use, `dobKey` is what matching uses,
     * and the picker exists because there are no names to type. This permission is the exception. It
     * used to be in no role at all; it is now in Admin, per the note on SYSTEM_ROLES.Admin, and every
     * use of it still writes a security event naming the actor.
     *
     * One permission for both halves. Searching by a date is not a lesser act than reading one: it is
     * the same disclosure approached from the other end, and it starts from the date rather than from
     * a person somebody already had a reason to open.
     */
    'participant:dob:read',
    /**
     * Agency file ingestion (docs/AGENCY-INGESTION-PLAN.md §9 O-6).
     *
     * Three, and the split between the last two is the point. `ingest:write` covers uploading a file
     * and deciding what its columns mean — work for whoever understands the agency's export.
     * `ingest:apply` commits a batch, which writes potentially thousands of countable events into the
     * measures every published number is computed from. Separating them lets the mapping be delegated
     * while committing stays with a supervisor.
     *
     * Neither is in ViewOnly. Reading a batch is, because seeing what arrived is not changing it.
     */
    'ingest:read',
    'ingest:write',
    'ingest:apply',
    /**
     * A funder's access (slice 13): aggregate reports only, from the funder report builder. No record of any
     * kind, no drill-down, and small-count suppression on every count, whatever the category. The Funder role
     * holds this and nothing else.
     */
    'funder:read',
    // Retired in Phase 2 along with the demo module itself.
    'demo:read',
    'demo:write',
];
/** Seeded role templates. An org may edit or add to these; the names are not load-bearing. */
export const SYSTEM_ROLES = {
    /**
     * Admin holds EVERY permission in the list above, by spread rather than by enumeration.
     *
     * ── Why the spread ────────────────────────────────────────────────────────────────────
     * The enumerated version is how this broke. `field:write` was added to PERMISSIONS and to this
     * list, but a deployment seeded before that day kept the Admin role it was created with, and
     * "Add a field" answered `Missing permission: field:write` to the only account there was. The
     * seed script reconciles additively, so the fix was a re-seed — but the next permission would
     * have done the same thing, because a hand-maintained copy of a list drifts from the list.
     * `[...PERMISSIONS]` cannot drift: a permission added above is in Admin the moment it exists.
     *
     * ── What that decides, deliberately ───────────────────────────────────────────────────
     * It ends the "ships dark" property for Admin. `participant:merge`,
     * `participant:relationships:read`/`:write`, `participant:dob:read` and the two
     * `participant:crossorg:*` permissions were kept out of every assignable role so that
     * `assertCanGrant` — which refuses to hand out what the grantor does not hold — made
     * self-escalation impossible rather than merely discouraged. An Admin holding them can now confer
     * them, and `participant:crossorg:read` reaches across the tenant boundary Phase 1 established.
     *
     * That is the instruction, and it is the right default for a single-operator deployment where the
     * Admin IS the operator. It is the wrong default for a multi-tenant one, and the way back is not
     * to re-enumerate this list — it is a named exclusion set here, so that what Admin is missing is
     * written down in one place instead of being the residue of a list somebody forgot to update.
     *
     * `assertCanGrant` is untouched and still the mechanism: it is generic, so it goes on working for
     * whatever the grantor does not hold. The purpose-built roles below (CrossOrgAuditor,
     * RelationshipViewer, DuplicateReviewer) also stay — they are how a NON-admin gets one of these
     * without getting all of them.
     */
    Admin: [...PERMISSIONS],
    /** Aggregate reports only, suppression always on (slice 13; COUNTY-DEFINITIONS-NEEDED §26). */
    Funder: ['funder:read'],
    /** Front-line work: register participants, open cases, file reports. Facilities are config. */
    Staff: [
        'org:read',
        'user:read',
        'dashboard:read',
        'participant:read',
        'participant:write',
        'facility:read',
        'case:read',
        'case:write',
        'report:read',
        'report:write',
        'measure:read',
        'measure:write',
        'family:read',
        'family:write',
        'lookup:read',
        /**
         * Staff map files but do not commit them (§9 O-6). `ingest:apply` is deliberately absent: the
         * person who knows what a column means is not necessarily the person who should turn a
         * spreadsheet into a published count.
         */
        'ingest:read',
        'ingest:write',
        'demo:read',
    ],
    ViewOnly: [
        'org:read',
        'dashboard:read',
        'participant:read',
        'facility:read',
        'case:read',
        'report:read',
        'measure:read',
        'family:read',
        'lookup:read',
        'ingest:read',
    ],
    /**
     * The cross-org role (D5). Seeded deliberately with no members, and still the way to give
     * cross-org visibility to somebody who should NOT also get everything else — which is what it is
     * for now that Admin holds these two as well and can therefore assign this role. Every use of what
     * it grants writes a security event naming the actor and a reason.
     */
    CrossOrgAuditor: [
        'org:read',
        'dashboard:read',
        'participant:read',
        'participant:crossorg:read',
        'participant:crossorg:link',
    ],
    /**
     * The relationship-graph role, seeded deliberately with no members — the same shape as
     * CrossOrgAuditor and for the same reason.
     *
     * An Admin can now assign it, because Admin holds what it grants (see SYSTEM_ROLES.Admin). It is
     * how a non-admin gets the graph and nothing else. Nobody has it until somebody is put in it, and
     * that is still worth being a decision with a name on it — the graph re-identifies a de-identified
     * system.
     *
     * `lookup:read` is in here because the add form cannot render its relationship-type dropdown
     * without reading the vocabulary.
     */
    RelationshipViewer: [
        'org:read',
        'dashboard:read',
        'participant:read',
        'lookup:read',
        'participant:relationships:read',
        'participant:relationships:write',
    ],
    /**
     * The duplicate-resolution role, seeded with no members — the third of this shape, and the one
     * with the least reversible permission in it (docs/PARTICIPANT-MERGE-PLAN.md M-11).
     *
     * `participant:merge` covers both seeing the candidate list and acting on it, because "these two
     * might be the same person" is an assertion about real people rather than a report. An Admin can
     * now assign this role, holding the permission itself (see SYSTEM_ROLES.Admin); the role remains
     * how a reviewer gets merge without getting the rest of an administrator. A merge rewrites the
     * participant reference on every case, entry, membership, relationship and report link belonging
     * to two people, and there is no unmerge (M-6).
     *
     * `lookup:read` is here for the same reason it is in RelationshipViewer: the comparison screen
     * cannot render a demographic value as anything but a code without the vocabulary. `measure:read`,
     * `case:read` and `report:read` are what let a reviewer see what each record actually holds before
     * deciding they are one person — a merge decided from two code numbers and a date is a guess.
     */
    DuplicateReviewer: [
        'org:read',
        'dashboard:read',
        'participant:read',
        'lookup:read',
        'case:read',
        'report:read',
        'measure:read',
        'participant:merge',
    ],
};
/**
 * Grid entry: committing typed rows (docs/GRID-ENTRY-PLAN.md §5.3).
 *
 * ── Why the response is per row and not a count ───────────────────────────────────────
 * Because a grid of forty rows where three are wrong commits thirty-seven, and "37 saved" is how somebody
 * comes to believe forty landed. Every row comes back named, with what happened to it and why — including
 * the ones that did nothing.
 */
export const GRID_ROW_OUTCOMES = [
    /** A new record exists. For an event this may still be waiting on a person. */
    'CREATED',
    /** An existing record changed. */
    'UPDATED',
    /** An existing row was sent with nothing different in it. Not an error, and worth saying. */
    'UNCHANGED',
    /**
     * Somebody else changed the record while the grid was open.
     *
     * Not committed, and not an error either: the typist is shown both values and decides. Overwriting
     * silently would make a grid a way to revert a colleague's correction.
     */
    'CONFLICT',
    /** The service refused it. The message is the service's own sentence. */
    'FAILED',
];
export const gridRowOutcome = z.enum(GRID_ROW_OUTCOMES);
/** One cell whose stored value moved since the row was loaded, with both values to choose between. */
export const gridConflict = z.object({
    column: z.string(),
    loaded: z.string(),
    stored: z.string(),
});
/**
 * A custom field the row carried and the commit did not write, with the reason.
 *
 * Reported rather than silently dropped: a person field typed on an event grid has nowhere to go until
 * the event resolves to somebody, and a typist who filled that column deserves to know it went nowhere
 * (docs/GRID-ENTRY-PLAN.md D-8).
 */
export const gridCustomSkip = z.object({
    key: z.string(),
    reason: z.string(),
});
/**
 * The most records one grid commit may write (docs/GRID-MULTI-EVENT-PLAN.md D-2).
 *
 * A second cap beside the row cap, because the row cap stopped meaning what the server does work for once
 * a row could record several kinds: nine kinds across two hundred rows is eighteen hundred entries, each
 * its own transaction, ladder run and audit row. Counted as one per corrected row and one per event a new
 * row records. In @cms/shared so the page can show the count against the same number the server enforces.
 */
export const GRID_COMMIT_ENTRY_CAP = 300;
export const gridRowResult = z.object({
    /** The client's own row id, echoed so the grid can put the answer on the right row. */
    rowId: z.string(),
    /**
     * The key every entry this row created carries, for withdrawing the row as one unit (D-4).
     *
     * Present on a CREATED row only. Minted by the server, never the client's row id.
     */
    rowKey: z.string().optional(),
    outcome: gridRowOutcome,
    recordId: z.string().optional(),
    recordCode: z.string().optional(),
    /**
     * For an event: whether the ladder attached a person.
     *
     * False with `CREATED` is the D-3 outcome — the event happened whether or not we know who it happened
     * to, so it is recorded and the attribution is queued.
     */
    personResolved: z.boolean().optional(),
    matchStatus: z.string().optional(),
    matchMethod: z.string().optional(),
    reviewId: z.string().optional(),
    reviewReason: z.string().optional(),
    /** The service's own sentence, when it refused. */
    message: z.string().optional(),
    conflicts: z.array(gridConflict).optional(),
    customSkipped: z.array(gridCustomSkip).optional(),
    /**
     * The other entries this row produced, one per secondary event (docs/SECONDARY-EVENTS-PLAN.md).
     *
     * Reported because they are records: a row that quietly created three things and said "Saved" would
     * leave somebody unable to tell whether the Narcan entry exists without going and looking.
     */
    alsoCreated: z
        .array(z.object({
        kind: z.string(),
        recordId: z.string().optional(),
        recordCode: z.string().optional(),
        /** Present when this one was refused. The primary still stands (D-1). */
        message: z.string().optional(),
    }))
        .optional(),
});
/** What withdrawing a grid row retracted: every entry it created, together (D-4). */
export const gridWithdrawResponse = z.object({
    withdrawn: z.array(z.object({ recordId: z.string(), recordCode: z.string(), kind: z.string() })),
});
export const gridCommitResponse = z.object({
    rows: z.array(gridRowResult),
    /** The same rows counted by outcome, so a summary line needs no client-side arithmetic. */
    created: z.number(),
    updated: z.number(),
    unchanged: z.number(),
    conflicted: z.number(),
    failed: z.number(),
    /** How many of the created events are waiting on a person. */
    pendingMatch: z.number(),
});
/**
 * The grid's create-time person check is GONE, with the endpoints it described.
 *
 * ── Why the contract went too, and what stayed ────────────────────────────────────────
 * It described one thing: a typed row's person being resolved before anything was created — the
 * disposition vocabulary, the safe candidate attributes, and `suggestedDisposition`. With the grid
 * events-only there is no such row, no caller for any of it, and a contract for a capability that does
 * not exist is a map of a road nobody can drive down.
 *
 * `gridCommitResponse` and `gridRowResult` above stay: the events commit is the one thing the grid still
 * does, and those are its answer.
 *
 * The argument the check was built on has NOT gone away, and is worth keeping written down:
 * `participantService.register` performs no deduplication — it validates, encrypts, keys, mints and
 * counts, and the CALLER decides whether this person should exist. The two callers that do decide are the
 * create form, where somebody is looking at one person, and the import's apply, which runs the ladder over
 * every staged row and refuses to commit until a human has settled each ambiguous one. A third path that
 * skipped that would be a duplicate factory, and a duplicate cannot be undone: merging rewrites the
 * participant reference on every case, entry, membership, relationship and report link belonging to two
 * people, and there is no way back.
 */
/**
 * Exporting query results (docs/QUERY-EXPORT-PLAN.md, REP-06).
 *
 * ── The property this whole feature is built on ────────────────────────────────────────
 * An export can never contain more than the screen already showed — by construction, not by
 * discipline. Every row is serialised from the DTO the LIST endpoint already returned, which is the
 * non-PHI summary projection: the one where the twelve encrypted demographics are absent and where a
 * date of birth is absent, because a list is the request most likely to be made casually.
 *
 * So the columns below are not an allow-list somebody has to keep in step with that projection. They
 * are a subset of it, and a test asserts exactly that against each entity's own zod contract — which
 * means a column naming a field the summary does not carry fails the build rather than exporting a
 * column of blanks, and a field the summary must never carry cannot be named here at all.
 *
 * Consequence, stated because somebody will look for it: no export from this feature contains a date
 * of birth. Reading one is its own endpoint, behind its own permission, audited per read.
 */
/** `docx` is a Word document: the table and what it is of, for a report somebody edits (OCC-68). */
export const EXPORT_FORMATS = ['csv', 'xlsx', 'pdf', 'docx'];
export const exportFormat = z.enum(EXPORT_FORMATS);
/**
 * What can be exported: the workspace's panels, plus the cohort counts.
 *
 * The same names `PANEL_ENTITIES` uses, because an export is a panel's answer in a file and a second
 * vocabulary for the same seven things would drift.
 */
export const EXPORT_SUBJECTS = [
    'participant',
    'case',
    'measureEntry',
    'report',
    'facility',
    'family',
    'ingestBatch',
    /** The rollup counts, which are counters rather than people — see `COHORT_EXPORT_COLUMNS`. */
    'cohort',
];
export const exportSubject = z.enum(EXPORT_SUBJECTS);
/**
 * How many rows each format will carry, and why the numbers differ.
 *
 * A cap that is hit is reported — in the file and in the audit row — because a file that silently
 * stopped is one somebody will analyse as complete.
 */
export const EXPORT_ROW_CAPS = {
    /** A data transfer. Ten thousand rows is a real answer and a bounded response. */
    csv: 10_000,
    xlsx: 10_000,
    /** A document somebody reads or prints. Ten thousand rows is two hundred pages nobody opens. */
    pdf: 2_000,
    /** The same, for a document somebody edits. */
    docx: 2_000,
};
/**
 * The columns each subject exports.
 *
 * ── Why identifiers come first, and why there are so few ──────────────────────────────
 * The first column is always the record's own code, because that is what a person matches a row back
 * to — and in a system with no names it is the only thing that identifies a row at all. The rest are
 * what the panel's results table shows, which is what "export what I am looking at" means.
 */
export const EXPORT_COLUMNS = {
    participant: [
        { key: 'participantCode', label: 'Participant code' },
        { key: 'status', label: 'Status' },
        { key: 'ageBand', label: 'Age band' },
        { key: 'sex', label: 'Sex' },
        // The full ZIP, not the prefix every Collier ZIP shares (tester feedback 10/7).
        { key: 'zip', label: 'ZIP code' },
        /** Quality metadata, not PHI — it is what tells a reader which rows need a human. */
        { key: 'dobQuality', label: 'Date of birth quality' },
        // Phones (OCC-45): exported only to a reader with record-level access, like the full ZIP.
        { key: 'primaryPhone', label: 'Primary phone' },
        { key: 'primaryPhoneType', label: 'Primary phone type' },
        { key: 'secondaryPhone', label: 'Secondary phone' },
        { key: 'secondaryPhoneType', label: 'Secondary phone type' },
        { key: 'facilityName', label: 'Agency' },
        { key: 'registeredAt', label: 'Registered' },
    ],
    case: [
        { key: 'caseCode', label: 'Case code' },
        { key: 'participantCode', label: 'Participant code' },
        { key: 'status', label: 'Status' },
        { key: 'episodeType', label: 'Episode type' },
        { key: 'openedAt', label: 'Opened' },
        { key: 'closedAt', label: 'Closed' },
        { key: 'facilityName', label: 'Agency' },
    ],
    measureEntry: [
        { key: 'entryCode', label: 'Entry code' },
        { key: 'kind', label: 'Measure' },
        { key: 'participantCode', label: 'Participant code' },
        { key: 'occurredAt', label: 'Occurred' },
        { key: 'facilityName', label: 'Agency' },
        { key: 'matchStatus', label: 'Person resolved' },
        { key: 'source', label: 'Source' },
        { key: 'status', label: 'Status' },
    ],
    report: [
        { key: 'reportCode', label: 'Report code' },
        { key: 'status', label: 'Status' },
        { key: 'reportType', label: 'Report type' },
        { key: 'reportDate', label: 'Report date' },
        { key: 'facilityName', label: 'Agency' },
        { key: 'participantCount', label: 'People named' },
        { key: 'receivedAt', label: 'Received' },
    ],
    facility: [
        { key: 'facilityCode', label: 'Agency code' },
        { key: 'name', label: 'Name' },
        { key: 'facilityType', label: 'Type' },
        { key: 'dataShape', label: 'Data shape' },
        { key: 'status', label: 'Status' },
    ],
    family: [
        { key: 'familyCode', label: 'Family code' },
        { key: 'status', label: 'Status' },
        { key: 'memberCount', label: 'Members' },
        { key: 'createdAt', label: 'Created' },
    ],
    ingestBatch: [
        { key: 'batchCode', label: 'Batch code' },
        { key: 'status', label: 'Status' },
        { key: 'facilityName', label: 'Agency' },
        { key: 'dataShape', label: 'Data shape' },
        { key: 'rowCount', label: 'Rows' },
        { key: 'appliedRowCount', label: 'Rows applied' },
        { key: 'errorRowCount', label: 'Rows with errors' },
        { key: 'createdAt', label: 'Uploaded' },
    ],
};
/**
 * The cohort counts, which are shaped like an answer rather than like a record.
 *
 * Its own list because there is no entity here: a row is "this many people are recorded as this",
 * which has no code, no status and nothing to link to.
 */
// ── The change log (OCC-33) ──────────────────────────────────────────────────────────
export const AUDIT_ACTIONS = ['CREATE', 'UPDATE', 'DELETE', 'SECURITY'];
const auditDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a date, YYYY-MM-DD');
const auditKey = z.string().trim().min(1).max(100);
/**
 * What the change log can be narrowed by. Dates are calendar days in the county's time zone, both
 * ends included — "everything on the 3rd" is `from=to=2026-10-03`.
 */
export const auditLogFilters = z
    .object({
    from: auditDay.optional(),
    to: auditDay.optional(),
    /** An account id — "what did this person do?". */
    actorId: auditKey.optional(),
    /** The kind of record: `participant`, `case`, `user`, `role`… */
    entityType: auditKey.optional(),
    /** One record — "what happened to this?". */
    entityId: auditKey.optional(),
    action: z.enum(AUDIT_ACTIONS).optional(),
    /** A security event's name, for SECURITY rows: `auth.login.failed`, `data.export`… */
    event: auditKey.optional(),
})
    .strict();
export const auditLogQuery = auditLogFilters
    .extend({
    cursor: z.string().max(500).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
/** One field that changed, as text. Encrypted values say so rather than showing ciphertext. */
export const auditChange = z.object({
    field: z.string(),
    before: z.string().optional(),
    after: z.string().optional(),
});
export const auditEntry = z.object({
    id: z.string(),
    at: z.string(),
    actorId: z.string(),
    /** The account's name, archived accounts included; absent for a system actor. */
    actorName: z.string().optional(),
    actorType: z.enum(['user', 'partner', 'system']),
    action: z.enum(AUDIT_ACTIONS),
    event: z.string().optional(),
    entityType: z.string(),
    entityId: z.string().optional(),
    changes: z.array(auditChange),
    /** More fields changed than one row lists. */
    changesTruncated: z.boolean().optional(),
    /** A bulk write touched more records than the snapshot keeps. */
    truncated: z.boolean().optional(),
    requestId: z.string().optional(),
    ip: z.string().optional(),
});
export const auditLogResponse = z.object({
    items: z.array(auditEntry),
    nextCursor: z.string().optional(),
});
/** `POST /export/audit`. CSV or Excel: a log is data to filter and sort, not a document to read. */
export const auditExportRequest = z
    .object({
    format: z.enum(['csv', 'xlsx']),
    filters: auditLogFilters.default({}),
})
    .strict();
/** One row per field changed — long, so a spreadsheet filters it without reshaping. */
export const AUDIT_EXPORT_COLUMNS = [
    { key: 'at', label: 'When (UTC)' },
    { key: 'actorName', label: 'Who' },
    { key: 'actorId', label: 'Account ID' },
    { key: 'action', label: 'Action' },
    { key: 'event', label: 'Event' },
    { key: 'entityType', label: 'Record type' },
    { key: 'entityId', label: 'Record ID' },
    { key: 'field', label: 'Field' },
    { key: 'before', label: 'Before' },
    { key: 'after', label: 'After' },
    { key: 'requestId', label: 'Request ID' },
];
export const COHORT_EXPORT_COLUMNS = [
    { key: 'dimensionLabel', label: 'Demographic' },
    { key: 'label', label: 'Value' },
    { key: 'code', label: 'Code' },
    { key: 'count', label: 'People' },
];
export function exportColumnsFor(subject) {
    return subject === 'cohort' ? COHORT_EXPORT_COLUMNS : EXPORT_COLUMNS[subject];
}
// ─── Several values in one list filter (OCC-49) ──────────────────────────────────────────────
/** The most values one list filter may hold. */
export const LIST_FILTER_MAX_VALUES = 20;
/**
 * A list filter that takes one value or several, comma-separated in the query string: `?status=ACTIVE,INACTIVE`
 * is "Active or Inactive". Always a list once parsed, so a repository has one shape to match with `$in`.
 *
 * For coded fields only — a vocabulary, a fixed list, an id. Codes and ids hold no commas; free text and dates
 * stay single, because "or" across two typed phrases or two days is a different question.
 */
export function anyOf(item) {
    return z.preprocess((v) => {
        if (typeof v !== 'string')
            return v;
        const values = [
            ...new Set(v
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)),
        ];
        // An empty filter (`?status=`) is no filter, as it was when each took one value.
        return values.length ? values : undefined;
    }, z.array(item).min(1).max(LIST_FILTER_MAX_VALUES).optional());
}
/** One filter's values from its query-string form, as a panel holds them. */
export function filterValues(value) {
    return value ? value.split(',').filter(Boolean) : [];
}
const STATUS = (values) => values.map((v) => ({
    value: v,
    label: v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, ' '),
}));
const PARTICIPANT_FIELDS = [
    {
        key: 'code',
        label: 'Participant code',
        kind: 'text',
        placeholder: 'PTC-000123',
        /** Exact on purpose: a prefix search over codes enumerates the whole tenant (§3, D-2). */
        hint: 'The whole code. Partial codes are not searched — that would list everybody.',
    },
    {
        key: 'status',
        label: 'Status',
        kind: 'enum',
        options: STATUS(['ACTIVE', 'INACTIVE', 'MERGED']),
    },
    {
        key: 'sex',
        label: 'Sex',
        kind: 'lookup',
        category: 'sex',
    },
    {
        key: 'ageBand',
        label: 'Age band',
        kind: 'enum',
        options: AGE_BAND_OPTIONS.map((b) => ({ value: b, label: b })),
        hint: 'Matches a band derived from a date of birth, or one an agency recorded directly.',
    },
    {
        key: 'zip',
        label: 'ZIP code',
        kind: 'text',
        placeholder: '34102',
        hint: 'Five digits. Every Collier ZIP starts 341, so a prefix would find everybody.',
    },
    {
        key: 'dobQuality',
        label: 'Date-of-birth quality',
        kind: 'enum',
        options: [
            { value: 'EXACT', label: 'Exact' },
            { value: 'PARTIAL', label: 'Partial (year or month only)' },
            { value: 'PLACEHOLDER', label: 'Placeholder — needs a human' },
            { value: 'ABSENT', label: 'None recorded' },
        ],
        hint: 'What needs attention: PLACEHOLDER is a date somebody typed to get past a form.',
    },
    {
        key: 'dobExact',
        label: 'Exact date of birth',
        kind: 'date',
        /**
         * §5: equality or nothing, and it needs its own permission.
         *
         * The hint says both, because a user who types a year and gets a refusal deserves to have been
         * told first.
         */
        hint: 'A full date only. It is matched exactly — never a range — and the search is recorded.',
        permission: 'participant:dob:read',
    },
];
const CASE_FIELDS = [
    { key: 'status', label: 'Status', kind: 'enum', options: STATUS(['OPEN', 'CLOSED']) },
    { key: 'facilityId', label: 'Agency', kind: 'facility' },
    { key: 'openedFrom', label: 'Opened from', kind: 'date' },
    { key: 'openedTo', label: 'Opened to', kind: 'date' },
    {
        key: 'closedFrom',
        label: 'Closed from',
        kind: 'date',
        hint: 'Closing dates apply to closed episodes only.',
    },
    { key: 'closedTo', label: 'Closed to', kind: 'date' },
];
const MEASURE_FIELDS = [
    {
        key: 'kind',
        label: 'What happened',
        kind: 'enum',
        options: MEASURE_KINDS.map((k) => ({
            value: k,
            label: k.charAt(0) + k.slice(1).toLowerCase().replace(/_/g, ' '),
        })),
    },
    { key: 'facilityId', label: 'Agency', kind: 'facility' },
    { key: 'occurredFrom', label: 'Occurred from', kind: 'date' },
    { key: 'occurredTo', label: 'Occurred to', kind: 'date' },
    {
        key: 'matchStatus',
        label: 'Person',
        kind: 'enum',
        options: [
            { value: 'MATCHED', label: 'Linked to somebody' },
            { value: 'PENDING_MATCH', label: 'Waiting on a decision' },
            { value: 'UNMATCHED', label: 'Nobody linked' },
        ],
        hint: 'PENDING_MATCH is the backlog: records an import could not resolve to a person.',
    },
    {
        key: 'source',
        label: 'Source',
        kind: 'enum',
        options: [
            { value: 'MANUAL', label: 'Entered by hand' },
            { value: 'INGESTED', label: 'From an import' },
        ],
    },
    {
        key: 'status',
        label: 'Standing',
        kind: 'enum',
        options: [
            { value: 'ACTIVE', label: 'Counted' },
            { value: 'RETRACTED', label: 'Retracted' },
        ],
    },
];
const REPORT_FIELDS = [
    {
        key: 'status',
        label: 'Status',
        kind: 'enum',
        options: [
            { value: 'RECEIVED', label: 'Received' },
            { value: 'PARSED', label: 'Parsed' },
            { value: 'LINKED', label: 'Linked' },
            { value: 'NEEDS_REVIEW', label: 'Needs review' },
            { value: 'SUPERSEDED', label: 'Superseded' },
        ],
        hint: 'NEEDS_REVIEW is the backlog: documents whose people nobody has resolved.',
    },
    { key: 'facilityId', label: 'Agency', kind: 'facility' },
    {
        key: 'reportType',
        label: 'Type',
        kind: 'text',
        placeholder: 'Monthly overdose summary',
        hint: 'Whatever the sending agency calls it. Free text, because no vocabulary governs it yet.',
    },
    /**
     * Two date ranges, because they are two different facts — and the labels have to say which.
     *
     * `receivedAt` is when this system took delivery; `reportDate` is the date printed on the document.
     * They differ by days or months, and somebody asking for "reports from March" almost always means the
     * second. A single "date" range would silently pick one of them.
     */
    { key: 'reportDateFrom', label: 'Document dated from', kind: 'date' },
    { key: 'reportDateTo', label: 'Document dated to', kind: 'date' },
    {
        key: 'receivedFrom',
        label: 'Received from',
        kind: 'date',
        hint: 'When we took delivery, not the date on the document.',
    },
    { key: 'receivedTo', label: 'Received to', kind: 'date' },
];
const FACILITY_FIELDS = [
    {
        key: 'q',
        label: 'Name contains',
        kind: 'text',
        placeholder: 'sheriff',
        /**
         * The one substring search in the workspace, and it is safe for the reason §3 gives: an agency is
         * an organisation, not a person. Every other partial match here is refused because it would
         * enumerate people.
         */
        hint: 'Any part of the name. Agencies are organisations, so partial matching discloses nothing.',
    },
    { key: 'status', label: 'Status', kind: 'enum', options: STATUS(['ACTIVE', 'INACTIVE']) },
    {
        key: 'agencyType',
        label: 'Category',
        kind: 'lookup',
        category: 'agencyType',
        hint: "The customer's own list — add a category in Record Management, not a release.",
    },
    {
        key: 'dataShape',
        label: 'Sends',
        kind: 'enum',
        options: [
            { value: 'INCIDENT', label: 'Incidents' },
            { value: 'PERSON', label: 'People' },
            { value: 'BOTH', label: 'Both' },
        ],
        hint: 'What their files are rows of, which decides how an import of theirs is read.',
    },
];
const FAMILY_FIELDS = [
    { key: 'status', label: 'Status', kind: 'enum', options: STATUS(['ACTIVE', 'INACTIVE']) },
    { key: 'familyType', label: 'Composition', kind: 'lookup', category: 'familyType' },
];
const INGEST_FIELDS = [
    { key: 'facilityId', label: 'Agency', kind: 'facility' },
    {
        key: 'status',
        label: 'Status',
        kind: 'enum',
        options: [
            { value: 'UPLOADED', label: 'Uploaded' },
            { value: 'INSPECTED', label: 'Read' },
            { value: 'NEEDS_MAPPING', label: 'Needs mapping' },
            { value: 'READY', label: 'Ready to apply' },
            { value: 'APPLIED', label: 'Applied' },
            { value: 'FAILED', label: 'Failed' },
            { value: 'REVERTED', label: 'Withdrawn' },
            { value: 'SUPERSEDED', label: 'Set aside' },
        ],
        hint: 'NEEDS_MAPPING and READY are the two worth watching: files waiting on somebody.',
    },
];
/**
 * Every panel's fields.
 *
 * ── Why `matchReview` is not here, and is not a panel either ──────────────────────────
 * The plan's slice 5 listed it with these four. It is deliberately left out: a match review is a work
 * queue with its own screen, ordered by what needs deciding, and the thing somebody wants from it is
 * the next item rather than a filtered cohort. Adding it as a search panel would offer a second, worse
 * way into a surface that already exists — and the queue's own list is not a keyset-paginated filter
 * endpoint, so it would need an API change to pretend otherwise.
 */
export const PANEL_FILTER_FIELDS = {
    participant: PARTICIPANT_FIELDS,
    case: CASE_FIELDS,
    measureEntry: MEASURE_FIELDS,
    report: REPORT_FIELDS,
    facility: FACILITY_FIELDS,
    family: FAMILY_FIELDS,
    ingestBatch: INGEST_FIELDS,
};
/** The body of an export request. Filters are the entity's own, validated by its own list schema. */
export const exportRequest = z.object({
    format: exportFormat,
    /**
     * The panel's applied filters, verbatim.
     *
     * Passed to the SAME zod list-query schema the entity's endpoint uses, so an unknown filter is
     * refused here exactly as it would be there — rather than being quietly ignored and producing a
     * file of everything.
     */
    filters: z.record(z.string().max(64), z.string().max(1024)).default({}),
    /**
     * Which of the subject's export columns go in the file, by key (OCC-48, OCC-73). Absent = all of them.
     *
     * Chosen from `exportColumnsFor(subject)` and nothing else, so a choice can narrow a file but never widen
     * it. The record's own code is always first whatever is chosen: it is what a row is matched back by. Kept
     * in the subject's order, so two files of the same subject line up.
     */
    columns: z.array(z.string().min(1).max(64)).min(1).max(60).optional(),
});
// ─── Analytics (docs/DASHBOARD-PLAN.md) ──────────────────────────────────────────────────────
/**
 * The county's calendar. A month on the dashboard is a month in Collier County, so an overdose at 23:30 on
 * 31 October is October's — the same default an import uses when a file gives no zone.
 */
export const ANALYTICS_TIME_ZONE = 'America/New_York';
/** `day` for a short range (a widget's last 7 days, say); a query by day covers a year at most. */
export const ANALYTICS_GRAINS = ['week', 'month', 'day'];
export const ANALYTICS_SUBJECTS = ['events', 'people', 'cases'];
/**
 * What a case count counts (docs/CASE-DASHBOARD-PLAN.md §1, CD-1):
 *   intakes     cases opened in the period, by their opened date
 *   discharges  closes in the period, from the history — a later reopening does not undo one
 *   active      cases open at the end of each period (and of the range), from the history
 */
export const CASE_MEASURES = ['active', 'intakes', 'discharges'];
/** A case count in words — on the dashboard, in a report, in an export. */
export const CASE_MEASURE_WORDS = {
    active: 'Active cases',
    intakes: 'Intakes',
    discharges: 'Discharges',
};
/**
 * The person dimensions a dashboard can filter and break down by — coded values only (plan §2).
 *
 * `sex`, `ageBand` and `zip3` are stored in the clear; the rest are encrypted and decrypted in the analytics
 * service (D-1). Free-text demographics are absent on purpose: a count keyed on prose counts spellings.
 * `recidivist` is absent too — recidivism waits on the county's definition.
 */
export const ANALYTICS_PERSON_DIMENSIONS = [
    'sex',
    'ageBand',
    'zip3',
    // The full five-digit ZIP (tester feedback 10/7): every Collier ZIP is 341xx, so the prefix says nothing.
    'zip',
    'race',
    'ethnicity',
    'veteranStatus',
    'disabilityStatus',
    'housingStatus',
    'employmentStatus',
    'incomeLevel',
    'insuranceCoverage',
    'familyType',
    'mhDiagnosis',
    'sudDiagnosis',
    'coOccurringDx',
    'dvSurvivor',
];
/**
 * Dimensions no longer offered — on a dashboard, a filter or a report — though still understood, so a saved
 * layout or link that names one keeps working. `zip3` carries nothing in a county whose ZIPs all start 341;
 * `coOccurringDx` is retired.
 */
export const RETIRED_ANALYTICS_DIMENSIONS = [
    'zip3',
    'coOccurringDx',
];
/** The person dimensions offered for breaking down and filtering. */
export const OFFERED_PERSON_DIMENSIONS = ANALYTICS_PERSON_DIMENSIONS.filter((d) => !RETIRED_ANALYTICS_DIMENSIONS.includes(d));
/**
 * Dimensions whose small counts are withheld (D-5): health and survivor status — and the full ZIP, because a
 * five-digit ZIP with three people in it, beside a sex and an age band, is close to naming them.
 */
export const SENSITIVE_ANALYTICS_DIMENSIONS = [
    'zip',
    'mhDiagnosis',
    'sudDiagnosis',
    'coOccurringDx',
    'dvSurvivor',
];
/** What a result can be grouped by — at most two at once. */
export const ANALYTICS_GROUP_KEYS = [
    'period',
    'site',
    'kind',
    'matchStatus',
    // Cases only (CASE-DASHBOARD C3).
    'caseStatus',
    'closeReason',
    'episodeType',
    // Events only: two coded details, from proposed lists (COUNTY-DEFINITIONS-NEEDED).
    'substanceType',
    'overdoseOutcome',
    ...ANALYTICS_PERSON_DIMENSIONS,
];
/** Event details a result can be grouped and filtered by. Each is a coded field named after its list. */
export const ANALYTICS_DETAIL_KEYS = ['substanceType', 'overdoseOutcome'];
/** Group keys that belong to events, and those that belong to cases: a query may use its own only. */
const EVENT_ONLY_KEYS = ['kind', 'matchStatus', ...ANALYTICS_DETAIL_KEYS];
const CASE_ONLY_KEYS = ['caseStatus', 'closeReason', 'episodeType'];
/**
 * The values a segment can have that are not a code.
 *
 * Both are filterable, so a drill-down on "not recorded" reaches exactly the records in that segment — every
 * segment a chart draws must be one a report can reproduce, or the tile and the report stop agreeing.
 */
export const ANALYTICS_NOT_RECORDED = '__NONE__';
/** An event with no person yet (PENDING_MATCH or UNMATCHED), in a breakdown by a person dimension. */
export const ANALYTICS_NOT_ATTRIBUTED = '__UNATTRIBUTED__';
/** Counts from 1 to this are withheld where suppression applies (D-5). */
export const ANALYTICS_SMALL_COUNT_MAX = 10;
/** The most people one query may be narrowed to: a group chosen from a list, not a population. */
export const ANALYTICS_MAX_PARTICIPANTS = 500;
/** A year: the longest range one query may group by day. */
export const ANALYTICS_MAX_DAILY_DAYS = 366;
/** Five years: the widest range one query may cover. */
export const ANALYTICS_MAX_RANGE_DAYS = 1830;
const analyticsDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine((s) => {
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}, 'Not a calendar date');
const analyticsCodes = z.array(z.string().trim().min(1).max(64)).min(1).max(200);
/**
 * What a count counts (confirmed 10/7). `events` is the default: one per event — for Narcan administered, one per
 * ENCOUNTER. `narcanAdministrations` sums the times Narcan was given in each encounter (an encounter with no
 * number recorded counts once: it was given at least once, by what the event is). `narcanMultiAdministration`
 * counts the encounters that needed two or more. The last two are for Narcan-administered events only.
 */
export const ANALYTICS_MEASURES = [
    'events',
    'narcanAdministrations',
    'narcanMultiAdministration',
];
/** What each measure is called wherever a number of it is shown — never a bare "Narcan administered: N". */
export const ANALYTICS_MEASURE_WORDS = {
    events: 'events',
    narcanAdministrations: 'administrations',
    narcanMultiAdministration: 'encounters needing 2+ administrations',
};
/** Whether a query counts Narcan-administered encounters and nothing else — where administrations make sense. */
export function isNarcanAdministeredOnly(kinds) {
    return kinds?.length === 1 && kinds[0] === 'NARCAN_ADMINISTERED';
}
/** Whether a query counts Narcan events only, administered or distributed: each event is an encounter. */
export function isNarcanEncountersOnly(kinds) {
    return (!!kinds?.length && kinds.every((k) => k === 'NARCAN_ADMINISTERED' || k === 'NARCAN_DISTRIBUTED'));
}
/** The unit a query's count is in, in words: administrations, encounters, or events. */
export function countUnit(query) {
    const measure = query.measure ?? 'events';
    if (measure !== 'events')
        return ANALYTICS_MEASURE_WORDS[measure];
    return isNarcanEncountersOnly(query.kinds) ? 'encounters' : 'events';
}
// ─── Segments (OCC-49): free-form AND/OR over person dimensions ──────────────────────────────
/** How deep groups may nest: a group, its groups, and theirs. Deeper is a query nobody can read back. */
export const SEGMENT_MAX_DEPTH = 3;
/** The most conditions one segment may hold, across every group. */
export const SEGMENT_MAX_CONDITIONS = 20;
const segmentCondition = z
    .object({
    field: z.enum(ANALYTICS_PERSON_DIMENSIONS),
    op: z.enum(['is', 'isNot']),
    values: analyticsCodes,
})
    .strict();
export const segmentGroup = z.lazy(() => z
    .object({
    match: z.enum(['all', 'any']),
    items: z
        .array(z.union([segmentCondition, segmentGroup]))
        .min(1)
        .max(SEGMENT_MAX_CONDITIONS),
})
    .strict());
export function isSegmentGroup(item) {
    return 'match' in item;
}
/** Every condition in a segment, however deep. */
export function segmentConditions(group) {
    return group.items.flatMap((item) => (isSegmentGroup(item) ? segmentConditions(item) : [item]));
}
function segmentDepth(group) {
    return 1 + Math.max(0, ...group.items.filter(isSegmentGroup).map(segmentDepth));
}
/** Whether a person's values satisfy a segment. `valueOf` gives their code for a dimension, or "not recorded". */
export function matchesSegment(group, valueOf) {
    const holds = (item) => {
        if (isSegmentGroup(item))
            return matchesSegment(item, valueOf);
        const inList = item.values.includes(valueOf(item.field));
        return item.op === 'is' ? inList : !inList;
    };
    return group.match === 'all' ? group.items.every(holds) : group.items.some(holds);
}
/**
 * The person dimensions a query filters on: its per-dimension filters and every field its segment names.
 * Everything that reads, decrypts or judges the sensitivity of a person filter asks this, not `people` alone.
 */
export function personFilterDimensions(query) {
    return [
        ...new Set([
            ...Object.keys(query.people),
            ...(query.segment ? segmentConditions(query.segment).map((c) => c.field) : []),
        ]),
    ];
}
/** Whether a person passes a query's person filters: every per-dimension filter, AND the segment. */
export function matchesPersonFilters(query, valueOf) {
    for (const [d, codes] of Object.entries(query.people)) {
        if (!codes.includes(valueOf(d)))
            return false;
    }
    return !query.segment || matchesSegment(query.segment, valueOf);
}
/**
 * A segment in words, the way a file and the report page state it:
 * `Sex is Female AND (Housing status is Homeless OR Veteran status is not Yes)`.
 *
 * AND and OR are capitalised so the logic reads apart from the labels; a nested group is bracketed, and the
 * top level is not. Several values in one condition read "is one of A, B" — an OR the condition itself makes.
 */
export function describeSegment(group, label, word, nested = false) {
    const parts = group.items.map((item) => {
        if (isSegmentGroup(item))
            return describeSegment(item, label, word, true);
        const values = item.values.map((v) => word(item.field, v));
        const verb = values.length === 1
            ? item.op === 'is'
                ? 'is'
                : 'is not'
            : item.op === 'is'
                ? 'is one of'
                : 'is none of';
        return `${label(item.field)} ${verb} ${values.join(', ')}`;
    });
    const joined = parts.join(group.match === 'all' ? ' AND ' : ' OR ');
    return nested && parts.length > 1 ? `(${joined})` : joined;
}
export const analyticsQuery = z
    .object({
    subject: z.enum(ANALYTICS_SUBJECTS).default('events'),
    /** Inclusive calendar days in `timeZone`. */
    from: analyticsDate,
    to: analyticsDate,
    grain: z.enum(ANALYTICS_GRAINS).default('month'),
    timeZone: z
        .string()
        .max(64)
        .default(ANALYTICS_TIME_ZONE)
        .refine((tz) => {
        try {
            new Intl.DateTimeFormat('en-US', { timeZone: tz });
            return true;
        }
        catch {
            return false;
        }
    }, 'Unknown time zone'),
    /** Facility ids, or ANALYTICS_NOT_RECORDED for events with no site. Absent = every site. */
    sites: analyticsCodes.optional(),
    kinds: z.array(z.enum(MEASURE_KINDS)).min(1).optional(),
    matchStatus: z
        .array(z.enum(['MATCHED', 'PENDING_MATCH', 'UNMATCHED']))
        .min(1)
        .optional(),
    /** Person-dimension filters: dimension → codes (or the two sentinels). */
    people: z.record(z.enum(ANALYTICS_PERSON_DIMENSIONS), analyticsCodes).default({}),
    /**
     * A free-form segment (OCC-49): groups of person conditions joined by AND or OR, nested up to
     * SEGMENT_MAX_DEPTH. ANDed with `people`, which stays the per-dimension filter a dashboard sets.
     */
    segment: segmentGroup.optional(),
    /** For `subject: cases`: which count (required there, refused elsewhere). */
    caseMeasure: z.enum(CASE_MEASURES).optional(),
    /** Case filters: status at the range's end, close reason, episode type (codes, or "not recorded"). */
    caseStatus: z
        .array(z.enum(['OPEN', 'CLOSED']))
        .min(1)
        .optional(),
    closeReasons: analyticsCodes.optional(),
    episodeTypes: analyticsCodes.optional(),
    /** Event detail filters (codes, or "not recorded"). */
    substanceTypes: analyticsCodes.optional(),
    overdoseOutcomes: analyticsCodes.optional(),
    groupBy: z.array(z.enum(ANALYTICS_GROUP_KEYS)).max(2).default([]),
    /**
     * These people only (OCC-70): one person's report, or a group chosen from a list. Record-level: a count
     * about named people needs permission to read them and their events, and is refused without it.
     */
    participants: z
        .array(z.string().regex(/^[a-f0-9]{24}$/, 'a participant id'))
        .min(1)
        .max(ANALYTICS_MAX_PARTICIPANTS)
        .optional(),
    /** What is counted: events (the default), or a Narcan encounter's administrations (confirmed 10/7). */
    measure: z.enum(ANALYTICS_MEASURES).default('events'),
})
    .superRefine((q, ctx) => {
    if (q.from > q.to) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'The range ends before it starts' });
    }
    const days = (Date.parse(`${q.to}T00:00:00Z`) - Date.parse(`${q.from}T00:00:00Z`)) / 86_400_000;
    if (q.grain === 'day' && days > ANALYTICS_MAX_DAILY_DAYS) {
        ctx.addIssue({
            code: 'custom',
            path: ['grain'],
            message: 'Daily grouping covers a year at most; group by week or month',
        });
    }
    if (days > ANALYTICS_MAX_RANGE_DAYS) {
        ctx.addIssue({
            code: 'custom',
            path: ['from'],
            message: 'A range can cover five years at most',
        });
    }
    if (q.measure !== 'events' && (q.subject !== 'events' || !isNarcanAdministeredOnly(q.kinds))) {
        ctx.addIssue({
            code: 'custom',
            path: ['measure'],
            message: 'Administrations are counted for Narcan-administered events only',
        });
    }
    if (q.segment) {
        if (segmentDepth(q.segment) > SEGMENT_MAX_DEPTH) {
            ctx.addIssue({
                code: 'custom',
                path: ['segment'],
                message: `Groups nest ${SEGMENT_MAX_DEPTH} deep at most`,
            });
        }
        if (segmentConditions(q.segment).length > SEGMENT_MAX_CONDITIONS) {
            ctx.addIssue({
                code: 'custom',
                path: ['segment'],
                message: `A segment holds ${SEGMENT_MAX_CONDITIONS} conditions at most`,
            });
        }
    }
    if (new Set(q.groupBy).size !== q.groupBy.length) {
        ctx.addIssue({ code: 'custom', path: ['groupBy'], message: 'Group by two different things' });
    }
    const cases = q.subject === 'cases';
    if (cases && !q.caseMeasure) {
        ctx.addIssue({ code: 'custom', path: ['caseMeasure'], message: 'Say which case count' });
    }
    if (!cases && (q.caseMeasure || q.caseStatus || q.closeReasons || q.episodeTypes)) {
        ctx.addIssue({
            code: 'custom',
            path: ['subject'],
            message: 'Case filters need subject: cases',
        });
    }
    if (cases && (q.substanceTypes || q.overdoseOutcomes)) {
        ctx.addIssue({
            code: 'custom',
            path: ['substanceTypes'],
            message: 'Cases have no substance or overdose outcome',
        });
    }
    if (cases && (q.kinds || q.matchStatus)) {
        ctx.addIssue({
            code: 'custom',
            path: ['kinds'],
            message: 'Cases have no kind or match status',
        });
    }
    for (const g of q.groupBy) {
        if ((cases && EVENT_ONLY_KEYS.includes(g)) || (!cases && CASE_ONLY_KEYS.includes(g))) {
            ctx.addIssue({ code: 'custom', path: ['groupBy'], message: `${g} does not apply here` });
        }
    }
});
/**
 * A count, or why it is not shown.
 *
 * SMALL is a count from 1 to ANALYTICS_SMALL_COUNT_MAX, shown as "1–10". COMPLEMENTARY is a larger count
 * withheld so a SMALL one cannot be worked out by subtracting from a total, shown as "Withheld".
 */
export const analyticsNumber = z.union([
    z.number().int().nonnegative(),
    z.object({ suppressed: z.enum(['SMALL', 'COMPLEMENTARY']) }),
]);
/** `unverifiedCount` only for the people subject: verified people are the count, unverified beside it. */
export const analyticsMeasure = z.object({
    count: analyticsNumber,
    unverifiedCount: analyticsNumber.optional(),
});
export const analyticsCell = analyticsMeasure.extend({ keys: z.array(z.string()) });
export const analyticsResult = z.object({
    query: analyticsQuery,
    /** One per non-empty group, in key order. Empty groups are absent: zero is not a segment. */
    cells: z.array(analyticsCell),
    /** With two group keys: margins[i] totals the cells by groupBy[i] alone. Empty otherwise. */
    margins: z.array(z.array(analyticsCell)),
    total: analyticsMeasure,
    suppression: z.object({
        /** Small counts in this result are withheld. */
        applied: z.boolean(),
        /** The result touches a sensitive dimension or a treatment provider's events. */
        sensitive: z.boolean(),
    }),
    asOf: z.string(),
});
// ─── Continuous variables: statistics and a histogram (OCC-77, OCC-81) ─────────────────────
/**
 * What a distribution can be of. `ageAtEvent`: each event's person's age on the event's day, in whole years,
 * from the date of birth, which only the server reads and never returns. `lengthOfStay`: each stay ended in
 * the range, in whole days from its opening.
 */
export const ANALYTICS_VARIABLES = ['ageAtEvent', 'lengthOfStay'];
export const ANALYTICS_VARIABLE_WORDS = {
    ageAtEvent: {
        label: 'Age at the event',
        unit: 'years',
        of: 'events with a person and a full date of birth',
    },
    lengthOfStay: { label: 'Length of stay', unit: 'days', of: 'stays ended in the range' },
};
/** Ages are top-coded here, as Safe Harbor requires: 90 and over is one value, counted as 90. */
export const AGE_TOP_CODE = 90;
/** The subject each variable belongs to. */
export const ANALYTICS_VARIABLE_SUBJECTS = {
    ageAtEvent: 'events',
    lengthOfStay: 'cases',
};
export const analyticsDistributionRequest = z
    .object({ query: analyticsQuery, variable: z.enum(ANALYTICS_VARIABLES) })
    .superRefine((r, ctx) => {
    if (r.query.subject !== ANALYTICS_VARIABLE_SUBJECTS[r.variable]) {
        ctx.addIssue({
            code: 'custom',
            path: ['variable'],
            message: `${ANALYTICS_VARIABLE_WORDS[r.variable].label} is of ${ANALYTICS_VARIABLE_SUBJECTS[r.variable]}`,
        });
    }
});
/**
 * A statistic, or why it is not given. SMALL_GROUP: the group is 1 to 10, where a mean says too much about
 * the few people in it. INDIVIDUAL: a minimum or maximum is one person's own value, given only to a reader
 * who may see it, and never in a file.
 */
export const analyticsStatistic = z.union([
    z.number(),
    z.object({ withheld: z.enum(['SMALL_GROUP', 'INDIVIDUAL']) }),
]);
export const analyticsDistribution = z.object({
    query: analyticsQuery,
    variable: z.enum(ANALYTICS_VARIABLES),
    unit: z.enum(['years', 'days']),
    /** How many values the statistics are of. */
    n: analyticsNumber,
    /** Rows in the selection with no value: no person, no full date of birth, a stay still open. */
    missing: analyticsNumber,
    /** Absent when there are no values at all. */
    stats: z
        .object({
        mean: analyticsStatistic,
        sd: analyticsStatistic,
        median: analyticsStatistic,
        min: analyticsStatistic,
        max: analyticsStatistic,
    })
        .optional(),
    /** Contiguous bins from the lowest non-empty one to the highest; the last may be open ("90+"). */
    bins: z.array(z.object({
        key: z.string(),
        label: z.string(),
        from: z.number(),
        to: z.number().optional(),
        count: analyticsNumber,
    })),
    /** Ages of 90 and over are among the values, counted as 90. */
    topCoded: z.boolean(),
    suppression: z.object({ applied: z.boolean(), sensitive: z.boolean() }),
    asOf: z.string(),
});
/** A statistic in words: "34.2", "Withheld (fewer than 11)", "Withheld (one person's value)". */
export function formatStatistic(value, digits = 1) {
    if (value === undefined)
        return '—';
    if (typeof value === 'number')
        return value.toLocaleString('en-US', { maximumFractionDigits: digits });
    return value.withheld === 'SMALL_GROUP'
        ? 'Withheld (fewer than 11)'
        : "Withheld (one person's value)";
}
// ─── Percentages and subgroup comparison (OCC-75, OCC-76, OCC-78) ──────────────────────────
/**
 * A percentage is given only of a base at least this large. Below it one event moves a share by more than five
 * points, and "100%" of three people reads as a finding. The same floor as change against the previous period.
 */
export const PERCENT_MIN_BASE = 20;
/**
 * `count` as a fraction of `base`, or undefined: when either is withheld, or the base is under PERCENT_MIN_BASE.
 *
 * Only shown numbers go in, so a percentage never says more than its count already does: a withheld count has
 * no percentage, rather than one that would give it back. An absent count is zero, as everywhere in a result.
 */
export function shareOf(count, base) {
    const shown = count ?? 0;
    if (typeof shown !== 'number' || typeof base !== 'number')
        return undefined;
    if (base < PERCENT_MIN_BASE)
        return undefined;
    return shown / base;
}
/** "42.3%", or "—" where there is no percentage to give. */
export function formatShare(share) {
    return share === undefined ? '—' : `${(share * 100).toFixed(1)}%`;
}
/** A difference of two shares in percentage points: "+5.2 pts", "−0.8 pts", "0.0 pts", or "—". */
export function formatPoints(difference) {
    if (difference === undefined)
        return '—';
    const points = Math.round(difference * 1000) / 10;
    if (points === 0)
        return '0.0 pts';
    return `${points > 0 ? '+' : '−'}${Math.abs(points).toFixed(1)} pts`;
}
/**
 * The base a breakdown row's percentage is of. With one key, the total. With two, a cell is a share of its
 * first key's row — of the men, how many were fentanyl — and a key's own totals are shares of the total.
 */
export function breakdownShare(result, cell, margin) {
    if (margin || cell.keys.length < 2)
        return shareOf(cell.count, result.total.count);
    const row = result.margins[0]?.find((m) => m.keys[0] === cell.keys[0]);
    return shareOf(cell.count, row?.count);
}
/**
 * Two subgroups side by side (OCC-75): the same query, grouped by what is compared across and by what tells the
 * subgroups apart, and two values of the second. `a` and `b` are codes of `by` (or the two sentinels).
 */
export const analyticsComparison = z
    .object({
    by: z.enum(ANALYTICS_GROUP_KEYS),
    a: z.string().trim().min(1).max(64),
    b: z.string().trim().min(1).max(64),
    across: z.enum(ANALYTICS_GROUP_KEYS),
})
    .superRefine((c, ctx) => {
    if (c.a === c.b) {
        ctx.addIssue({ code: 'custom', path: ['b'], message: 'Compare two different subgroups' });
    }
    if (c.by === c.across) {
        ctx.addIssue({
            code: 'custom',
            path: ['across'],
            message: 'Compare across something other than what tells the subgroups apart',
        });
    }
});
/** The query a comparison reads: the report's own, grouped by [across, by]. */
export function comparisonQuery(query, comparison) {
    return { ...query, groupBy: [comparison.across, comparison.by] };
}
/**
 * The comparison table, from a result of `comparisonQuery`. Each subgroup's percentages are of its own total,
 * so two subgroups of different sizes compare; a category either has a count of is a row, the other at zero.
 */
export function compareSubgroups(result, comparison) {
    const totalOf = (code) => result.margins[1]?.find((m) => m.keys[0] === code)?.count ?? 0;
    const a = totalOf(comparison.a);
    const b = totalOf(comparison.b);
    const countOf = (key, code) => result.cells.find((c) => c.keys[0] === key && c.keys[1] === code)?.count ?? 0;
    const keys = (result.margins[0] ?? [])
        .map((m) => m.keys[0])
        .filter((key) => result.cells.some((c) => c.keys[0] === key && (c.keys[1] === comparison.a || c.keys[1] === comparison.b)));
    const rows = keys.map((key) => {
        const ca = countOf(key, comparison.a);
        const cb = countOf(key, comparison.b);
        const sa = shareOf(ca, a);
        const sb = shareOf(cb, b);
        return {
            key,
            a: { count: ca, share: sa },
            b: { count: cb, share: sb },
            difference: sa !== undefined && sb !== undefined ? sa - sb : undefined,
        };
    });
    return { rows, a, b };
}
export const analyticsRecordsRequest = z.object({
    query: analyticsQuery,
    cursor: z.string().max(64).optional(),
    limit: z.number().int().min(1).max(500).default(50),
});
/** One row under a case count: a case, or one close of it for discharges. */
export const analyticsCaseRecord = z.object({
    id: z.string(),
    caseId: z.string(),
    caseCode: z.string(),
    facilityId: z.string(),
    facilityName: z.string().optional(),
    participantCode: z.string().optional(),
    /** Status at the range's end. */
    status: z.enum(['OPEN', 'CLOSED']),
    openedAt: z.string(),
    /** For a discharge row: when this close happened. Otherwise the case's current close, if any. */
    closedAt: z.string().optional(),
    closeReasonCode: z.string().optional(),
    episodeTypeCode: z.string().optional(),
});
export const analyticsRecordsResponse = z.object({
    /** Events, for `subject: events | people`. Empty for cases. */
    items: z.array(measureEntrySummary),
    /** Cases (or closes), for `subject: cases`. */
    cases: z.array(analyticsCaseRecord).optional(),
    /** Every event the query selects — what the events count is. */
    total: z.number().int().nonnegative(),
    /** The distinct people behind them — what the people count is. */
    people: z.object({ count: z.number().int(), unverifiedCount: z.number().int() }),
    nextCursor: z.string().optional(),
    asOf: z.string(),
});
/** `YYYY-MM-DD` plus `days`, on the calendar. */
export function addCalendarDays(date, days) {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
/** A local calendar day's period: `YYYY-MM` for a month; the Monday that starts it for a week. */
export function analyticsPeriodKey(date, grain) {
    if (grain === 'day')
        return date;
    if (grain === 'month')
        return date.slice(0, 7);
    const weekday = new Date(`${date}T00:00:00Z`).getUTCDay(); // 0 Sunday … 6 Saturday
    return addCalendarDays(date, -((weekday + 6) % 7));
}
/** Every period a range touches, in order — so a chart draws empty months as zero, not as gaps. */
export function analyticsPeriods(from, to, grain) {
    const keys = [];
    let key = analyticsPeriodKey(from, grain);
    const last = analyticsPeriodKey(to, grain);
    while (key <= last) {
        keys.push(key);
        key =
            grain === 'day'
                ? addCalendarDays(key, 1)
                : grain === 'week'
                    ? addCalendarDays(key, 7)
                    : (() => {
                        const [y, m] = key.split('-').map(Number);
                        return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
                    })();
    }
    return keys;
}
/**
 * The days one period covers, clipped to the query's range — what a drill-down on a period opens.
 *
 * Clipped because a range starting 15 October draws October as the 15th onward; opening all of October
 * would show a report bigger than the bar that was clicked.
 */
export function analyticsPeriodRange(key, grain, from, to) {
    let start;
    let end;
    if (grain === 'day') {
        start = key;
        end = key;
    }
    else if (grain === 'week') {
        start = key;
        end = addCalendarDays(key, 6);
    }
    else {
        start = `${key}-01`;
        const [y, m] = key.split('-').map(Number);
        end = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
    }
    return { from: start < from ? from : start, to: end > to ? to : end };
}
/** D-2: the last twelve full months plus this month so far. */
export function defaultAnalyticsRange(today) {
    const [y, m] = today.split('-').map(Number);
    const first = new Date(Date.UTC(y, m - 1 - 12, 1)).toISOString().slice(0, 10);
    return { from: first, to: today };
}
/**
 * The query a segment opens: the same query, narrowed to that segment, ungrouped by what it was grouped by.
 *
 * The one rule drill-down depends on: the report's total equals the segment's value, because the report runs
 * exactly this query through the same service.
 */
export function analyticsDrillDown(query, keys) {
    const next = { ...query, people: { ...query.people }, groupBy: [] };
    query.groupBy.forEach((group, i) => {
        const value = keys[i];
        if (group === 'period') {
            Object.assign(next, analyticsPeriodRange(value, query.grain, next.from, next.to));
        }
        else if (group === 'site') {
            next.sites = [value];
        }
        else if (group === 'kind') {
            next.kinds = [value];
        }
        else if (group === 'matchStatus') {
            next.matchStatus = [value];
        }
        else if (group === 'caseStatus') {
            next.caseStatus = [value];
        }
        else if (group === 'closeReason') {
            next.closeReasons = [value];
        }
        else if (group === 'episodeType') {
            next.episodeTypes = [value];
        }
        else if (group === 'substanceType') {
            next.substanceTypes = [value];
        }
        else if (group === 'overdoseOutcome') {
            next.overdoseOutcomes = [value];
        }
        else {
            next.people[group] = [value];
        }
    });
    return next;
}
/**
 * An export of a dashboard report (DASHBOARD-PLAN §7): its breakdown table, or the records behind it.
 *
 * The query is the report's own, so the file holds exactly what the screen showed — with small counts
 * always withheld in the table, whoever exports it (D-5).
 */
export const ANALYTICS_EXPORT_PARTS = ['table', 'records', 'comparison', 'distribution'];
export const analyticsExportRequest = z
    .object({
    query: analyticsQuery,
    format: exportFormat,
    part: z.enum(ANALYTICS_EXPORT_PARTS),
    /** For `part: comparison` only: the two subgroups and what they are compared across. */
    comparison: analyticsComparison.optional(),
    /** For `part: distribution` only: what the statistics and histogram are of. */
    variable: z.enum(ANALYTICS_VARIABLES).optional(),
})
    .superRefine((r, ctx) => {
    if ((r.part === 'comparison') !== (r.comparison !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['comparison'],
            message: 'A comparison export says which two subgroups, and nothing else does',
        });
    }
    if ((r.part === 'distribution') !== (r.variable !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['variable'],
            message: 'A distribution export says what it is of, and nothing else does',
        });
    }
    if (r.variable && r.query.subject !== ANALYTICS_VARIABLE_SUBJECTS[r.variable]) {
        ctx.addIssue({
            code: 'custom',
            path: ['variable'],
            message: `${r.variable} does not apply here`,
        });
    }
    if (r.comparison) {
        const cases = r.query.subject === 'cases';
        for (const key of [r.comparison.by, r.comparison.across]) {
            if ((cases && EVENT_ONLY_KEYS.includes(key)) || (!cases && CASE_ONLY_KEYS.includes(key))) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['comparison'],
                    message: `${key} does not apply here`,
                });
            }
        }
    }
});
// ─── Dashboard layouts (docs/DASHBOARD-PLAN.md §6) ──────────────────────────────────────────
/** Every widget a dashboard can hold. A layout naming a type no longer here renders it as unavailable. */
export const DASHBOARD_WIDGET_TYPES = [
    'events-in-range',
    'trend',
    'by-site',
    'breakdown',
    'people',
    'awaiting',
    'people-queues',
    'imports',
    'kind-trend',
    'overdose-trend',
    'site-trend',
    'demographics',
    'recent-weeks',
    'measure-cards',
    'overdose-summary',
    // Cases (CASE-DASHBOARD C4).
    'case-summary',
    'case-sites',
    'case-reasons',
    'case-demographics',
    // Provisional measures (COUNTY-DEFINITIONS-NEEDED).
    'provisional-metrics',
    // Events by the proposed substance and outcome lists.
    'event-details',
    // Overdoses recorded with fentanyl as the substance, and their trend (10/8).
    'fentanyl-overdoses',
    // Administration (feedback 10/15): the user, role and organisation tiles, for accounts that manage users.
    'admin-active-users',
    'admin-recent-users',
    'admin-roles',
    'admin-org',
    // Continuous variables (OCC-77, OCC-81): statistics and a histogram.
    'age-distribution',
    'stay-distribution',
];
export const DASHBOARD_WIDGET_WIDTHS = ['full', 'wide', 'half', 'third'];
/** The substance code a fentanyl-involved overdose carries, on the proposed list (SUBSTANCE_TYPE_DEFAULTS). */
export const FENTANYL_SUBSTANCE = 'FENTANYL';
/**
 * What a breakdown panel can break events down by: a person's dimension, or the event's substance (10/8). A
 * substance is the event's own, so a panel of kinds that record none shows them as not recorded.
 */
export const BREAKDOWN_DIMENSIONS = [...ANALYTICS_PERSON_DIMENSIONS, 'substanceType'];
/**
 * A widget's own date range (10/15): a preset re-read against today, or custom dates, and how it is grouped.
 * Absent, a widget follows the dashboard's filter bar — except "Last 13 weeks", whose own default is 13 weeks.
 * `dashboard` says "follow the filter bar" explicitly, for a widget whose own default is something else.
 */
export const WIDGET_RANGE_PRESETS = [
    'dashboard',
    '7d',
    '4w',
    '13w',
    '26w',
    '52w',
    '12m',
    'custom',
];
export const WIDGET_RANGE_WORDS = {
    dashboard: 'the dashboard’s range',
    '7d': 'last 7 days',
    '4w': 'last 4 weeks',
    '13w': 'last 13 weeks',
    '26w': 'last 26 weeks',
    '52w': 'last 52 weeks',
    '12m': 'last 12 months',
};
const rangeDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
export const widgetRange = z
    .object({
    preset: z.enum(WIDGET_RANGE_PRESETS),
    from: rangeDate.optional(),
    to: rangeDate.optional(),
    /** Absent or `auto`: by day up to a month, by week up to half a year, by month beyond. */
    grain: z.enum([...ANALYTICS_GRAINS, 'auto']).optional(),
})
    .strict()
    .superRefine((r, ctx) => {
    if ((r.preset === 'custom') !== (r.from !== undefined && r.to !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['from'],
            message: 'Custom dates need a start and an end, and only custom',
        });
    }
    if (r.from && r.to && r.from > r.to) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'The range ends before it starts' });
    }
});
/** A widget's own default range, where it has one: what it shows with nothing chosen. */
export const WIDGET_DEFAULT_RANGES = {
    'recent-weeks': '13w',
};
/** The dates a preset means today. Weeks are whole weeks, Monday first, ending with this one; months likewise. */
export function widgetRangeDates(preset, today) {
    const monday = analyticsPeriodKey(today, 'week');
    switch (preset) {
        case '7d':
            return { from: addCalendarDays(today, -6), to: today };
        case '4w':
            return { from: addCalendarDays(monday, -21), to: today };
        case '13w':
            return { from: addCalendarDays(monday, -84), to: today };
        case '26w':
            return { from: addCalendarDays(monday, -175), to: today };
        case '52w':
            return { from: addCalendarDays(monday, -357), to: today };
        case '12m': {
            const [y, m] = today.split('-').map(Number);
            return { from: new Date(Date.UTC(y, m - 12, 1)).toISOString().slice(0, 10), to: today };
        }
    }
}
/** How a range is grouped when nobody chose: by day up to a month, by week up to half a year, by month after. */
export function autoGrain(from, to) {
    const days = (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000 + 1;
    if (days <= 31)
        return 'day';
    if (days <= 183)
        return 'week';
    return 'month';
}
/** "Sep 1 – Oct 15", or "Sep 1, 2025 – Oct 15, 2026" across years: custom dates in a title. */
export function rangeInWords(from, to) {
    const part = (d, year) => {
        const [y, m, day] = d.split('-').map(Number);
        return `${SHORT_MONTHS[m - 1]} ${day}${year ? `, ${y}` : ''}`;
    };
    const years = from.slice(0, 4) !== to.slice(0, 4);
    return `${part(from, years)} – ${part(to, years)}`;
}
export const dashboardWidget = z.object({
    /** The instance, so two breakdowns of different dimensions are two widgets. */
    id: z.string().trim().min(1).max(64),
    type: z.enum(DASHBOARD_WIDGET_TYPES),
    width: z.enum(DASHBOARD_WIDGET_WIDTHS),
    config: z
        .object({
        /** A breakdown's dimension: a person's, or the event's substance. */
        dimension: z.enum(BREAKDOWN_DIMENSIONS).optional(),
        /** Narrows a widget to these kinds, within the filter bar's — "Overdoses by site" is by-site of two. */
        kinds: z.array(z.enum(MEASURE_KINDS)).min(1).max(MEASURE_KINDS.length).optional(),
        /** The widget's own range (10/15); absent, it follows the dashboard. */
        range: widgetRange.optional(),
        /** A count on one day ("Active cases as of …", 10/16); absent, today. */
        asOf: rangeDate.optional(),
    })
        .strict()
        .default({}),
});
/**
 * Filters a user keeps as their own starting point. A date range is a preset, not dates: "last 90 days"
 * saved in October still means the last 90 days in December. Custom dates are kept as dates.
 */
export const DASHBOARD_RANGE_PRESETS = ['default', 'year', '90', 'custom'];
export const dashboardDefaultFilters = z
    .object({
    range: z.enum(DASHBOARD_RANGE_PRESETS),
    from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    grain: z.enum(ANALYTICS_GRAINS),
    sites: z.array(z.string().max(64)).max(200),
    kinds: z.array(z.enum(MEASURE_KINDS)).max(MEASURE_KINDS.length),
    person: z
        .object({
        dimension: z.enum(ANALYTICS_PERSON_DIMENSIONS),
        codes: z.array(z.string().max(64)).max(200),
    })
        .optional(),
})
    .refine((f) => f.range !== 'custom' || (f.from !== undefined && f.to !== undefined), {
    message: 'A custom range needs both dates',
});
/**
 * Where a layout comes from (plan §6, D-6): a role's, an agency's, the organisation's standard, or the one in
 * code when the organisation has not set its own. A person's copy records which it was made from, and at
 * which version, so a later change to it can be offered rather than imposed.
 */
export const DASHBOARD_BASE_SCOPES = ['ROLE', 'AGENCY', 'ORG', 'STANDARD'];
export const dashboardLayoutBase = z.object({
    scope: z.enum(DASHBOARD_BASE_SCOPES),
    ownerId: z.string().trim().min(1).max(64),
    version: z.number().int().min(0),
});
/** A person's own layout, as saved. */
export const dashboardLayoutBody = z.object({
    widgets: z.array(dashboardWidget).max(40),
    filters: dashboardDefaultFilters.optional(),
    basedOn: dashboardLayoutBase,
});
// ─── Data-entry defaults (OCC-44) ───────────────────────────────────────────────────────────
/**
 * What a form starts with, set by the organisation in Admin (OCC-44). A default is a prefill and nothing more:
 * it is on the screen, changeable, and only what is submitted is saved. The server never fills a value nobody
 * entered, so an import or an API call is untouched by these.
 *
 * A date's default is relative — today, or so many days before or after — so it means the same thing every
 * day it is used. The rest are values from their own lists.
 */
export const ENTRY_DEFAULT_FORMS = ['measureEntry', 'case'];
export const ENTRY_DEFAULT_FIELDS = [
    {
        form: 'measureEntry',
        field: 'kind',
        label: 'Measure',
        kind: 'kind',
        gridColumn: 'measure.kind',
    },
    {
        form: 'measureEntry',
        field: 'occurredAt',
        label: 'Occurred at',
        kind: 'date',
        gridColumn: 'measure.occurredAt',
        help: 'The time of day, in the form, is the time the form was opened.',
    },
    { form: 'measureEntry', field: 'facilityId', label: 'Facility', kind: 'facility' },
    {
        form: 'measureEntry',
        field: 'substanceType',
        label: 'Substance (from the list)',
        kind: 'lookup',
        category: 'substanceType',
        gridColumn: 'measure.detail.substanceType',
    },
    {
        form: 'measureEntry',
        field: 'overdoseOutcome',
        label: 'Overdose outcome (from the list)',
        kind: 'lookup',
        category: 'overdoseOutcome',
        gridColumn: 'measure.detail.overdoseOutcome',
    },
    { form: 'case', field: 'facilityId', label: 'Facility', kind: 'facility' },
    { form: 'case', field: 'openedOn', label: 'Opened on', kind: 'date' },
    {
        form: 'case',
        field: 'referredOn',
        label: 'Referral date',
        kind: 'date',
        help: 'Most often left without a default: a referral date is the referral’s own.',
    },
    {
        form: 'case',
        field: 'episodeTypeCode',
        label: 'Episode type',
        kind: 'lookup',
        category: 'episodeType',
    },
    { form: 'case', field: 'phaseCode', label: 'Phase', kind: 'lookup', category: 'casePhase' },
];
/** One default: a value from its list, or a date as days from today (0 is today, -1 yesterday). */
export const entryDefault = z
    .object({
    form: z.enum(ENTRY_DEFAULT_FORMS),
    field: z.string().min(1).max(64),
    value: z.string().trim().min(1).max(64).optional(),
    days: z.number().int().min(-365).max(365).optional(),
})
    .strict()
    .superRefine((d, ctx) => {
    const def = ENTRY_DEFAULT_FIELDS.find((f) => f.form === d.form && f.field === d.field);
    if (!def) {
        ctx.addIssue({
            code: 'custom',
            path: ['field'],
            message: `${d.form}.${d.field} takes no default`,
        });
        return;
    }
    if (def.kind === 'date'
        ? d.days === undefined || d.value !== undefined
        : d.value === undefined || d.days !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: [def.kind === 'date' ? 'days' : 'value'],
            message: def.kind === 'date' ? 'A date default is a number of days from today' : 'Say which value',
        });
    }
    if (def.kind === 'kind' && d.value && !MEASURE_KINDS.includes(d.value)) {
        ctx.addIssue({ code: 'custom', path: ['value'], message: 'Not a kind of event' });
    }
});
export const entryDefaultsBody = z
    .object({ defaults: z.array(entryDefault).max(ENTRY_DEFAULT_FIELDS.length) })
    .strict()
    .refine((b) => new Set(b.defaults.map((d) => `${d.form}.${d.field}`)).size === b.defaults.length, { message: 'Each field once' });
export const entryDefaultsResponse = z.object({
    defaults: z.array(z.object({
        form: z.enum(ENTRY_DEFAULT_FORMS),
        field: z.string(),
        value: z.string().optional(),
        days: z.number().optional(),
    })),
    updatedAt: z.string().optional(),
});
/** "Today", "3 days ago", "In 7 days": a date default in words. */
export function entryDateWords(days) {
    if (days === 0)
        return 'Today';
    if (days === -1)
        return 'Yesterday';
    if (days === 1)
        return 'Tomorrow';
    return days < 0 ? `${-days} days ago` : `In ${days} days`;
}
// ─── Saved reports (OCC-62, OCC-70) ─────────────────────────────────────────────────────────
/**
 * A report somebody saved to run again: its query, and how its dates are read. A preset is re-read against
 * the day it is run ("the last 90 days" in December is not October's); `custom` keeps the dates it was saved
 * with. Private to its owner, or shared with roles the owner holds. Whoever runs one sees it through their own
 * permissions and small-count suppression: sharing a report never shares access.
 */
export const savedReportBody = z
    .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).optional(),
    query: analyticsQuery,
    range: z.enum(DASHBOARD_RANGE_PRESETS).default('custom'),
    /** Empty: private. Each must be a role the owner holds. */
    sharedWithRoleIds: z
        .array(z.string().regex(/^[a-f0-9]{24}$/))
        .max(20)
        .default([]),
})
    .strict();
export const savedReport = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().optional(),
    query: analyticsQuery,
    range: z.enum(DASHBOARD_RANGE_PRESETS),
    sharedWith: z.array(z.object({ id: z.string(), name: z.string() })),
    ownerName: z.string(),
    /** The reader's own: only they may change or archive it. */
    mine: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const savedReportListResponse = z.object({
    items: z.array(savedReport),
    /** The roles the reader holds: what they may share with. */
    roles: z.array(z.object({ id: z.string(), name: z.string() })),
});
/** A role's, an agency's or the organisation's layout, as a manager saves it (D-7). */
export const groupLayoutBody = z.object({
    widgets: z.array(dashboardWidget).max(40),
    filters: dashboardDefaultFilters.optional(),
});
export const GROUP_LAYOUT_SCOPES = ['ROLE', 'AGENCY', 'ORG'];
/**
 * The standard dashboard: what everybody sees until they make it their own (plan §6).
 *
 * In code, versioned. A change here bumps `version`; a personal copy made from an older version is not
 * rewritten — its owner is shown what was added and removed and decides.
 */
export const STANDARD_DASHBOARD = {
    // Not bumped for std-breakdown's ZIP → full ZIP (10/7): a version adds or removes widgets, and a personal copy
    // keeps its own settings; one still on the prefix keeps working, as that dimension is retired, not removed.
    // 7 (10/8): fentanyl-involved overdoses, and Narcan by substance and outcome.
    // 8 (10/15): the administration tiles, as widgets (shown only to accounts that manage users).
    // 9 (10/15): age at the event and length of stay, as statistics and histograms.
    version: 9,
    introduced: {
        'std-events': 1,
        'std-trend': 1,
        'std-people': 1,
        'std-awaiting': 1,
        'std-sites': 1,
        'std-breakdown': 1,
        'std-people-queues': 1,
        'std-imports': 1,
        'std-overdose-trend': 2,
        'std-kind-trend': 2,
        'std-recent-weeks': 2,
        'std-site-trend': 2,
        'std-demographics': 2,
        'std-overdoses': 3,
        'std-overdose-sites': 3,
        'std-overdose-weeks': 3,
        'std-measures': 3,
        'std-cases': 4,
        'std-case-sites': 4,
        'std-case-reasons': 4,
        'std-case-demographics': 4,
        'std-provisional': 5,
        'std-overdose-details': 6,
        'std-fentanyl': 7,
        'std-narcan-details': 7,
        'std-admin-users': 8,
        'std-admin-recent': 8,
        'std-admin-roles': 8,
        'std-admin-org': 8,
        'std-age-distribution': 9,
        'std-stay-distribution': 9,
    },
    widgets: [
        { id: 'std-overdoses', type: 'overdose-summary', width: 'full', config: {} },
        { id: 'std-fentanyl', type: 'fentanyl-overdoses', width: 'full', config: {} },
        {
            id: 'std-overdose-sites',
            type: 'by-site',
            width: 'half',
            config: { kinds: ['FATAL_OVERDOSE', 'NONFATAL_OVERDOSE'] },
        },
        {
            id: 'std-overdose-weeks',
            type: 'recent-weeks',
            width: 'half',
            config: { kinds: ['FATAL_OVERDOSE', 'NONFATAL_OVERDOSE'] },
        },
        {
            id: 'std-overdose-details',
            type: 'event-details',
            width: 'full',
            config: { kinds: ['FATAL_OVERDOSE', 'NONFATAL_OVERDOSE'] },
        },
        {
            id: 'std-narcan-details',
            type: 'event-details',
            width: 'full',
            config: { kinds: ['NARCAN_ADMINISTERED'] },
        },
        { id: 'std-measures', type: 'measure-cards', width: 'full', config: {} },
        { id: 'std-provisional', type: 'provisional-metrics', width: 'full', config: {} },
        { id: 'std-cases', type: 'case-summary', width: 'full', config: {} },
        { id: 'std-case-sites', type: 'case-sites', width: 'half', config: {} },
        { id: 'std-case-reasons', type: 'case-reasons', width: 'half', config: {} },
        { id: 'std-case-demographics', type: 'case-demographics', width: 'full', config: {} },
        { id: 'std-people', type: 'people', width: 'third', config: {} },
        { id: 'std-awaiting', type: 'awaiting', width: 'third', config: {} },
        // In the row People and Awaiting leave a third of; for somebody who does not manage users, that third
        // stays empty, as it always has.
        { id: 'std-admin-users', type: 'admin-active-users', width: 'third', config: {} },
        { id: 'std-kind-trend', type: 'kind-trend', width: 'half', config: {} },
        { id: 'std-recent-weeks', type: 'recent-weeks', width: 'half', config: {} },
        { id: 'std-sites', type: 'by-site', width: 'half', config: {} },
        { id: 'std-site-trend', type: 'site-trend', width: 'half', config: {} },
        { id: 'std-demographics', type: 'demographics', width: 'full', config: {} },
        { id: 'std-age-distribution', type: 'age-distribution', width: 'half', config: {} },
        { id: 'std-stay-distribution', type: 'stay-distribution', width: 'half', config: {} },
        // ZIP, not sex: the demographics panel already shows sex, age band, race and ethnicity (C12). The full ZIP,
        // not the prefix every Collier ZIP shares (tester feedback 10/7); small counts withheld.
        { id: 'std-breakdown', type: 'breakdown', width: 'half', config: { dimension: 'zip' } },
        { id: 'std-people-queues', type: 'people-queues', width: 'half', config: {} },
        { id: 'std-imports', type: 'imports', width: 'half', config: {} },
        { id: 'std-admin-recent', type: 'admin-recent-users', width: 'half', config: {} },
        { id: 'std-admin-roles', type: 'admin-roles', width: 'half', config: {} },
        { id: 'std-admin-org', type: 'admin-org', width: 'half', config: {} },
    ],
};
export const dashboardLayoutResponse = z.object({
    /** PERSONAL once the reader saves; otherwise the layer they fall under (D-6). */
    source: z.enum(['PERSONAL', ...DASHBOARD_BASE_SCOPES]),
    widgets: z.array(dashboardWidget),
    filters: dashboardDefaultFilters.optional(),
    /** What the reader falls back to — and what "Reset" returns them to. */
    base: dashboardLayoutBase.extend({ name: z.string() }),
    /** Other groups at the winning level, which lost on name (D-6) — said, so nobody wonders. */
    alsoMatched: z.array(z.string()),
    /** For a personal layout: the base it was made from, at the version it was made from. */
    basedOn: dashboardLayoutBase.optional(),
    /** When the base changed since: what is new in it, and what is gone from it. Never applied unasked. */
    baseChanged: z
        .object({
        added: z.array(dashboardWidget),
        removed: z.array(z.enum(DASHBOARD_WIDGET_TYPES)),
    })
        .optional(),
    updatedAt: z.string().optional(),
});
/** One group a manager can set a dashboard for, and whether it has one. */
export const groupLayoutSummary = z.object({
    scope: z.enum(GROUP_LAYOUT_SCOPES),
    ownerId: z.string(),
    name: z.string(),
    custom: z.boolean(),
    version: z.number().int(),
    widgetCount: z.number().int(),
    updatedAt: z.string().optional(),
});
export const groupLayoutListResponse = z.object({ items: z.array(groupLayoutSummary) });
export const groupLayoutDetail = z.object({
    scope: z.enum(GROUP_LAYOUT_SCOPES),
    ownerId: z.string(),
    name: z.string(),
    /** False while the group has none of its own — then `widgets` is what its members see instead. */
    custom: z.boolean(),
    version: z.number().int(),
    widgets: z.array(dashboardWidget),
    filters: dashboardDefaultFilters.optional(),
});
// ─── Chart axes (dashboard feedback B6) ─────────────────────────────────────────────────────
/**
 * Every how many slots an axis label can go without touching the next: the label's width and a gap over the
 * width of one slot. The one rule the screen and the PDF both follow, so neither overlaps at any width.
 */
export function axisLabelStep(count, slotWidth, labelWidth, gap = 6) {
    if (count <= 1 || slotWidth <= 0)
        return 1;
    return Math.max(1, Math.ceil((labelWidth + gap) / slotWidth));
}
const SHORT_MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];
/**
 * A period's label in two parts: a short one under its bar ("Oct", or "Sep 28" for a week) and the year,
 * shown once beneath the run of periods it covers — not "Oct 2025" twelve times over.
 */
export function periodAxisLabel(key, grain) {
    const [y, m, d] = key.split('-');
    const month = SHORT_MONTHS[Number(m) - 1] ?? m ?? '';
    // A day's label is its date, and its group the month: "15" beneath, "Oct 2026" once under the run.
    if (grain === 'day')
        return { short: String(Number(d)), group: `${month} ${y ?? ''}` };
    return { short: grain === 'month' ? month : `${month} ${Number(d)}`, group: y ?? '' };
}
/** Runs of consecutive equal groups: where each year's label goes, and how far it reaches. */
export function axisGroups(groups) {
    const out = [];
    groups.forEach((g, i) => {
        const last = out.at(-1);
        if (last && last.text === g)
            last.end = i;
        else
            out.push({ text: g, start: i, end: i });
    });
    return out;
}
/** Not recorded, not yet attributed, or recorded as unknown: drawn muted and listed after real values (C10). */
export function isNotAValue(code) {
    return (code === ANALYTICS_NOT_RECORDED ||
        code === ANALYTICS_NOT_ATTRIBUTED ||
        code.toUpperCase() === 'UNKNOWN');
}
/**
 * Where an axis's labels go (B6): every `step`th slot, centred on it, pulled inside the chart where it would
 * cross an edge — and left out where that would bring it within `gap` of the label before. So no label is
 * clipped and none touches another, at any width; the screen and the PDF both place labels with this.
 */
export function placeAxisLabels(centers, texts, widthOf, bounds, step, gap = 4) {
    const out = [];
    let lastRight = -Infinity;
    for (let i = 0; i < texts.length; i += Math.max(1, step)) {
        const width = widthOf(texts[i]);
        if (width > bounds.max - bounds.min)
            continue;
        const x = Math.min(Math.max(centers[i] - width / 2, bounds.min), bounds.max - width);
        if (x < lastRight + gap)
            continue;
        out.push({ index: i, x, width });
        lastRight = x + width;
    }
    return out;
}
/**
 * The case vocabularies' starting values (CASE-DASHBOARD CD-5), provisional until the county confirms them.
 * Seeded into each org's `caseCloseReason` and `episodeType` lists; an org edits its own from there.
 */
export const CASE_CLOSE_REASON_DEFAULTS = [
    { code: 'COMPLETED', label: 'Completed the programme' },
    { code: 'TRANSFERRED', label: 'Transferred to another provider' },
    { code: 'DECLINED', label: 'Declined further services' },
    { code: 'LOST_CONTACT', label: 'Lost contact' },
    { code: 'MOVED_AWAY', label: 'Moved out of the area' },
    { code: 'INCARCERATED', label: 'Incarcerated' },
    { code: 'DECEASED', label: 'Deceased' },
    { code: 'ADMINISTRATIVE', label: 'Closed administratively' },
    { code: 'OTHER', label: 'Other' },
];
/**
 * Case phases: a proposed list (COUNTY-DEFINITIONS-NEEDED), from the original application's, for the county to
 * confirm. Each organisation edits its own in Admin → Lists. A phase is where an open case stands now; how
 * it got there is in the audit trail.
 */
export const CASE_PHASE_DEFAULTS = [
    { code: 'INTAKE', label: 'Intake' },
    { code: 'ASSESSMENT', label: 'Assessment' },
    { code: 'ACTIVE_TREATMENT', label: 'Active treatment' },
    { code: 'TRANSITION', label: 'Transition' },
    { code: 'DISCHARGE_PLANNING', label: 'Discharge planning' },
    { code: 'AFTERCARE', label: 'Aftercare' },
];
/**
 * Proposed lists for two event fields the county has not yet coded (COUNTY-DEFINITIONS-NEEDED): the substance
 * involved, and how an overdose ended. Seeded per organisation, provisional, editable in Admin → Lists.
 */
export const SUBSTANCE_TYPE_DEFAULTS = [
    { code: 'FENTANYL', label: 'Fentanyl or an analogue' },
    { code: 'HEROIN', label: 'Heroin' },
    { code: 'PRESCRIPTION_OPIOID', label: 'Prescription opioid' },
    { code: 'METHADONE', label: 'Methadone' },
    { code: 'BUPRENORPHINE', label: 'Buprenorphine' },
    { code: 'COCAINE', label: 'Cocaine' },
    { code: 'METHAMPHETAMINE', label: 'Methamphetamine' },
    { code: 'XYLAZINE', label: 'Xylazine' },
    { code: 'POLYSUBSTANCE', label: 'More than one substance' },
    { code: 'OTHER', label: 'Other' },
    { code: 'UNKNOWN', label: 'Unknown' },
];
export const OVERDOSE_OUTCOME_DEFAULTS = [
    { code: 'REVIVED_ON_SCENE', label: 'Revived on scene' },
    { code: 'TRANSPORTED', label: 'Transported to hospital' },
    { code: 'REFUSED_TRANSPORT', label: 'Refused transport' },
    { code: 'ADMITTED', label: 'Admitted to hospital' },
    { code: 'DECEASED', label: 'Deceased' },
    { code: 'OTHER', label: 'Other' },
    { code: 'UNKNOWN', label: 'Unknown' },
];
export const EPISODE_TYPE_DEFAULTS = [
    { code: 'DRUG_COURT_DIVERSION', label: 'Drug court diversion' },
    { code: 'RESIDENTIAL_TREATMENT', label: 'Residential treatment' },
    { code: 'OUTPATIENT_TREATMENT', label: 'Outpatient treatment' },
    { code: 'MAT', label: 'Medication-assisted treatment' },
    { code: 'DETOX', label: 'Withdrawal management (detox)' },
    { code: 'RECOVERY_SUPPORT', label: 'Recovery support / peer services' },
    { code: 'CASE_MANAGEMENT', label: 'Case management' },
    { code: 'OTHER', label: 'Other' },
];
// ─── Provisional definitions (docs/COUNTY-DEFINITIONS-NEEDED.md — PROPOSED DEFINITIONS) ─────────
/**
 * Every definition the system proposes and the county may adjust. Each is a settings object per
 * organisation — time windows, thresholds, which events or values count — edited in Admin → Definitions,
 * audited, and described in plain words wherever it is shown, labelled provisional until the county signs
 * it off. County feedback is a settings change, not code.
 *
 * Facility scoping and exact-count exports are deliberately not here: they are access decisions, kept at
 * their safe settings for county sign-off before go-live.
 */
export const DEFINITION_KEYS = [
    'fatalShare',
    'recidivism',
    'narcanPerOverdose',
    'narcanDoses',
    'ratePerPopulation',
    'lengthOfStay',
    'completion',
    'retention',
    'referralToIntake',
    'completeness',
    'improvement',
    'followUp',
];
const kindList = z.array(z.enum(MEASURE_KINDS)).min(1).max(MEASURE_KINDS.length);
/** A value's code in one of the organisation's lists. Whether it is in the list is the list's business. */
const code = z.string().trim().min(1).max(64);
export const definitionSchemas = {
    fatalShare: z
        .object({
        /** The share is shown only from this many overdoses up. */
        minOverdoses: z.number().int().min(1).max(10_000),
    })
        .strict(),
    recidivism: z
        .object({
        /** The events that count, both as the one in the period and as the earlier one. */
        kinds: kindList,
        /** How far back an earlier event makes the later one a repeat. */
        windowDays: z.number().int().min(1).max(1_095),
        /** Whether people not yet verified are counted, or only verified people. */
        people: z.enum(['all', 'verified']),
        /** Shown only from this many people up. */
        minPeople: z.number().int().min(1).max(10_000),
    })
        .strict(),
    narcanPerOverdose: z
        .object({
        numeratorKinds: kindList,
        denominatorKinds: kindList,
        minDenominator: z.number().int().min(1).max(10_000),
    })
        .strict(),
    narcanDoses: z
        .object({
        kinds: kindList,
        /** Doses assumed for an event with no dose count; 0 leaves such events out of the sum. */
        missingDoses: z.number().int().min(0).max(100),
    })
        .strict(),
    ratePerPopulation: z
        .object({
        kinds: kindList,
        per: z.union([z.literal(1_000), z.literal(10_000), z.literal(100_000)]),
        /** Where the figures come from, in words: shown beside every rate. */
        source: z.string().trim().min(1).max(200),
        /** The county's population by year. A year without one has no rate. */
        populations: z
            .array(z
            .object({
            year: z.number().int().min(2000).max(2100),
            population: z.number().int().min(1).max(100_000_000),
        })
            .strict())
            .max(100)
            .refine((rows) => new Set(rows.map((r) => r.year)).size === rows.length, {
            message: 'Each year once',
        }),
    })
        .strict(),
    lengthOfStay: z
        .object({
        statistic: z.enum(['median', 'mean']),
        /** Whether a reopened case starts a new stay, or its stay runs from the first opening. */
        reopening: z.enum(['newStay', 'sameStay']),
        /** Only stays closed for these reasons; none listed means every close. */
        closeReasons: z.array(code).max(50),
        minStays: z.number().int().min(1).max(10_000),
    })
        .strict(),
    completion: z
        .object({
        completedReasons: z.array(code).min(1).max(50),
        /** Discharges for these reasons are neither completions nor failures: left out of the count. */
        excludedReasons: z.array(code).max(50),
        minDischarges: z.number().int().min(1).max(10_000),
    })
        .strict()
        .refine((v) => !v.completedReasons.some((r) => v.excludedReasons.includes(r)), {
        message: 'A reason cannot both complete and be left out',
    }),
    retention: z
        .object({
        /** Retained: still open this many days after intake. */
        days: z.number().int().min(1).max(1_095),
        minIntakes: z.number().int().min(1).max(10_000),
    })
        .strict(),
    referralToIntake: z
        .object({
        statistic: z.enum(['median', 'mean']),
        /** Shown only when at least this share of the period's intakes have a referral date. */
        minCoveragePercent: z.number().int().min(0).max(100),
        minCases: z.number().int().min(1).max(10_000),
    })
        .strict(),
    completeness: z
        .object({
        /** The coded event field whose completeness is measured. */
        field: z.string().refine((f) => CODED_MEASURE_DETAIL_FIELDS.includes(f), {
            message: 'Not a coded event field',
        }),
        kinds: kindList,
        /** Whether a recorded "Unknown" counts as answered. */
        unknownAnswered: z.enum(['no', 'yes']),
        minEvents: z.number().int().min(1).max(10_000),
    })
        .strict(),
    improvement: z
        .object({
        /** The `housingStatus` values, worst first. A value not listed (Unknown) is never compared. */
        order: z
            .array(code)
            .min(2)
            .max(50)
            .refine((o) => new Set(o).size === o.length, { message: 'Each value once' }),
        minCases: z.number().int().min(1).max(10_000),
    })
        .strict(),
    followUp: z
        .object({
        /** The events a follow-up is looked for after. */
        triggerKinds: kindList,
        /** How many days after one a contact still counts as its follow-up. */
        windowDays: z.number().int().min(1).max(365),
        /** The person's later events that count as a follow-up. */
        followUpKinds: kindList,
        /** Whether case activity — a case opened, closed or reopened — counts as a follow-up. */
        caseActivity: z.enum(['yes', 'no']),
        people: z.enum(['all', 'verified']),
        minOverdoses: z.number().int().min(1).max(10_000),
    })
        .strict(),
};
export const DEFINITION_DEFAULTS = {
    fatalShare: { minOverdoses: 20 },
    recidivism: {
        kinds: ['FATAL_OVERDOSE', 'NONFATAL_OVERDOSE'],
        windowDays: 365,
        people: 'all',
        minPeople: 20,
    },
    narcanPerOverdose: {
        numeratorKinds: ['NARCAN_ADMINISTERED'],
        denominatorKinds: ['FATAL_OVERDOSE', 'NONFATAL_OVERDOSE'],
        minDenominator: 20,
    },
    narcanDoses: { kinds: ['NARCAN_DISTRIBUTED'], missingDoses: 0 },
    // No figures: we do not invent a population. Until the county enters its own, rates say so.
    ratePerPopulation: {
        kinds: ['FATAL_OVERDOSE', 'NONFATAL_OVERDOSE'],
        per: 100_000,
        source: 'BEBR county population estimate',
        populations: [],
    },
    lengthOfStay: { statistic: 'median', reopening: 'newStay', closeReasons: [], minStays: 10 },
    completion: {
        completedReasons: ['COMPLETED'],
        excludedReasons: ['TRANSFERRED', 'DECEASED'],
        minDischarges: 10,
    },
    retention: { days: 90, minIntakes: 10 },
    referralToIntake: { statistic: 'median', minCoveragePercent: 50, minCases: 10 },
    improvement: { order: ['UNHOUSED', 'HOUSED'], minCases: 10 },
    completeness: {
        field: 'substanceType',
        kinds: ['FATAL_OVERDOSE', 'NONFATAL_OVERDOSE'],
        unknownAnswered: 'no',
        minEvents: 20,
    },
    // Decided 10/15, provisional: any recorded contact, event or case activity within 30 days of a non-fatal
    // overdose. Every kind but a fatal overdose is a contact: a death is not a follow-up.
    followUp: {
        triggerKinds: ['NONFATAL_OVERDOSE'],
        windowDays: 30,
        followUpKinds: MEASURE_KINDS.filter((k) => k !== 'FATAL_OVERDOSE'),
        caseActivity: 'yes',
        people: 'all',
        minOverdoses: 20,
    },
};
export const DEFINITION_TITLES = {
    fatalShare: 'Fatal share of overdoses',
    recidivism: 'Recidivism (repeat overdoses)',
    // Encounters, not administrations: it counts Narcan-administered EVENTS (confirmed vocabulary, 10/7).
    narcanPerOverdose: 'Narcan encounters per overdose',
    narcanDoses: 'Narcan units distributed (as reported)',
    ratePerPopulation: 'Rate per population',
    lengthOfStay: 'Length of stay',
    completion: 'Completion rate',
    retention: 'Retention',
    referralToIntake: 'Referral to intake',
    completeness: 'Data completeness',
    improvement: 'Improvement rate (housing)',
    followUp: 'Follow-up after a non-fatal overdose',
};
const CASE_FIELD_OPTIONS = {
    statistic: [
        { value: 'median', label: 'Median' },
        { value: 'mean', label: 'Mean' },
    ],
    reopening: [
        { value: 'newStay', label: 'A reopening starts a new stay' },
        { value: 'sameStay', label: 'A reopened case is one stay from its first opening' },
    ],
};
export const DEFINITION_FIELDS = {
    followUp: [
        { name: 'triggerKinds', label: 'Events a follow-up is looked for after', kind: 'kinds' },
        {
            name: 'windowDays',
            label: 'Days after, within which a contact is a follow-up',
            kind: 'int',
            min: 1,
            max: 365,
        },
        { name: 'followUpKinds', label: 'Later events that count as a follow-up', kind: 'kinds' },
        {
            name: 'caseActivity',
            label: 'Case activity',
            kind: 'choice',
            options: [
                { value: 'yes', label: 'counts as a follow-up (a case opened, closed or reopened)' },
                { value: 'no', label: 'does not count' },
            ],
        },
        {
            name: 'people',
            label: 'People counted',
            kind: 'choice',
            options: [
                { value: 'all', label: 'Verified and not yet verified' },
                { value: 'verified', label: 'Verified only' },
            ],
            help: 'A follow-up recorded on a duplicate of the person is missed, so the share is an undercount until duplicates are merged.',
        },
        {
            name: 'minOverdoses',
            label: 'Show from this many overdoses',
            kind: 'int',
            min: 1,
            max: 10_000,
        },
    ],
    fatalShare: [
        {
            name: 'minOverdoses',
            label: 'Show the share from this many overdoses',
            kind: 'int',
            min: 1,
            max: 10_000,
            help: 'Below it, one death moves the share by more than a few points — read as a trend, it is not one.',
        },
    ],
    recidivism: [
        { name: 'kinds', label: 'Events that count', kind: 'kinds' },
        {
            name: 'windowDays',
            label: 'Window, in days, for an earlier event',
            kind: 'int',
            min: 1,
            max: 1_095,
        },
        {
            name: 'people',
            label: 'People counted',
            kind: 'choice',
            options: [
                { value: 'all', label: 'Verified and not yet verified' },
                { value: 'verified', label: 'Verified only' },
            ],
            help: 'A person not yet verified may be a duplicate of another, so their repeats can be missed either way. The basis is shown beside the number.',
        },
        { name: 'minPeople', label: 'Show from this many people', kind: 'int', min: 1, max: 10_000 },
    ],
    narcanPerOverdose: [
        { name: 'numeratorKinds', label: 'Narcan events counted', kind: 'kinds' },
        { name: 'denominatorKinds', label: 'Per these events', kind: 'kinds' },
        {
            name: 'minDenominator',
            label: 'Show from this many of them',
            kind: 'int',
            min: 1,
            max: 10_000,
        },
    ],
    narcanDoses: [
        { name: 'kinds', label: 'Events whose doses are summed', kind: 'kinds' },
        {
            name: 'missingDoses',
            label: 'Units for an encounter with no number reported',
            kind: 'int',
            min: 0,
            max: 100,
            help: '0 leaves such events out of the sum; how many there were is shown beside it.',
        },
    ],
    ratePerPopulation: [
        { name: 'kinds', label: 'Events counted', kind: 'kinds' },
        {
            name: 'per',
            label: 'Per',
            kind: 'choice',
            options: [
                { value: '100000', label: '100,000 residents' },
                { value: '10000', label: '10,000 residents' },
                { value: '1000', label: '1,000 residents' },
            ],
        },
        { name: 'source', label: 'Source of the population figures', kind: 'text' },
        {
            name: 'populations',
            label: 'County population by year',
            kind: 'yearTable',
            help: 'A year with no figure has no rate. We have not entered any: the figures are the county’s to give.',
        },
    ],
    lengthOfStay: [
        { name: 'statistic', label: 'Average', kind: 'choice', options: CASE_FIELD_OPTIONS.statistic },
        {
            name: 'reopening',
            label: 'Reopened cases',
            kind: 'choice',
            options: CASE_FIELD_OPTIONS.reopening,
        },
        {
            name: 'closeReasons',
            label: 'Only stays closed for these reasons',
            kind: 'codes',
            category: 'caseCloseReason',
            help: 'None ticked: every close counts.',
        },
        { name: 'minStays', label: 'Show from this many stays', kind: 'int', min: 1, max: 10_000 },
    ],
    completion: [
        {
            name: 'completedReasons',
            label: 'Close reasons that count as completion',
            kind: 'codes',
            category: 'caseCloseReason',
        },
        {
            name: 'excludedReasons',
            label: 'Close reasons left out of the count',
            kind: 'codes',
            category: 'caseCloseReason',
            help: 'Neither a completion nor a failure — a transfer, a death.',
        },
        {
            name: 'minDischarges',
            label: 'Show from this many discharges',
            kind: 'int',
            min: 1,
            max: 10_000,
        },
    ],
    retention: [
        {
            name: 'days',
            label: 'Still open this many days after intake',
            kind: 'int',
            min: 1,
            max: 1_095,
        },
        { name: 'minIntakes', label: 'Show from this many intakes', kind: 'int', min: 1, max: 10_000 },
    ],
    referralToIntake: [
        { name: 'statistic', label: 'Average', kind: 'choice', options: CASE_FIELD_OPTIONS.statistic },
        {
            name: 'minCoveragePercent',
            label: 'Show only when this percentage of intakes have a referral date',
            kind: 'int',
            min: 0,
            max: 100,
            help: 'An average of the few cases that happen to carry a date is not the agency’s average.',
        },
        {
            name: 'minCases',
            label: 'Show from this many dated referrals',
            kind: 'int',
            min: 1,
            max: 10_000,
        },
    ],
    improvement: [
        {
            name: 'order',
            label: 'Housing status, worst first',
            kind: 'orderedCodes',
            category: 'housingStatus',
            help: 'A case improved when its status at closing is later in this order than at opening. A value left out — Unknown — is never compared.',
        },
        { name: 'minCases', label: 'Show from this many cases', kind: 'int', min: 1, max: 10_000 },
    ],
    completeness: [
        {
            name: 'field',
            label: 'Field',
            kind: 'choice',
            options: MEASURE_DETAIL_FIELD_DEFS.filter((f) => f.coded).map((f) => ({
                value: f.key,
                label: f.label,
            })),
        },
        { name: 'kinds', label: 'Events', kind: 'kinds' },
        {
            name: 'unknownAnswered',
            label: 'A recorded “Unknown”',
            kind: 'choice',
            options: [
                { value: 'no', label: 'is not an answer' },
                { value: 'yes', label: 'counts as answered' },
            ],
            help: '“Unknown” is a value somebody chose; a blank is a value nobody entered. Either way, nobody knows.',
        },
        { name: 'minEvents', label: 'Show from this many events', kind: 'int', min: 1, max: 10_000 },
    ],
};
function reasonsInWords(codes) {
    const words = codes.map((c) => CASE_CLOSE_REASON_DEFAULTS.find((d) => d.code === c)?.label.toLowerCase() ?? c);
    if (words.length < 2)
        return words.join('');
    return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
}
function kindsInWords(kinds) {
    // The bare kind name: a rule says "events" or "encounters" itself, after it.
    const words = kinds.map((k) => MEASURE_KIND_LABELS[k].replace(' (encounters)', '').toLowerCase());
    if (words.length < 2)
        return words.join('');
    return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}
/**
 * The kinds that count as a follow-up, in words: "any event except a fatal overdose" when most kinds count,
 * since nine kinds in a row is a list nobody reads.
 */
function followUpKindsInWords(kinds) {
    const left = MEASURE_KINDS.filter((k) => !kinds.includes(k));
    if (left.length === 0)
        return 'any event';
    if (kinds.length > MEASURE_KINDS.length / 2) {
        const words = left.map((k) => MEASURE_KIND_LABELS[k].replace(' (encounters)', '').toLowerCase());
        return `any event except ${words.length < 2 ? `a ${words[0]}` : `${words.slice(0, -1).join(', ')} or ${words.at(-1)}`}`;
    }
    return `a ${kindsInWords(kinds)} event`;
}
/** A definition's rule in plain words, as it is set — the sentence shown beside every number it makes. */
export function describeDefinition(key, settings) {
    switch (key) {
        case 'fatalShare': {
            const s = settings;
            return ('Fatal overdoses as a share of all overdoses (fatal and non-fatal) in the period, counting events. ' +
                `Shown only when there are at least ${s.minOverdoses} overdoses in the period.`);
        }
        case 'recidivism': {
            const s = settings;
            return (`Of the people with a ${kindsInWords(s.kinds)} event in the period, the share who also had one in ` +
                `the ${s.windowDays} days before it. ` +
                (s.people === 'all'
                    ? 'People not yet verified are counted. '
                    : 'Only verified people are counted. ') +
                `Events with no person are left out. Shown only from ${s.minPeople} people.`);
        }
        case 'narcanPerOverdose': {
            const s = settings;
            return (`${kindsInWords(s.numeratorKinds)} ${isNarcanAdministeredOnly(s.numeratorKinds) ? 'encounters' : 'events'} ` +
                `per ${kindsInWords(s.denominatorKinds)} event in the period, counting events, not administrations. ` +
                `Shown only from ${s.minDenominator} ${kindsInWords(s.denominatorKinds)} events.`).replace(/^./, (c) => c.toUpperCase());
        }
        case 'narcanDoses': {
            const s = settings;
            return (`The units distributed on ${kindsInWords(s.kinds)} encounters in the period, as each agency reported ` +
                `them (doses or kits — no conversion), added up. Agencies reporting different units are added as reported. ` +
                (s.missingDoses === 0
                    ? 'An encounter with no number reported is left out of the sum.'
                    : `An encounter with no number reported counts as ${s.missingDoses} unit${s.missingDoses === 1 ? '' : 's'}.`));
        }
        case 'ratePerPopulation': {
            const s = settings;
            return (`${kindsInWords(s.kinds)} events in the period per ${s.per.toLocaleString('en-US')} county ` +
                `residents, from the ${s.source} for the period's year (the average of the years a range covers). ` +
                `Not annualised: a month's rate is that month's events. ` +
                (s.populations.length === 0
                    ? 'No population figures are entered yet, so no rate is shown.'
                    : `Figures entered for ${s.populations
                        .map((p) => p.year)
                        .sort()
                        .join(', ')}.`)).replace(/^./, (c) => c.toUpperCase());
        }
        case 'lengthOfStay': {
            const s = settings;
            return (`The ${s.statistic} number of days from a case's opening to its close, for closes in the period` +
                (s.closeReasons.length ? ` with the reason ${reasonsInWords(s.closeReasons)}. ` : '. ') +
                (s.reopening === 'newStay'
                    ? 'A reopening starts a new stay. '
                    : 'A reopened case is one stay, from its first opening. ') +
                `Shown only from ${s.minStays} stays.`);
        }
        case 'completion': {
            const s = settings;
            return (`Discharges in the period with the reason ${reasonsInWords(s.completedReasons)}, as a share of ` +
                (s.excludedReasons.length
                    ? `all discharges except ${reasonsInWords(s.excludedReasons)}. `
                    : 'all discharges. ') +
                `A discharge with no reason recorded counts as not completed. Shown only from ${s.minDischarges} discharges.`);
        }
        case 'referralToIntake': {
            const s = settings;
            return (`The ${s.statistic} number of days from a case's referral date to its opening, for cases opened in ` +
                `the period. Shown only when at least ${s.minCoveragePercent}% of those cases have a referral date, ` +
                `and from ${s.minCases} of them.`);
        }
        case 'improvement': {
            const s = settings;
            return (`Of the cases closed in the period with the person's housing status recorded at both opening and ` +
                `closing, the share whose status at closing is better than at opening, in the order ` +
                `${s.order.join(' → ')} (worst to best). A status not in the order is not compared. Only cases ` +
                `opened since housing was recorded on cases can count. Shown only from ${s.minCases} cases.`);
        }
        case 'completeness': {
            const s = settings;
            const label = MEASURE_DETAIL_FIELD_DEFS.find((f) => f.key === s.field)?.label.toLowerCase() ?? s.field;
            return (`Of the ${kindsInWords(s.kinds)} events in the period, the share with ${label} recorded. ` +
                (s.unknownAnswered === 'yes'
                    ? 'A recorded “Unknown” counts as answered; a blank does not. '
                    : 'A blank is not an answer, and neither is a recorded “Unknown”. ') +
                `Shown only from ${s.minEvents} events.`);
        }
        case 'followUp': {
            const s = settings;
            return (`Of the ${kindsInWords(s.triggerKinds)} events in the period with a person, the share where the same ` +
                `person had ${followUpKindsInWords(s.followUpKinds)}` +
                (s.caseActivity === 'yes'
                    ? ', or any case activity (a case opened, closed or reopened),'
                    : '') +
                ` within ${s.windowDays} days after. An event less than ${s.windowDays} days ago is left out until ` +
                `its window has passed. ` +
                (s.people === 'all'
                    ? 'People not yet verified are counted. '
                    : 'Only verified people are counted. ') +
                `Shown only from ${s.minOverdoses} events.`).replace(/^./, (c) => c.toUpperCase());
        }
        case 'retention': {
            const s = settings;
            return (`Of the cases opened in the period, the share still open ${s.days} days later. A case opened ` +
                `less than ${s.days} days ago is left out until it can be known. Shown only from ${s.minIntakes} intakes.`);
        }
    }
}
/** The label every provisional number carries, on screen and in files. */
/** What a defined measure says about its rule, in the app and in its files: where the rule is set. */
export const DEFINITION_NOTE = 'Counted by the rule set in Admin → Definitions';
export const definitionView = z.object({
    key: z.enum(DEFINITION_KEYS),
    title: z.string(),
    settings: z.record(z.unknown()),
    defaults: z.record(z.unknown()),
    /** False while the organisation uses the proposed default. */
    custom: z.boolean(),
    version: z.number().int(),
    rule: z.string(),
    provisional: z.literal(true),
    updatedAt: z.string().optional(),
    updatedBy: z.string().optional(),
});
export const definitionListResponse = z.object({ items: z.array(definitionView) });
// ─── Provisional metrics ─────────────────────────────────────────────────────────────────────
/** Metrics computed from a provisional definition. Grows as each is built. */
export const METRIC_KEYS = [
    'fatalShare',
    'recidivism',
    'narcanPerOverdose',
    'narcanDoses',
    'ratePerPopulation',
    'lengthOfStay',
    'completion',
    'retention',
    'referralToIntake',
    'completeness',
    'improvement',
    'followUp',
];
export const METRIC_UNITS = ['percent', 'ratio', 'days', 'count', 'rate'];
export const metricPoint = z.object({
    /** A period's key, or "total" for the whole range. */
    key: z.string(),
    /** Null when not shown: too few to read, a count withheld, or nothing to compute from — see `note`. */
    value: z.number().nullable(),
    numerator: analyticsNumber,
    denominator: analyticsNumber,
    note: z.string().optional(),
});
export const metricResult = z.object({
    key: z.enum(METRIC_KEYS),
    title: z.string(),
    provisional: z.literal(true),
    rule: z.string(),
    settingsVersion: z.number().int(),
    /** Where the figures come from, and how fresh they are (OCC-4). `asOf` is when. */
    source: z.string(),
    refresh: z.string(),
    unit: z.enum(METRIC_UNITS),
    /** For a rate: per how many residents. */
    ratePer: z.number().int().optional(),
    numeratorLabel: z.string(),
    denominatorLabel: z.string(),
    periods: z.array(metricPoint),
    total: metricPoint,
    /** Where the metric depends on knowing who is who: how many people it rests on, verified or not. */
    basis: z
        .object({ verified: z.number().int(), unverified: z.number().int(), note: z.string() })
        .optional(),
    /** Where it depends on a field being filled: how many records had it. */
    coverage: z
        .object({ with: z.number().int(), without: z.number().int(), note: z.string() })
        .optional(),
    /**
     * A second number shown beside this one, from a different source — recidivism's "Flagged by agency"
     * beside the calculated share (approved 10/7). Said in words, because the two measure different things.
     */
    companion: z
        .object({ label: z.string(), count: analyticsNumber, unit: z.string(), note: z.string() })
        .optional(),
    /** The reports a numerator and a denominator open — the same queries the metric counted. */
    drill: z.object({ numerator: analyticsQuery.optional(), denominator: analyticsQuery.optional() }),
    suppression: z.object({ applied: z.boolean(), sensitive: z.boolean() }),
    asOf: z.string(),
});
export const metricExportRequest = z.object({
    key: z.enum(METRIC_KEYS),
    query: analyticsQuery,
    format: exportFormat,
});
/** What each metric counts, so the right permission guards it: events need `measure:read`, cases `case:read`. */
export const METRIC_SUBJECTS = {
    fatalShare: 'events',
    recidivism: 'events',
    narcanPerOverdose: 'events',
    narcanDoses: 'events',
    ratePerPopulation: 'events',
    lengthOfStay: 'cases',
    completion: 'cases',
    retention: 'cases',
    referralToIntake: 'cases',
    completeness: 'events',
    improvement: 'cases',
    followUp: 'events',
};
/**
 * Where each metric's figures come from, in words: shown beside every metric, on screen and in files (OCC-4).
 */
export const METRIC_SOURCES = {
    fatalShare: 'Overdose events recorded or imported by the agencies',
    recidivism: 'Overdose events, matched to people',
    narcanPerOverdose: 'Narcan and overdose events recorded or imported by the agencies',
    narcanDoses: 'Narcan distribution events, with the units each agency reported',
    ratePerPopulation: 'Events, and the county population figures entered in Admin → Definitions',
    lengthOfStay: 'Cases and their status history',
    completion: 'Cases and their close reasons',
    retention: 'Cases and their status history',
    referralToIntake: 'Cases with a referral date',
    completeness: 'Events and their recorded details',
    improvement: 'Cases, with housing recorded at opening and closing',
    followUp: 'Overdose events and the same person’s later events and case activity',
};
/** How fresh a metric is (OCC-4): every one is worked out from the records when it is shown. */
export const METRIC_REFRESH = 'Recalculated from the current records each time it is shown, at most a minute old.';
/**
 * Where a catalog target's value ends up, in words (tester feedback 10/7). Shown beside "Mapped", so a column
 * bound to an event field is not mistaken for one that fills the person's record.
 */
export function targetPlace(entry) {
    if (entry.domain === 'CUSTOM') {
        return entry.customScope === 'EVENT'
            ? 'custom field on each event'
            : 'custom field on the person';
    }
    if (entry.key.startsWith('participant.'))
        return 'on the person';
    if (entry.key.startsWith('measure.'))
        return 'on each event';
    return '';
}
/**
 * Calculated recidivism beside the agencies' own flags, person by person (approved 10/7): who an agency marks a
 * recidivist with no calculated repeat, and who has a calculated repeat with no flag. Both provisional — the
 * calculation is a proposed definition, and the flag is each agency's own judgement.
 */
export const recidivismComparisonPerson = z.object({
    participantId: z.string(),
    participantCode: z.string(),
    verified: z.boolean(),
    /** The day an agency marked them, where one is recorded. */
    flaggedOn: z.string().optional(),
});
export const recidivismComparison = z.object({
    provisional: z.literal(true),
    rule: z.string(),
    settingsVersion: z.number().int(),
    /** People with a counted event in the period: the population both lists come from. */
    people: z.number().int(),
    calculatedRepeats: z.number().int(),
    flagged: z.number().int(),
    flaggedWithoutRepeat: z.array(recidivismComparisonPerson),
    repeatWithoutFlag: z.array(recidivismComparisonPerson),
    note: z.string(),
    asOf: z.string(),
});
// ─── Research export (slice 9, OCC-69) ───────────────────────────────────────────────────────
/**
 * The de-identified, linked research dataset (COUNTY-DEFINITIONS-NEEDED §23): a zip of CSV files, one per
 * table, linked by system IDs, with a data dictionary and the code lists. Record-level access only; every
 * export audited.
 *
 * What it never holds: names, dates of birth, phones, agency or client IDs, addresses, notes, or any free
 * text an agency typed (which could name someone). The full ZIP only for a reader with record-level access.
 */
export const RESEARCH_EXPORT_MAX_ROWS = 100_000;
/** An event's coded and numeric details: every detail field except the free text an agency types. */
export const RESEARCH_EVENT_DETAILS = [
    {
        field: 'confirmedStatus',
        column: 'confirmed_status',
        type: 'code',
        description: 'Whether the overdose was confirmed or suspected',
    },
    { field: 'opioidType', column: 'opioid_type', type: 'code', description: 'Opioid involved' },
    {
        field: 'substanceType',
        column: 'substance_type',
        type: 'code',
        description: 'Substance, from the list',
    },
    {
        field: 'overdoseOutcome',
        column: 'overdose_outcome',
        type: 'code',
        description: 'Overdose outcome, from the list',
    },
    {
        field: 'patientStatusInitial',
        column: 'patient_status_initial',
        type: 'code',
        description: 'The person’s status when first seen',
    },
    {
        field: 'intentional',
        column: 'intentional',
        type: 'code',
        description: 'Whether the overdose was intentional',
    },
    {
        field: 'narcanDoses',
        column: 'narcan_doses',
        type: 'number',
        description: 'Narcan doses given',
    },
    {
        field: 'narcanAdministrations',
        column: 'narcan_administrations',
        type: 'number',
        description: 'Times Narcan was given in the encounter',
    },
    {
        field: 'narcanAdministeredBy',
        column: 'narcan_administered_by',
        type: 'code',
        description: 'Who gave the Narcan',
    },
    {
        field: 'transportRefused',
        column: 'transport_refused',
        type: 'code',
        description: 'Whether transport was refused',
    },
    {
        field: 'edDisposition',
        column: 'ed_disposition',
        type: 'code',
        description: 'Emergency department disposition',
    },
    {
        field: 'lengthOfStayDays',
        column: 'length_of_stay_days',
        type: 'number',
        description: 'Length of stay, in days',
    },
    {
        field: 'arrestCharge',
        column: 'arrest_charge',
        type: 'code',
        description: 'Arrest charge category',
    },
    {
        field: 'arrestDisposition',
        column: 'arrest_disposition',
        type: 'code',
        description: 'Arrest disposition',
    },
    {
        field: 'courtProgramStatus',
        column: 'court_program_status',
        type: 'code',
        description: 'Drug court programme status',
    },
    { field: 'admissionType', column: 'admission_type', type: 'code', description: 'Admission type' },
    {
        field: 'referralSource',
        column: 'referral_source',
        type: 'code',
        description: 'Where the referral came from',
    },
    {
        field: 'referredTo',
        column: 'referred_to',
        type: 'code',
        description: 'Where the person was referred',
    },
    { field: 'matMedication', column: 'mat_medication', type: 'code', description: 'MAT medication' },
];
const PERSON_ID = {
    name: 'participant_id',
    type: 'id',
    description: 'The system participant ID: the code this system assigned. Not an agency’s or client’s ID.',
};
export const RESEARCH_FILES = [
    {
        file: 'participants.csv',
        title: 'Participants',
        description: 'One row per person named by an event or case in the range.',
        columns: [
            PERSON_ID,
            {
                name: 'verified',
                type: 'yes/no',
                description: 'Whether the person’s identity has been verified',
            },
            { name: 'sex', type: 'code', codes: 'sex', description: 'Sex' },
            { name: 'race', type: 'code', codes: 'race', description: 'Race' },
            { name: 'ethnicity', type: 'code', codes: 'ethnicity', description: 'Ethnicity' },
            {
                name: 'age_band',
                type: 'code',
                codes: 'ageBand',
                description: 'Age band today; 90 and over is counted as 90',
            },
            { name: 'zip3', type: 'text', description: 'First three digits of the ZIP code' },
            { name: 'zip', type: 'text', description: 'Full five-digit ZIP code', recordLevel: true },
        ],
    },
    {
        file: 'events.csv',
        title: 'Events',
        description: 'One row per active event that occurred in the range.',
        columns: [
            { name: 'event_id', type: 'id', description: 'The system event ID' },
            {
                ...PERSON_ID,
                description: `${PERSON_ID.description} Blank when no person is attributed yet.`,
            },
            { name: 'kind', type: 'code', codes: 'kind', description: 'What kind of event it was' },
            {
                name: 'occurred_on',
                type: 'date',
                description: 'The day it occurred, in the county (YYYY-MM-DD)',
            },
            { name: 'agency', type: 'text', description: 'The agency (site) that recorded it' },
            {
                name: 'age_at_event',
                type: 'number',
                description: 'Age in whole years on the day; 90 and over is 90. Blank without a full date of birth.',
            },
            {
                name: 'age_band_at_event',
                type: 'code',
                codes: 'ageBand',
                description: 'Age band on the day',
            },
            ...RESEARCH_EVENT_DETAILS.map((d) => ({
                name: d.column,
                type: d.type,
                description: d.description,
                ...(d.type === 'code' ? { codes: d.field } : {}),
            })),
        ],
    },
    {
        file: 'cases.csv',
        title: 'Cases',
        description: 'One row per case open at any time in the range. Present only with permission to read cases.',
        columns: [
            { name: 'case_id', type: 'id', description: 'The system case ID' },
            { ...PERSON_ID, description: `${PERSON_ID.description} The case’s main person.` },
            { name: 'agency', type: 'text', description: 'The agency (site) the case is with' },
            { name: 'status', type: 'code', codes: 'caseStatus', description: 'Open or closed, today' },
            { name: 'referred_on', type: 'date', description: 'Referral date (YYYY-MM-DD)' },
            { name: 'opened_on', type: 'date', description: 'Day the case opened (YYYY-MM-DD)' },
            {
                name: 'closed_on',
                type: 'date',
                description: 'Day the case closed (YYYY-MM-DD); blank while open',
            },
            { name: 'episode_type', type: 'code', codes: 'episodeType', description: 'Episode type' },
            { name: 'phase', type: 'code', codes: 'casePhase', description: 'Case phase' },
            {
                name: 'close_reason',
                type: 'code',
                codes: 'caseCloseReason',
                description: 'Why the case closed',
            },
        ],
    },
    {
        file: 'case_people.csv',
        title: 'People on cases',
        description: 'Everyone added to a case in cases.csv besides its main person.',
        columns: [
            { name: 'case_id', type: 'id', description: 'The system case ID, as in cases.csv' },
            PERSON_ID,
            { name: 'role', type: 'text', description: 'Their role on the case' },
            { name: 'added_on', type: 'date', description: 'Day they were added (YYYY-MM-DD)' },
            {
                name: 'removed_on',
                type: 'date',
                description: 'Day they were removed (YYYY-MM-DD); blank if still on it',
            },
        ],
    },
];
/** What is deliberately left out, said in the README and on the page. */
export const RESEARCH_EXCLUDED = [
    'Names',
    'Dates of birth',
    'Phone numbers',
    'Agency and client IDs',
    'Addresses and notes',
    'Free text typed by an agency (substance and outcome words, lot numbers, where someone was taken, the agency’s own row references)',
];
export const researchExportRequest = z
    .object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
})
    .strict()
    .superRefine((r, ctx) => {
    if (r.from > r.to)
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'The range ends before it starts' });
    const days = (Date.parse(`${r.to}T00:00:00Z`) - Date.parse(`${r.from}T00:00:00Z`)) / 86_400_000 + 1;
    if (days > ANALYTICS_MAX_RANGE_DAYS) {
        ctx.addIssue({
            code: 'custom',
            path: ['from'],
            message: 'A research export covers five years at most',
        });
    }
});
// ─── Scheduled reports (slice 11, OCC-71) ────────────────────────────────────────────────────
/**
 * A saved report emailed on a schedule (COUNTY-DEFINITIONS-NEEDED §24). The email is a sign-in link to the
 * report, never its data. Only the report's owner schedules it, and only to people who can open it — the
 * owner, and holders of a role it is shared with — which is checked again at every send.
 */
export const REPORT_FREQUENCIES = ['DAILY', 'WEEKLY', 'MONTHLY'];
export const REPORT_SCHEDULE_STATUSES = ['ACTIVE', 'PAUSED', 'ARCHIVED'];
/** LOGGED: the mailer is in log-only mode, so the message was recorded and not delivered. */
export const REPORT_DELIVERY_STATUSES = [
    'SENT',
    'LOGGED',
    'SUPPRESSED',
    'SKIPPED',
    'FAILED',
];
export const WEEKDAY_NAMES = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
];
export const REPORT_SCHEDULE_MAX_RECIPIENTS = 50;
export const reportScheduleBody = z
    .object({
    savedReportId: z.string().regex(/^[a-f0-9]{24}$/),
    frequency: z.enum(REPORT_FREQUENCIES),
    /** 1 is Monday … 7 Sunday; weekly only. */
    dayOfWeek: z.number().int().min(1).max(7).optional(),
    /** 1–28, so every month has the day; monthly only. */
    dayOfMonth: z.number().int().min(1).max(28).optional(),
    /** The county's time of day, `HH:MM`. */
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM'),
    recipientIds: z
        .array(z.string().regex(/^[a-f0-9]{24}$/))
        .min(1)
        .max(REPORT_SCHEDULE_MAX_RECIPIENTS),
})
    .strict()
    .superRefine((b, ctx) => {
    if ((b.frequency === 'WEEKLY') !== (b.dayOfWeek !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['dayOfWeek'],
            message: 'A weekly schedule needs a day of the week, and only it',
        });
    }
    if ((b.frequency === 'MONTHLY') !== (b.dayOfMonth !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['dayOfMonth'],
            message: 'A monthly schedule needs a day of the month, and only it',
        });
    }
    if (new Set(b.recipientIds).size !== b.recipientIds.length) {
        ctx.addIssue({ code: 'custom', path: ['recipientIds'], message: 'Each recipient once' });
    }
});
export const reportDeliveryView = z.object({
    recipientName: z.string(),
    status: z.enum(REPORT_DELIVERY_STATUSES),
    reason: z.string().optional(),
    at: z.string(),
});
export const reportScheduleView = z.object({
    id: z.string(),
    savedReportId: z.string(),
    reportName: z.string(),
    frequency: z.enum(REPORT_FREQUENCIES),
    dayOfWeek: z.number().optional(),
    dayOfMonth: z.number().optional(),
    time: z.string(),
    recipients: z.array(z.object({ id: z.string(), name: z.string() })),
    status: z.enum(REPORT_SCHEDULE_STATUSES),
    nextRunAt: z.string().optional(),
    lastRunAt: z.string().optional(),
    recentDeliveries: z.array(reportDeliveryView),
});
export const reportScheduleListResponse = z.object({ items: z.array(reportScheduleView) });
export const reportRecipientsResponse = z.object({
    items: z.array(z.object({ id: z.string(), name: z.string(), email: z.string() })),
});
/** "Every Monday at 07:00", "Daily at 07:00", "On the 1st of each month at 07:00". */
export function scheduleInWords(s) {
    if (s.frequency === 'DAILY')
        return `Daily at ${s.time}`;
    if (s.frequency === 'WEEKLY')
        return `Every ${WEEKDAY_NAMES[(s.dayOfWeek ?? 1) - 1]} at ${s.time}`;
    const d = s.dayOfMonth ?? 1;
    const suffix = d % 10 === 1 && d !== 11
        ? 'st'
        : d % 10 === 2 && d !== 12
            ? 'nd'
            : d % 10 === 3 && d !== 13
                ? 'rd'
                : 'th';
    return `On the ${d}${suffix} of each month at ${s.time}`;
}
const scheduleDayFormat = new Intl.DateTimeFormat('en-CA', {
    timeZone: ANALYTICS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
});
/** The county's wall-clock day and minutes of an instant. */
function countyClock(at) {
    const parts = Object.fromEntries(scheduleDayFormat.formatToParts(at).map((p) => [p.type, p.value]));
    return {
        day: `${parts.year}-${parts.month}-${parts.day}`,
        minutes: Number(parts.hour) * 60 + Number(parts.minute),
    };
}
/** The instant a county day's wall-clock time falls at, across a daylight-saving change. */
export function countyInstant(day, time) {
    const [h, m] = time.split(':').map(Number);
    const want = h * 60 + m;
    let at = new Date(startOfDayIn(day, ANALYTICS_TIME_ZONE).getTime() + want * 60_000);
    // Midnight plus the minutes is an hour out on a change day; read the clock back and correct by the gap.
    const got = countyClock(at);
    const drift = got.day === day
        ? got.minutes - want
        : got.day > day
            ? got.minutes + 1440 - want
            : got.minutes - 1440 - want;
    if (drift !== 0)
        at = new Date(at.getTime() - drift * 60_000);
    return at;
}
/** When a schedule next sends, strictly after `after`. */
export function nextScheduledRun(s, after) {
    const today = countyClock(after).day;
    for (let i = 0; i < 70; i++) {
        const day = addCalendarDays(today, i);
        const [y, mo, d] = day.split('-').map(Number);
        const weekday = ((new Date(Date.UTC(y, mo - 1, d)).getUTCDay() + 6) % 7) + 1;
        const matches = s.frequency === 'DAILY' ||
            (s.frequency === 'WEEKLY' && weekday === s.dayOfWeek) ||
            (s.frequency === 'MONTHLY' && d === s.dayOfMonth);
        if (!matches)
            continue;
        const at = countyInstant(day, s.time);
        if (at.getTime() > after.getTime())
            return at;
    }
    throw new Error('No run within ten weeks: the schedule is not a schedule');
}
// ─── Funder reports (slice 13, OCC-12, OCC-83) ───────────────────────────────────────────────
/**
 * The funder report builder (COUNTY-DEFINITIONS-NEEDED §26): choose measures, a breakdown and a period, and
 * export it as PDF, Excel or Word. Aggregate only: every count is suppressed when 1–10 (with a further cell
 * where one would reveal it), whoever runs it — the report is for a funder. Specific federal and state formats
 * are not built; they wait for the county to say which.
 */
export const FUNDER_MEASURES = [
    'events',
    ...MEASURE_KINDS.map((k) => `kind:${k}`),
    'people',
    'narcanAdministrations',
    'cases:intakes',
    'cases:discharges',
    'cases:active',
];
export function funderMeasureLabel(m) {
    if (m.startsWith('kind:'))
        return MEASURE_KIND_LABELS[m.slice(5)];
    return {
        events: 'All events',
        people: 'People with an event',
        narcanAdministrations: 'Narcan administrations',
        'cases:intakes': 'Case intakes',
        'cases:discharges': 'Case discharges',
        'cases:active': 'Active cases (at the period’s end)',
    }[m];
}
export const FUNDER_BREAKDOWNS = [
    'period',
    'site',
    'kind',
    'sex',
    'race',
    'ethnicity',
    'ageBand',
];
export const FUNDER_BREAKDOWN_LABELS = {
    period: 'Period',
    site: 'Agency',
    kind: 'Kind of event',
    sex: 'Sex',
    race: 'Race',
    ethnicity: 'Ethnicity',
    ageBand: 'Age band',
};
const funderDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const funderReportBody = z
    .object({
    title: z.string().trim().min(1).max(120).default('Funder report'),
    measures: z.array(z.enum(FUNDER_MEASURES)).min(1).max(8),
    groupBy: z.array(z.enum(FUNDER_BREAKDOWNS)).max(2).default([]),
    from: funderDate,
    to: funderDate,
    grain: z.enum(ANALYTICS_GRAINS).default('month'),
})
    .strict()
    .superRefine((b, ctx) => {
    if (b.from > b.to)
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'The period ends before it starts' });
    if (new Set(b.groupBy).size !== b.groupBy.length) {
        ctx.addIssue({ code: 'custom', path: ['groupBy'], message: 'Each breakdown once' });
    }
    if (new Set(b.measures).size !== b.measures.length) {
        ctx.addIssue({ code: 'custom', path: ['measures'], message: 'Each measure once' });
    }
    // A kind of event breaks down events; a case has no kind.
    if (b.groupBy.includes('kind') &&
        b.measures.some((m) => m.startsWith('cases:') || m === 'people')) {
        ctx.addIssue({
            code: 'custom',
            path: ['groupBy'],
            message: 'Kind of event breaks down event measures only; leave out cases and people, or choose another breakdown',
        });
    }
    if (b.grain === 'day' && b.groupBy.includes('period')) {
        const days = (Date.parse(`${b.to}T00:00:00Z`) - Date.parse(`${b.from}T00:00:00Z`)) / 86_400_000 + 1;
        if (days > ANALYTICS_MAX_DAILY_DAYS) {
            ctx.addIssue({ code: 'custom', path: ['grain'], message: 'Daily covers a year at most' });
        }
    }
});
/** The same report as a file. Its own schema: the report's is strict, so it cannot be widened by `.and`. */
export const funderReportExportBody = z
    .object({ format: z.enum(['pdf', 'xlsx', 'docx']) })
    .passthrough()
    .transform(({ format, ...rest }, ctx) => {
    const report = funderReportBody.safeParse(rest);
    if (!report.success) {
        for (const issue of report.error.issues)
            ctx.addIssue(issue);
        return z.NEVER;
    }
    return { ...report.data, format };
});
const funderValue = z.union([
    z.number(),
    z.object({ suppressed: z.enum(['SMALL', 'COMPLEMENTARY']) }),
]);
export const funderReportResult = z.object({
    title: z.string(),
    columns: z.array(z.object({ key: z.string(), label: z.string() })),
    rows: z.array(z.object({ labels: z.array(z.string()), values: z.array(funderValue) })),
    totals: z.array(funderValue),
    notes: z.array(z.string()),
    asOf: z.string(),
});
// ─── Outbox (slice 11) ───────────────────────────────────────────────────────────────────────
/**
 * Every message the system sent, or only logged (Admin → Outbox): to whom, about what, its link, when, and
 * what became of it. SENT went to SendGrid; LOGGED was recorded because the mailer is log-only; NOT_ALLOWED
 * was off the allowlist (cms-dev delivers only to it), so it was logged, not sent; FAILED was refused.
 */
export const OUTBOX_STATUSES = ['SENT', 'LOGGED', 'NOT_ALLOWED', 'FAILED'];
export const OUTBOX_STATUS_WORDS = {
    SENT: 'Sent',
    LOGGED: 'Logged (log-only mode)',
    NOT_ALLOWED: 'Logged (not on the allowlist)',
    FAILED: 'Failed',
};
export const outboxEntry = z.object({
    id: z.string(),
    to: z.string(),
    subject: z.string(),
    link: z.string().optional(),
    source: z.string(),
    status: z.enum(OUTBOX_STATUSES),
    reason: z.string().optional(),
    driver: z.string().optional(),
    providerId: z.string().optional(),
    at: z.string(),
});
export const outboxListResponse = z.object({
    items: z.array(outboxEntry),
    nextCursor: z.string().optional(),
    /** How the mailer is set: `sendgrid` delivers, `log` only records. */
    mode: z.enum(['sendgrid', 'log']),
    allowlist: z.array(z.string()),
});
//# sourceMappingURL=index.js.map